import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDataLayer } from "../../../../Contexts/DataLayer.context.jsx";
import { useNavBar } from "../../../../Contexts/navbar.items.context.jsx";
import { usePlayer } from "../../../../Contexts/playerData.context.jsx";
import { getUserFromToken, runS1Search } from "../../../../utils/functions.ts";
import { CFLStatus, NFLStatus, cflObject, genPos, nflObject, ufl24Object } from "../../../../utils/loadFunctions.ts";
import {
  DEFAULT_WHITE_BOARD_POSITION_MARKER_COLOR,
  WHITE_BOARD_PREFERENCES_EVENT,
  getReadableTextColor,
  getWhiteBoardGradeKey,
  normalizeWhiteBoardPreferences,
  normalizeWhiteBoardPositionMarkerColor,
} from "../../../../utils/whiteBoardPreferences";
import shareIcon from "../../../../assets/icons/share.svg";
import deleteIcon from "../../../../assets/icons/delete_red.svg";
import BigBoxButtons from "../../../Universals/DragBox/BigBox/bigBoxButtons.component.jsx";
import DragBox2026 from "../../../Universals/DragBox/DragBox2026/dragBox2026.component.jsx";
import FBXCalendar from "../../../Universals/FBXCalendar/fbxcalendar.component.jsx";
import WhiteBoardTutorial from "./WhiteBoardTutorial.component.jsx";
import "./draftboard.style.scss";

const DRAFT_BOARD_ENDPOINT = "https://www.fbxchange.com/ReactPHP/draftBoard.php";
const QUICK_NOTES_ENDPOINT = "https://www.fbxchange.com/ReactPHP/quickNotes.php";
const DRAFT_BOARD_LOCAL_KEY = "FBX_DRAFT_BOARD_CURRENT";
const DRAFT_BOARD_SIZE_MODE_KEY = "FBX_DRAFT_BOARD_SIZE_MODE";
const DRAFT_BOARD_PRESETS_POPUP_POSITION_KEY = "DRAFT_BOARD_PRESETS_LOCATION";
const DRAFT_BOARD_POPUP_RESET_EVENT = "FBX_RESET_WINDOW_POSITION";
const DRAFT_BOARD_POPUP_POSITION_KEYS = [
  "DRAFT_BOARD_PLAYERS_LOCATION",
  "DRAFT_BOARD_ON_BOARD_LOCATION",
  "DRAFT_BOARD_DRAFT_LOCATION",
  "DRAFT_BOARD_DATA_LOCATION",
];
const DRAFT_BOARD_EDITABLE_REFRESH_MS = 6000;
const DRAFT_BOARD_READONLY_REFRESH_MS = 20000;
const DRAFT_BOARD_DETAIL_SAVE_DELAY_MS = 1000;
const DRAFT_BOARD_LABEL_SAVE_DELAY_MS = 1000;
const FBX_SHARE_SCOUT_IDS = ["1", "2"];
const PRESETS_POPUP_WIDTH = 560;
const PRESETS_POPUP_HEIGHT = 650;
const PRESETS_POPUP_MARGIN = 12;
const PRESETS_POPUP_DEFAULT_POSITION = { left: 10, top: 172 };
const DRAFT_BOARD_POPUP_DEFAULT_OFFSET_Y = 60;
const BOARD_GRID_SIZE = 4;
const LEGACY_BOARD_REMOVE_RAIL_WIDTH = 24;
const BOARD_REMOVE_RAIL_WIDTH = 16;
const BOARD_PRINT_CARD_WIDTH = 120;
const BOARD_PRINT_CARD_HEIGHT = 31;
const BOARD_HEADING_STACK_GAP = 1;
const BOARD_LABEL_PRINT_WIDTH = 120;
const BOARD_LABEL_PRINT_HEIGHT = 28;
const BOARD_LABEL_ROW_SNAP_DISTANCE = Math.round(BOARD_LABEL_PRINT_HEIGHT / 2);
const BOARD_LABEL_COLUMN_SNAP_DISTANCE = BOARD_GRID_SIZE * 4;
const PAGE_TAB_TRANSFER_OPEN_DELAY_MS = 650;
const BOARD_DETAIL_PRINT_WIDTH = 260;
const BOARD_DETAIL_PRINT_HEIGHT = 190;
const BOARD_PRINT_PAGE_WIDTH = 978;
const BOARD_PRINT_PAGE_HEIGHT = 700;
const BOARD_PRINT_SAFE_INSET = 10;
const BOARD_PRINT_RIGHT_INSET = 20;
const BOARD_PAGE_TYPES = {
  regular: "regular",
  splitSort: "split-sort",
  flipped: "flipped",
};
const normalizeBoardPageType = (value) => {
  const text = cleanText(value).toLowerCase();
  if (text === BOARD_PAGE_TYPES.splitSort || text === "split" || text === "split-sort") return BOARD_PAGE_TYPES.splitSort;
  if (text === BOARD_PAGE_TYPES.flipped || text === "flip") return BOARD_PAGE_TYPES.flipped;
  return BOARD_PAGE_TYPES.regular;
};
const BOARD_PAGE_TYPE_OPTIONS = [
  { type: BOARD_PAGE_TYPES.regular, label: "Regular" },
  { type: BOARD_PAGE_TYPES.splitSort, label: "Split" },
  { type: BOARD_PAGE_TYPES.flipped, label: "Flipped", badgeLabel: "FLIP" },
];
const getBoardPageTypeLabel = (pageType, key = "label") => (
  BOARD_PAGE_TYPE_OPTIONS.find((option) => option.type === normalizeBoardPageType(pageType))?.[key] ||
  BOARD_PAGE_TYPE_OPTIONS.find((option) => option.type === normalizeBoardPageType(pageType))?.label ||
  "Regular"
);
const BOARD_SPLIT_SORT_Y = BOARD_PRINT_PAGE_HEIGHT / 2;
const DEFAULT_BOARD_PAGE_NAME = "Page 1";
const BOARD_CHOOSER_NAME = "Choose a Board";
const DRAFT_BOARD_SEARCH_LIMIT = 75;
const DRAFT_BOARD_SEARCH_DEFAULTS = {
  LAST: "",
  FIRST: "",
  GENPOS: "-",
  GENUNIT: "-",
  CFLTEAM: "-",
  CFLSTATUS: "-",
  CFLStatusDateStart: "",
  CFLStatusDateEnd: "",
  NFLTEAM: "-",
  NFLSTATUS: "-",
  NFLStatusDateStart: "",
  NFLStatusDateEnd: "",
  UFL24TEAM: "-",
  UFL24STATUS: "-",
  UFL24StatusDateStart: "",
  UFL24StatusDateEnd: "",
};
const cleanText = (value) => {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  if (text === "-" || text.toLowerCase() === "null") return "";
  return text;
};

const firstCleanText = (...values) => {
  for (const value of values) {
    const text = cleanText(value);
    if (text) return text;
  }
  return "";
};

const getTodayInputDate = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getPlayerAlphaValue = (player, keys = []) => {
  const source = player || {};
  for (const key of keys) {
    const value = cleanText(source[key]);
    if (value) return value;
  }
  return "";
};

const comparePlayersAlpha = (leftPlayer, rightPlayer) => {
  const compareOptions = { sensitivity: "base", numeric: true };
  const fields = [
    ["last", "LAST"],
    ["first", "FIRST"],
    ["school", "SCHOOL", "School"],
  ];

  for (const keys of fields) {
    const leftValue = getPlayerAlphaValue(leftPlayer, keys);
    const rightValue = getPlayerAlphaValue(rightPlayer, keys);
    const result = leftValue.localeCompare(rightValue, undefined, compareOptions);
    if (result !== 0) return result;
  }

  return getPlayerAlphaValue(leftPlayer, ["FBXID", "DR_Num", "DRNUM"])
    .localeCompare(getPlayerAlphaValue(rightPlayer, ["FBXID", "DR_Num", "DRNUM"]), undefined, compareOptions);
};

const sortPlayersAlpha = (players = []) => [...players].sort(comparePlayersAlpha);

const compareBoardsAlpha = (leftBoard, rightBoard) => {
  const compareOptions = { sensitivity: "base", numeric: true };
  const leftName = cleanText(leftBoard?.boardName || leftBoard?.name);
  const rightName = cleanText(rightBoard?.boardName || rightBoard?.name);
  const nameCompare = leftName.localeCompare(rightName, undefined, compareOptions);
  if (nameCompare !== 0) return nameCompare;

  return String(leftBoard?.boardId || "").localeCompare(String(rightBoard?.boardId || ""), undefined, compareOptions);
};

const hasFilterValue = (value) => {
  const text = String(value ?? "").trim();
  return text !== "" && text !== "-";
};

const isPlaceholderStatus = (status) => {
  const id = String(status?.status_id || "").trim();
  const label = String(status?.status || "").trim().toLowerCase();
  return !id || id === "-" || id === "--" || label === "status";
};

const getVisibleStatusOptions = (statuses = []) => (Array.isArray(statuses) ? statuses : [])
  .filter((status) => !isPlaceholderStatus(status))
  .filter((status, index, statusList) => {
    const id = String(status?.status_id || "").trim();
    return statusList.findIndex((item) => String(item?.status_id || "").trim() === id) === index;
  });

const isTruthyFlag = (value) => value === true || value === 1 || String(value ?? "").toUpperCase() === "Y" || String(value ?? "").toLowerCase() === "true";

const clampPresetsPopupPosition = (position = {}) => {
  if (typeof window === "undefined") {
    return {
      left: Number(position.left || PRESETS_POPUP_DEFAULT_POSITION.left),
      top: Number(position.top || PRESETS_POPUP_DEFAULT_POSITION.top),
    };
  }

  const popupWidth = Math.min(PRESETS_POPUP_WIDTH, window.innerWidth - (PRESETS_POPUP_MARGIN * 2));
  const popupHeight = Math.min(PRESETS_POPUP_HEIGHT, window.innerHeight - (PRESETS_POPUP_MARGIN * 2));
  const maxLeft = Math.max(PRESETS_POPUP_MARGIN, window.innerWidth - popupWidth - PRESETS_POPUP_MARGIN);
  const maxTop = Math.max(PRESETS_POPUP_MARGIN, window.innerHeight - popupHeight - PRESETS_POPUP_MARGIN);

  return {
    left: Math.min(Math.max(Number(position.left || PRESETS_POPUP_DEFAULT_POSITION.left), PRESETS_POPUP_MARGIN), maxLeft),
    top: Math.min(Math.max(Number(position.top || PRESETS_POPUP_DEFAULT_POSITION.top), PRESETS_POPUP_MARGIN), maxTop),
  };
};

const getStoredPresetsPopupPosition = (defaultPosition = PRESETS_POPUP_DEFAULT_POSITION) => {
  const fallbackPosition = clampPresetsPopupPosition(defaultPosition);
  if (typeof window === "undefined") return fallbackPosition;

  try {
    const savedPosition = JSON.parse(window.localStorage.getItem(DRAFT_BOARD_PRESETS_POPUP_POSITION_KEY) || "{}");
    const hasSavedPosition = Object.prototype.hasOwnProperty.call(savedPosition, "left") ||
      Object.prototype.hasOwnProperty.call(savedPosition, "top");
    return hasSavedPosition ? clampPresetsPopupPosition(savedPosition) : fallbackPosition;
  } catch {
    return fallbackPosition;
  }
};

const saveStoredPresetsPopupPosition = (position = {}) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(DRAFT_BOARD_PRESETS_POPUP_POSITION_KEY, JSON.stringify(clampPresetsPopupPosition(position)));
  } catch {}
};

const GENERAL_POSITION_OPTIONS = genPos
  .map((position) => {
    const code = cleanText(position?.GeneralPos);
    const title = cleanText(position?.title);
    const includedPositions = cleanText(position?.DescriptionOfPos);
    return {
      code,
      label: includedPositions ? `${code} - ${includedPositions}` : code,
      title: [title, includedPositions].filter(Boolean).join(": "),
    };
  })
  .filter((position) => position.code);

const POSITION_UNIT_OPTIONS = [
  { code: "offence", label: "Offence", positions: ["QB", "WR", "RB", "OL"] },
  { code: "defence", label: "Defence", positions: ["DL", "LB", "DB"] },
  { code: "special-teams", label: "Special Teams", positions: ["ST"] },
];

const buildBoardPlayer = (player, x, y) => ({
  boardItemId: `${player?.FBXID || Date.now()}-${Date.now()}`,
  FBXID: player?.FBXID || "",
  first: cleanText(player?.first),
  last: cleanText(player?.last),
  school: cleanText(player?.school),
  colPos1: cleanText(player?.colPos1 || player?.COL_POS1 || player?.colPos || player?.College_Position || player?.collegePosition),
  colPos: cleanText(player?.colPos || player?.colPos1 || player?.COL_POS1 || player?.College_Position || player?.collegePosition),
  collegeJersey: getCollegeJerseyNumber(player),
  jersey: getCollegeJerseyNumber(player),
  proJersey: getProJerseyNumber(player),
  genPos: cleanText(player?.genPos || player?.GENPOS || player?.generalPos || player?.GeneralPos),
  cflGp: getBoardLeagueGeneralPosition(player, "CFL"),
  nflGp: getBoardLeagueGeneralPosition(player, "NFL"),
  cflStatus: getBoardLeagueStatus(player, "CFL"),
  nflStatus: getBoardLeagueStatus(player, "NFL"),
  cflTeam: getBoardLeagueTeam(player, "CFL"),
  nflTeam: getBoardLeagueTeam(player, "NFL"),
  impnip: cleanText(player?.impnip || player?.IMPNIP),
  x,
  y,
});

const getBoardPlayerSnapshotFields = (player = {}) => ({
  colPos1: cleanText(player?.colPos1 || player?.COL_POS1 || player?.colPos || player?.College_Position || player?.collegePosition),
  colPos: cleanText(player?.colPos || player?.colPos1 || player?.COL_POS1 || player?.College_Position || player?.collegePosition),
  collegeJersey: getCollegeJerseyNumber(player),
  jersey: getCollegeJerseyNumber(player),
  proJersey: getProJerseyNumber(player),
  genPos: cleanText(player?.genPos || player?.GENPOS || player?.generalPos || player?.GeneralPos),
  cflGp: getBoardLeagueGeneralPosition(player, "CFL"),
  nflGp: getBoardLeagueGeneralPosition(player, "NFL"),
  cflStatus: getBoardLeagueStatus(player, "CFL"),
  nflStatus: getBoardLeagueStatus(player, "NFL"),
  cflTeam: getBoardLeagueTeam(player, "CFL"),
  nflTeam: getBoardLeagueTeam(player, "NFL"),
});

const refreshBoardPlayerSnapshot = (item = {}, freshPlayer = null) => {
  if (!freshPlayer) return { player: item, changed: false };

  const freshFields = getBoardPlayerSnapshotFields(freshPlayer);
  let changed = false;
  const nextPlayer = { ...item };

  Object.entries(freshFields).forEach(([key, value]) => {
    if (cleanText(nextPlayer[key]) === value) return;
    nextPlayer[key] = value;
    changed = true;
  });

  return { player: changed ? nextPlayer : item, changed };
};

const getPrintCardPlayerName = (player, fallback = "") => {
  const last = cleanText(player?.last);
  const firstInitial = cleanText(player?.first).charAt(0).toUpperCase();
  if (last && firstInitial) return `${last}, ${firstInitial}`;
  return last || fallback;
};

const buildBoardLabel = (x, y, text = "Position", idSuffix = "", extra = {}) => ({
  labelItemId: `label-${Date.now()}${idSuffix ? `-${idSuffix}` : ""}`,
  text,
  x,
  y,
  ...extra,
});

const normalizePresetLabels = (labels = []) => (Array.isArray(labels) ? labels : [])
  .map((label) => ({
    text: cleanText(label?.text) || "Position",
    x: Math.round(Number(label?.x || 0)),
    y: Math.round(Number(label?.y || 0)),
  }))
  .sort((left, right) => left.y - right.y || left.x - right.x || left.text.localeCompare(right.text));

const stringifyPresetLabels = (labels = []) => JSON.stringify(normalizePresetLabels(labels));

const getPresetLabelsFromData = (presetData) => {
  if (Array.isArray(presetData)) return presetData;
  if (Array.isArray(presetData?.labels)) return presetData.labels;
  return [];
};

const getPresetPageTypeFromData = (presetData, fallbackName = "") => {
  const normalizedPageType = normalizeBoardPageType(presetData?.pageType || presetData?.type);
  if (normalizedPageType !== BOARD_PAGE_TYPES.regular) return normalizedPageType;

  const name = cleanText(fallbackName).toLowerCase();
  if (name.includes("split")) return BOARD_PAGE_TYPES.splitSort;
  if (name.includes("flipped") || name.includes("flip")) return BOARD_PAGE_TYPES.flipped;

  return BOARD_PAGE_TYPES.regular;
};

const normalizePagePresetDraft = (presetDraft) => {
  if (!presetDraft || typeof presetDraft !== "object") return null;

  const source = cleanText(presetDraft.source);
  const presetSessionId = cleanText(presetDraft.presetSessionId);
  if (!source || !presetSessionId) return null;

  return {
    source,
    type: cleanText(presetDraft.type),
    presetId: Number(presetDraft.presetId || 0),
    name: cleanText(presetDraft.name) || "Page Preset",
    presetSessionId,
    baseline: cleanText(presetDraft.baseline),
    pageType: getPresetPageTypeFromData(presetDraft, presetDraft.name),
  };
};

const cloneBoardValue = (value) => {
  if (typeof structuredClone === "function") return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
};

const buildBoardDetail = (x, y, author = {}) => ({
  detailItemId: `detail-${Date.now()}`,
  text: "",
  createdByScoutId: author.scoutId || "",
  createdByScoutName: author.scoutName || "",
  x,
  y,
});

const buildBoardOverlay = (type, x, y) => ({
  overlayItemId: `overlay-${type}-${Date.now()}`,
  type,
  x,
  y,
});

const BOARD_OVERLAY_TYPES = {
  oline: { label: "O-Line", cells: ["OLT", "OLG", "OC", "ORG", "ORT"] },
  defense4: { label: "D 4-3", cells: ["DE", "DT", "DT", "DE"] },
  defense3: { label: "D 3-4", cells: ["DE", "NT", "DE"] },
  gameDayOffence: { label: "Offence", kind: "gameDayOffence", width: 610, height: 270 },
  gameDayDefence43: { label: "Defence 4-3", kind: "gameDayDefence43", width: 610, height: 300 },
  gameDayDefence34: { label: "Defence 3-4", kind: "gameDayDefence34", width: 610, height: 300 },
  gameDayDefence44: { label: "Defence 4-4", kind: "gameDayDefence44", width: 610, height: 300 },
  odsBeta: {
    label: "ODS Game Day",
    kind: "headingPreset",
    pageType: BOARD_PAGE_TYPES.splitSort,
  },
  odsNaIrFa: {
    label: "ODS NA/IR/FA",
    kind: "headingPreset",
    pageType: BOARD_PAGE_TYPES.splitSort,
  },
  odsNegList: {
    label: "ODS Neg List",
    kind: "headingPreset",
    pageType: BOARD_PAGE_TYPES.splitSort,
  },
  defaultOffence: {
    label: "Default Offence",
    kind: "headingPreset",
    pageType: BOARD_PAGE_TYPES.regular,
  },
  defaultDefence: {
    label: "Default Defence",
    kind: "headingPreset",
    pageType: BOARD_PAGE_TYPES.flipped,
  },
};

const BOARD_PRESET_TYPES = ["defaultOffence", "defaultDefence", "odsBeta", "odsNaIrFa", "odsNegList"];

const BOARD_LEGACY_OVERLAY_TYPES = {
  defense43: BOARD_OVERLAY_TYPES.defense4,
  defense44: BOARD_OVERLAY_TYPES.defense4,
  defense34: BOARD_OVERLAY_TYPES.defense3,
  gameDayDefence: BOARD_OVERLAY_TYPES.gameDayDefence43,
};

const getBoardOverlayConfig = (type) => BOARD_OVERLAY_TYPES[type] || BOARD_LEGACY_OVERLAY_TYPES[type] || BOARD_OVERLAY_TYPES.oline;

const GAME_DAY_DEFENCE_43_SPOTS = [
  { pos: "DE", x: 20, y: 7, w: 12 },
  { pos: "DT", x: 38, y: 7, w: 12 },
  { pos: "DT", x: 56, y: 7, w: 12 },
  { pos: "DE", x: 74, y: 7, w: 12 },
  { pos: "LB", x: 29, y: 33, w: 12 },
  { pos: "LB", x: 47, y: 33, w: 12 },
  { pos: "LB", x: 65, y: 33, w: 12 },
  { pos: "CB", x: 4, y: 43, w: 12 },
  { pos: "CB", x: 87, y: 43, w: 12 },
  { pos: "HB", x: 20, y: 66, w: 12 },
  { pos: "HB", x: 74, y: 66, w: 12 },
  { pos: "S", x: 47, y: 78, w: 12 },
];

const GAME_DAY_DEFENCE_34_SPOTS = [
  { pos: "DE", x: 26, y: 7, w: 12 },
  { pos: "NT", x: 47, y: 7, w: 12 },
  { pos: "DE", x: 68, y: 7, w: 12 },
  { pos: "LB", x: 20, y: 33, w: 12 },
  { pos: "LB", x: 36, y: 33, w: 12 },
  { pos: "LB", x: 52, y: 33, w: 12 },
  { pos: "LB", x: 68, y: 33, w: 12 },
  { pos: "CB", x: 4, y: 43, w: 12 },
  { pos: "CB", x: 84, y: 43, w: 12 },
  { pos: "HB", x: 20, y: 66, w: 12 },
  { pos: "HB", x: 68, y: 66, w: 12 },
  { pos: "S", x: 47, y: 78, w: 12 },
];

const GAME_DAY_DEFENCE_44_SPOTS = [
  { pos: "DE", x: 20, y: 7, w: 12 },
  { pos: "DT", x: 38, y: 7, w: 12 },
  { pos: "DT", x: 56, y: 7, w: 12 },
  { pos: "DE", x: 74, y: 7, w: 12 },
  { pos: "LB", x: 20, y: 33, w: 12 },
  { pos: "LB", x: 38, y: 33, w: 12 },
  { pos: "LB", x: 56, y: 33, w: 12 },
  { pos: "LB", x: 74, y: 33, w: 12 },
  { pos: "CB", x: 6, y: 63, w: 12 },
  { pos: "CB", x: 86, y: 63, w: 12 },
  { pos: "S", x: 34, y: 78, w: 12 },
  { pos: "S", x: 60, y: 78, w: 12 },
];

const GAME_DAY_OFFENCE_SPOTS = [
  { pos: "WR", x: 2, y: 4, w: 10 },
  { pos: "LT", x: 18, y: 4, w: 10 },
  { pos: "LG", x: 31, y: 4, w: 10 },
  { pos: "C", x: 44, y: 4, w: 10 },
  { pos: "RG", x: 57, y: 4, w: 10 },
  { pos: "RT", x: 70, y: 4, w: 10 },
  { pos: "WR", x: 88, y: 4, w: 10 },
  { pos: "SB", x: 18, y: 46, w: 10 },
  { pos: "QB", x: 44, y: 46, w: 10 },
  { pos: "SB", x: 57, y: 46, w: 10 },
  { pos: "SB", x: 70, y: 46, w: 10 },
  { pos: "FB", x: 29, y: 64, w: 10 },
  { pos: "RB", x: 44, y: 74, w: 10 },
  { pos: "K/P", x: 2, y: 82, w: 10 },
  { pos: "LS", x: 80, y: 82, w: 10 },
];

const GAME_DAY_DEFENCE_43_LABELS = [
  { text: "DE", x: 188, y: 35 },
  { text: "DT", x: 368, y: 35 },
  { text: "DT", x: 548, y: 35 },
  { text: "DE", x: 728, y: 35 },
  { text: "LB", x: 278, y: 205 },
  { text: "LB", x: 458, y: 205 },
  { text: "LB", x: 638, y: 205 },
  { text: "CB", x: 40, y: 265 },
  { text: "CB", x: 850, y: 265 },
  { text: "HB", x: 188, y: 430 },
  { text: "HB", x: 728, y: 430 },
  { text: "S", x: 458, y: 525 },
];

const GAME_DAY_DEFENCE_34_LABELS = [
  { text: "DE", x: 248, y: 35 },
  { text: "NT", x: 458, y: 35 },
  { text: "DE", x: 668, y: 35 },
  { text: "LB", x: 188, y: 205 },
  { text: "LB", x: 348, y: 205 },
  { text: "LB", x: 508, y: 205 },
  { text: "LB", x: 668, y: 205 },
  { text: "CB", x: 40, y: 265 },
  { text: "CB", x: 818, y: 265 },
  { text: "HB", x: 188, y: 430 },
  { text: "HB", x: 668, y: 430 },
  { text: "S", x: 458, y: 525 },
];

const GAME_DAY_DEFENCE_44_LABELS = [
  { text: "DE", x: 188, y: 35 },
  { text: "DT", x: 368, y: 35 },
  { text: "DT", x: 548, y: 35 },
  { text: "DE", x: 728, y: 35 },
  { text: "LB", x: 188, y: 205 },
  { text: "LB", x: 368, y: 205 },
  { text: "LB", x: 548, y: 205 },
  { text: "LB", x: 728, y: 205 },
  { text: "CB", x: 58, y: 410 },
  { text: "CB", x: 838, y: 410 },
  { text: "S", x: 328, y: 525 },
  { text: "S", x: 588, y: 525 },
];

const GAME_DAY_DEFENCE_LAYOUTS = {
  gameDayDefence43: { title: "Defence 4-3", spots: GAME_DAY_DEFENCE_43_SPOTS },
  gameDayDefence34: { title: "Defence 3-4", spots: GAME_DAY_DEFENCE_34_SPOTS },
  gameDayDefence44: { title: "Defence 4-4", spots: GAME_DAY_DEFENCE_44_SPOTS },
};

const GAME_DAY_OFFENCE_LABELS = [
  { text: "WR", x: 28, y: 35 },
  { text: "LT", x: 180, y: 35 },
  { text: "LG", x: 310, y: 35 },
  { text: "C", x: 440, y: 35 },
  { text: "RG", x: 570, y: 35 },
  { text: "RT", x: 700, y: 35 },
  { text: "WR", x: 850, y: 35 },
  { text: "SB", x: 180, y: 305 },
  { text: "QB", x: 440, y: 305 },
  { text: "SB", x: 570, y: 305 },
  { text: "SB", x: 700, y: 305 },
  { text: "FB", x: 300, y: 410 },
  { text: "RB", x: 440, y: 470 },
  { text: "K/P", x: 28, y: 525 },
  { text: "LS", x: 820, y: 525 },
];

const DEFAULT_OFFENCE_LABELS = [
  { text: "WR", x: 14, y: 19 },
  { text: "LT", x: 166, y: 19 },
  { text: "LG", x: 296, y: 19 },
  { text: "C", x: 426, y: 19 },
  { text: "RG", x: 556, y: 19 },
  { text: "RT", x: 686, y: 19 },
  { text: "WR", x: 836, y: 19 },
  { text: "SB", x: 20, y: 232 },
  { text: "FB", x: 300, y: 232 },
  { text: "QB", x: 430, y: 232 },
  { text: "SB", x: 690, y: 232 },
  { text: "RB", x: 296, y: 440 },
  { text: "ST", x: 18, y: 525 },
  { text: "ret", x: 166, y: 525 },
];

const DEFAULT_DEFENCE_LABELS = [
  { text: "s", x: 412, y: 152 },
  { text: "hb", x: 40, y: 220 },
  { text: "hb", x: 816, y: 220 },
  { text: "lb", x: 232, y: 384 },
  { text: "lb", x: 412, y: 384 },
  { text: "lb", x: 592, y: 384 },
  { text: "cb", x: 40, y: 484 },
  { text: "cb", x: 816, y: 484 },
  { text: "dl", x: 172, y: 619 },
  { text: "dl", x: 340, y: 619 },
  { text: "dl", x: 508, y: 619 },
  { text: "dl", x: 676, y: 619 },
];

const ODS_BETA_LABELS = [
  { text: "S", x: 432, y: 72 },
  { text: "Reserve", x: 840, y: 72 },
  { text: "HB", x: 153, y: 140 },
  { text: "HB", x: 703, y: 140 },
  { text: "LB", x: 295, y: 212 },
  { text: "LB", x: 432, y: 212 },
  { text: "lB", x: 569, y: 212 },
  { text: "CB", x: 16, y: 252 },
  { text: "CB", x: 840, y: 252 },
  { text: "DL", x: 153, y: 320 },
  { text: "DL", x: 328, y: 320 },
  { text: "DL", x: 512, y: 320 },
  { text: "DL", x: 703, y: 320 },
  { text: "WR", x: 16, y: 354 },
  { text: "OL", x: 153, y: 354 },
  { text: "OL", x: 291, y: 354 },
  { text: "C", x: 428, y: 354 },
  { text: "OL", x: 565, y: 354 },
  { text: "OL", x: 703, y: 354 },
  { text: "wr", x: 840, y: 354 },
  { text: "sb", x: 840, y: 488 },
  { text: "ST", x: 16, y: 565 },
  { text: "sb", x: 153, y: 488 },
  { text: "SB", x: 704, y: 488 },
  { text: "RB", x: 291, y: 520 },
  { text: "QB", x: 428, y: 520 },
  { text: "FB", x: 565, y: 520 },
  { text: "PR/KR", x: 840, y: 596 },
];

const ODS_NA_IR_FA_LABELS = [
  { text: "S", x: 432, y: 72 },
  { text: "HB", x: 153, y: 140 },
  { text: "HB", x: 703, y: 140 },
  { text: "lb", x: 295, y: 212 },
  { text: "lb", x: 432, y: 212 },
  { text: "lb", x: 569, y: 212 },
  { text: "CB", x: 16, y: 252 },
  { text: "CB", x: 840, y: 288 },
  { text: "dl", x: 153, y: 320 },
  { text: "dl", x: 328, y: 320 },
  { text: "dl", x: 512, y: 320 },
  { text: "dl", x: 703, y: 320 },
  { text: "WR", x: 16, y: 356 },
  { text: "ol", x: 153, y: 356 },
  { text: "ol", x: 291, y: 356 },
  { text: "C", x: 428, y: 356 },
  { text: "ol", x: 565, y: 356 },
  { text: "ol", x: 703, y: 356 },
  { text: "wr", x: 840, y: 356 },
  { text: "sb", x: 153, y: 488 },
  { text: "SB", x: 704, y: 488 },
  { text: "sb", x: 840, y: 488 },
  { text: "fb", x: 291, y: 520 },
  { text: "QB", x: 428, y: 520 },
  { text: "rb", x: 565, y: 520 },
  { text: "ST", x: 16, y: 560 },
  { text: "PR/KR", x: 840, y: 596 },
];

const ODS_NEG_LIST_LABELS = [
  { text: "S", x: 432, y: 72 },
  { text: "HB", x: 153, y: 140 },
  { text: "HB", x: 703, y: 140 },
  { text: "LB", x: 295, y: 212 },
  { text: "LB", x: 432, y: 212 },
  { text: "lB", x: 569, y: 212 },
  { text: "CB", x: 16, y: 252 },
  { text: "CB", x: 840, y: 252 },
  { text: "DL", x: 153, y: 320 },
  { text: "DL", x: 328, y: 320 },
  { text: "DL", x: 512, y: 320 },
  { text: "DL", x: 703, y: 320 },
  { text: "WR", x: 16, y: 356 },
  { text: "OL", x: 153, y: 356 },
  { text: "OL", x: 291, y: 356 },
  { text: "C", x: 428, y: 356 },
  { text: "OL", x: 565, y: 356 },
  { text: "OL", x: 703, y: 356 },
  { text: "wr", x: 840, y: 356 },
  { text: "sb", x: 705, y: 456 },
  { text: "sb", x: 840, y: 456 },
  { text: "QB", x: 424, y: 472 },
  { text: "QB", x: 561, y: 472 },
  { text: "rb", x: 153, y: 520 },
  { text: "RB", x: 291, y: 520 },
  { text: "ST", x: 16, y: 596 },
  { text: "PR/KR", x: 840, y: 596 },
];

const OLINE_LABELS = [
  { text: "LT", x: 180, y: 35 },
  { text: "LG", x: 310, y: 35 },
  { text: "C", x: 440, y: 35 },
  { text: "RG", x: 570, y: 35 },
  { text: "RT", x: 700, y: 35 },
];

const DEFENCE_43_LINE_LABELS = [
  { text: "DE", x: 170, y: 35 },
  { text: "DT", x: 350, y: 35 },
  { text: "DT", x: 550, y: 35 },
  { text: "DE", x: 770, y: 35 },
];

const DEFENCE_34_LINE_LABELS = [
  { text: "DE", x: 250, y: 35 },
  { text: "NT", x: 470, y: 35 },
  { text: "DE", x: 690, y: 35 },
];

const centerGameDayLabels = (labels) => {
  const minX = Math.min(...labels.map((label) => label.x));
  const maxX = Math.max(...labels.map((label) => label.x + BOARD_LABEL_PRINT_WIDTH));
  const offsetX = Math.round((BOARD_PRINT_PAGE_WIDTH - (maxX - minX)) / 2) - minX;
  return labels.map((label) => ({ ...label, x: label.x + offsetX }));
};

const BOARD_HEADING_LABEL_SETS = {
  defaultOffence: DEFAULT_OFFENCE_LABELS,
  defaultDefence: DEFAULT_DEFENCE_LABELS,
  odsBeta: ODS_BETA_LABELS,
  odsNaIrFa: ODS_NA_IR_FA_LABELS,
  odsNegList: ODS_NEG_LIST_LABELS,
  oline: centerGameDayLabels(OLINE_LABELS),
  defense4: centerGameDayLabels(DEFENCE_43_LINE_LABELS),
  defense43: centerGameDayLabels(DEFENCE_43_LINE_LABELS),
  defense44: centerGameDayLabels(DEFENCE_43_LINE_LABELS),
  defense3: centerGameDayLabels(DEFENCE_34_LINE_LABELS),
  defense34: centerGameDayLabels(DEFENCE_34_LINE_LABELS),
  gameDayOffence: centerGameDayLabels(GAME_DAY_OFFENCE_LABELS),
  gameDayDefence: centerGameDayLabels(GAME_DAY_DEFENCE_43_LABELS),
  gameDayDefence43: centerGameDayLabels(GAME_DAY_DEFENCE_43_LABELS),
  gameDayDefence34: centerGameDayLabels(GAME_DAY_DEFENCE_34_LABELS),
  gameDayDefence44: centerGameDayLabels(GAME_DAY_DEFENCE_44_LABELS),
};

const GameDayLayoutOverlay = ({ type }) => {
  const defenceLayout = GAME_DAY_DEFENCE_LAYOUTS[type] || GAME_DAY_DEFENCE_LAYOUTS.gameDayDefence43;
  const isDefence = type !== "gameDayOffence";
  const spots = isDefence ? defenceLayout.spots : GAME_DAY_OFFENCE_SPOTS;
  const title = isDefence ? defenceLayout.title : "Offence";

  return (
    <div className={`draft-board-game-day-layout ${isDefence ? "draft-board-game-day-layout-defence" : "draft-board-game-day-layout-offence"}`}>
      <div className="draft-board-game-day-layout-title">{title}</div>
      {isDefence && (
        <>
          <div className="draft-board-game-day-midline" />
          <div className="draft-board-game-day-marker draft-board-game-day-boundary">BOUNDARY</div>
          <div className="draft-board-game-day-marker draft-board-game-day-field">FIELD</div>
        </>
      )}
      {spots.map((spot) => (
        <div
          key={`${spot.pos}-${spot.x}-${spot.y}`}
          className={`draft-board-game-day-spot ${isDefence ? "draft-board-game-day-spot-defence" : "draft-board-game-day-spot-offence"}`}
          style={{
            left: `${spot.x}%`,
            top: `${spot.y}%`,
            width: `${spot.w}%`,
          }}
        >
	          {spot.pos}
	        </div>
	      ))}
    </div>
  );
};

const normalizeBoardCardPosition = (item) => {
  const x = Number(item?.x);
  if (!Number.isFinite(x)) return item;

  // Only migrate when legacy rail width is explicitly marked on the item.
  const sourceRailWidth = Number(item?.removeRailWidth || item?.boardRemoveRailWidth || 0);
  const hasLegacyRailMarker = sourceRailWidth === LEGACY_BOARD_REMOVE_RAIL_WIDTH;
  const legacyOffset = sourceRailWidth - BOARD_REMOVE_RAIL_WIDTH;
  if (!hasLegacyRailMarker || !legacyOffset) return item;

  return {
    ...item,
    x: Math.max(BOARD_REMOVE_RAIL_WIDTH, x - legacyOffset),
    removeRailWidth: BOARD_REMOVE_RAIL_WIDTH,
  };
};

const buildBoardPage = (pageName = DEFAULT_BOARD_PAGE_NAME, pageData = {}) => ({
  pageId: pageData.pageId || `page-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  pageName: cleanText(pageName) || DEFAULT_BOARD_PAGE_NAME,
  pageType: normalizeBoardPageType(pageData.pageType),
  includeInBoardCount: pageData.includeInBoardCount !== false,
  hideWarnings: pageData.hideWarnings === true,
  players: Array.isArray(pageData.players) ? pageData.players.map(normalizeBoardCardPosition) : [],
  labels: Array.isArray(pageData.labels) ? pageData.labels : [],
  details: Array.isArray(pageData.details) ? pageData.details : [],
  overlays: Array.isArray(pageData.overlays) ? pageData.overlays : [],
  presetDraft: normalizePagePresetDraft(pageData.presetDraft || pageData.activePresetDraft),
});

const normalizeBoardPages = (boardData = {}) => {
  const sourcePages = Array.isArray(boardData.pages) ? boardData.pages : [];
  const pages = sourcePages
    .map((page, index) => buildBoardPage(page?.pageName || page?.name || `${DEFAULT_BOARD_PAGE_NAME} ${index + 1}`, {
      pageId: page?.pageId || page?.id,
      players: page?.players,
      labels: page?.labels,
      details: page?.details,
      overlays: page?.overlays,
      pageType: page?.pageType || page?.type,
      includeInBoardCount: page?.includeInBoardCount,
      hideWarnings: page?.hideWarnings,
      presetDraft: page?.presetDraft || page?.activePresetDraft,
    }))
    .filter((page) => page.pageId);

  if (pages.length > 0) return pages;

  return [buildBoardPage(DEFAULT_BOARD_PAGE_NAME, {
    players: boardData.players,
    labels: boardData.labels,
    details: boardData.details,
    overlays: boardData.overlays,
    pageType: boardData.pageType || boardData.type,
    includeInBoardCount: boardData.includeInBoardCount,
    hideWarnings: boardData.hideWarnings,
    presetDraft: boardData.presetDraft || boardData.activePresetDraft,
  })];
};

const getBoardLevelDraftPlayers = (boardData = {}, normalizedPages = []) => {
  if (Array.isArray(boardData.draftPlayers)) return boardData.draftPlayers;

  const sourcePages = Array.isArray(boardData.pages) && boardData.pages.length > 0
    ? boardData.pages
    : normalizedPages;
  const seenDraftIds = new Set();

  return sourcePages
    .flatMap((page) => Array.isArray(page?.draftPlayers) ? page.draftPlayers : [])
    .filter((player) => {
      const playerId = String(player?.FBXID || player?.draftItemId || "").trim();
      if (!playerId || seenDraftIds.has(playerId)) return false;
      seenDraftIds.add(playerId);
      return true;
    });
};

const stripPageDraftPlayers = (page = {}) => {
  const nextPage = { ...page };
  delete nextPage.draftPlayers;
  return nextPage;
};

const getBoardDataPlayerCount = (boardData = {}) => {
  const pages = normalizeBoardPages(boardData);
  return pages.reduce((count, page) => count + page.players.length, 0);
};

const getBoardPositionMarkerColor = (boardData = {}) => normalizeWhiteBoardPositionMarkerColor(
  boardData?.positionMarkerColor ||
  boardData?.boardPreferences?.positionMarkerColor ||
  boardData?.preferences?.positionMarkerColor
);

const WHITE_BOARD_BASE_TEAM_LEAGUES = ["CFL", "NFL"];
const WHITE_BOARD_NUMBER_DISPLAY_TYPES = [
  { code: "college", label: "College Number" },
  { code: "pro", label: "Pro Number" },
];
const WHITE_BOARD_POSITION_DISPLAY_TYPES = [
  { code: "college", label: "College Position" },
  { code: "pro", label: "Pro Position" },
];

const normalizeBoardNumberDisplayType = (value) => {
  const text = cleanText(value).toLowerCase();
  return text === "pro" ? "pro" : "college";
};

const normalizeBoardPositionDisplayType = (value) => {
  const text = cleanText(value).toLowerCase();
  return text === "pro" ? "pro" : "college";
};

const getBoardNumberDisplayType = (boardData = {}) => normalizeBoardNumberDisplayType(
  boardData?.numberDisplayType ||
  boardData?.boardPreferences?.numberDisplayType ||
  boardData?.preferences?.numberDisplayType ||
  "pro"
);

const getBoardPositionDisplayType = (boardData = {}) => normalizeBoardPositionDisplayType(
  boardData?.positionDisplayType ||
  boardData?.boardPreferences?.positionDisplayType ||
  boardData?.preferences?.positionDisplayType ||
  "pro"
);

const normalizeJerseyNumber = (value) => {
  const text = cleanText(value);
  if (text === "-1") return "0";
  if (text === "0") return "";
  return text;
};

const serializeJerseyNumber = (value) => {
  const text = cleanText(value);
  if (!text) return "";
  if (text === "0") return "-1";
  return text;
};

const getCollegeJerseyNumber = (player = {}) => normalizeJerseyNumber(
  firstCleanText(
    player?.collegeJersey,
    player?.CollegeJersey,
    player?.jersey,
    player?.JERSEY,
    player?.Jersey,
    player?.JRSEY,
    player?.College_Jersey,
    player?.COLLEGE_JERSEY
  )
);

const getProJerseyNumber = (player = {}) => normalizeJerseyNumber(
  firstCleanText(
    player?.proJersey,
    player?.ProJersey,
    player?.PROJERSEY,
    player?.Pro_Jersey,
    player?.proNumber,
    player?.ProNumber
  )
);

const getBoardJerseyNumber = (player = {}, numberDisplayType = "college") => (
  normalizeBoardNumberDisplayType(numberDisplayType) === "pro"
    ? getProJerseyNumber(player)
    : getCollegeJerseyNumber(player)
);

const getCollegeBoardPosition = (player = {}) => cleanText(
  player?.colPos1 ||
  player?.COL_POS1 ||
  player?.Col_Pos1 ||
  player?.colPos ||
  player?.COL_POS ||
  player?.College_Position ||
  player?.collegePosition
);

const getBoardLeagueGeneralPosition = (player = {}, league = "CFL") => (
  cleanText(league).toUpperCase() === "NFL"
    ? cleanText(player?.nflGp || player?.NFLGP || player?.nflGP || player?.NFL_GP || player?.nflGeneralPosition)
    : cleanText(player?.cflGp || player?.CFLGP || player?.cflGP || player?.CFL_GP || player?.cflGeneralPosition)
);

const getBoardDisplayPosition = (player = {}, positionDisplayType = "college", league = "CFL") => (
  normalizeBoardPositionDisplayType(positionDisplayType) === "pro"
    ? getBoardLeagueGeneralPosition(player, league)
    : getCollegeBoardPosition(player)
);

const getBoardLeagueStatus = (player = {}, league = "CFL") => (
  cleanText(league).toUpperCase() === "NFL"
    ? cleanText(player?.nflStatus || player?.NFLSTATUS || player?.NFLStatus)
    : cleanText(player?.cflStatus || player?.CFLSTATUS || player?.CFLStatus)
);

const getBoardLeagueTeam = (player = {}, league = "CFL") => (
  cleanText(league).toUpperCase() === "NFL"
    ? cleanText(player?.nflTeam || player?.NFLTEAM || player?.NFLTeam)
    : cleanText(player?.cflTeam || player?.CFLTEAM || player?.CFLTeam)
).toUpperCase();

const getBoardBaseTeamLeague = (boardData = {}) => {
  const sourceLeague = cleanText(
    boardData?.baseTeamLeague ||
    boardData?.boardPreferences?.baseTeamLeague ||
    boardData?.preferences?.baseTeamLeague ||
    boardData?.teamLeague
  ).toUpperCase();
  if (WHITE_BOARD_BASE_TEAM_LEAGUES.includes(sourceLeague)) return sourceLeague;

  const teamCode = cleanText(
    boardData?.baseTeamCode ||
    boardData?.boardPreferences?.baseTeamCode ||
    boardData?.preferences?.baseTeamCode ||
    boardData?.baseTeam
  ).toUpperCase();
  if (cflObject.some((team) => cleanText(team.Team).toUpperCase() === teamCode)) return "CFL";
  if (nflObject.some((team) => cleanText(team.Team).toUpperCase() === teamCode)) return "NFL";
  return "CFL";
};

const getBoardBaseTeamCode = (boardData = {}) => {
  if (Object.prototype.hasOwnProperty.call(boardData || {}, "baseTeamCode")) return cleanText(boardData?.baseTeamCode).toUpperCase();
  if (Object.prototype.hasOwnProperty.call(boardData?.boardPreferences || {}, "baseTeamCode")) return cleanText(boardData?.boardPreferences?.baseTeamCode).toUpperCase();
  if (Object.prototype.hasOwnProperty.call(boardData?.preferences || {}, "baseTeamCode")) return cleanText(boardData?.preferences?.baseTeamCode).toUpperCase();
  return cleanText(boardData?.baseTeam).toUpperCase();
};

const getBaseTeamOptions = (league, currentCode = "") => {
  const sourceTeams = league === "NFL" ? nflObject : cflObject;
  const options = [{ code: "", name: "No Base Team" }, ...sourceTeams.map((team) => ({
    code: cleanText(team.Team).toUpperCase(),
    name: cleanText(team.Team_Name),
  })).filter((team) => team.code)];
  const cleanCurrentCode = cleanText(currentCode).toUpperCase();
  if (cleanCurrentCode && !options.some((team) => team.code === cleanCurrentCode)) {
    return [{ code: cleanCurrentCode, name: `${cleanCurrentCode} (Current Team)` }, ...options];
  }
  return options;
};

const buildDraftPlayer = (player) => ({
  draftItemId: `${player?.FBXID || player?.boardItemId || Date.now()}-${Date.now()}`,
  FBXID: player?.FBXID || "",
  first: cleanText(player?.first),
  last: cleanText(player?.last),
  school: cleanText(player?.school),
  genPos: cleanText(player?.genPos),
  cflGp: getBoardLeagueGeneralPosition(player, "CFL"),
  nflGp: getBoardLeagueGeneralPosition(player, "NFL"),
});

const snapToGrid = (value) => Math.round(value / BOARD_GRID_SIZE) * BOARD_GRID_SIZE;
const snapDeltaToGrid = (value) => Math.round(value / BOARD_GRID_SIZE) * BOARD_GRID_SIZE;
const getBoardSelectionKey = (type, id) => `${type}:${id}`;

const getScoutId = (scout) => String(scout?.userId ?? scout?.scoutId ?? scout?.scout ?? scout?.id ?? scout ?? "").trim();

const getScoutName = (scout) => cleanText(scout?.displayName) || cleanText(scout?.name) || cleanText(scout?.fullName) || getScoutId(scout);

const getCompactScoutName = (name) => {
  const cleanName = cleanText(name);
  const parts = cleanName.split(/\s+/).filter(Boolean);
  if (parts.length < 2) return cleanName;
  return `${parts[0].charAt(0)}.${parts[parts.length - 1]}`;
};

const getScoutTeam = (scout) => cleanText(scout?.team || scout?.Team || scout?.TEAM).toUpperCase();

const isActiveScout = (scout) => {
  const activeValue = cleanText(scout?.isActive || scout?.active || scout?.Active || scout?.status || scout?.Status).toLowerCase();
  return !activeValue || ["yes", "y", "active", "a", "1", "true"].includes(activeValue);
};

const getPlayerField = (player, ...keys) => {
  for (const key of keys) {
    const value = player?.[key];
    if (cleanText(value)) return cleanText(value);
  }
  return "";
};

const normalizeHeadingText = (value) => cleanText(value).toUpperCase().replace(/[^A-Z0-9/]/g, "");

const POSITION_HEADING_CANDIDATE_MAP = {
  CB: ["CB", "DC", "DB"],
  DC: ["CB", "DB", "HB", "DC"],
  DB: ["CB", "DB", "HB"],
  HB: ["HB", "DB", "CB"],
  S: ["S", "FS"],
  FS: ["FS", "S"],
  WR: ["WR", "SB"],
  SB: ["SB", "WR"],
  OL: ["OL", "LT", "LG", "RG", "RT"],
  OLINE: ["OL", "LT", "LG", "RG", "RT"],
  OT: ["LT", "RT", "OL"],
  OG: ["LG", "RG", "OL"],
  C: ["C"],
  OC: ["C"],
};

const OLINE_PLAYER_POSITIONS = ["OL", "OLINE", "OT", "OG", "OC", "C"];
const CENTER_PLAYER_POSITIONS = ["C", "OC"];

const getPlayerPositionValues = (player, preferredValues = []) => {
  const exactPosition = getPlayerField(
    player,
    "colPos1",
    "COL_POS1",
    "colPos",
    "PLAYER_POS",
    "playerPos",
    "position",
    "Position",
    "POS",
    "pos"
  );
  const generalPosition = getPlayerField(player, "genPos", "GENPOS", "generalPos", "GeneralPos");

  return [...preferredValues, exactPosition, generalPosition]
    .map(normalizeHeadingText)
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index);
};

const formatDraftBoardDate = (value) => {
  const text = cleanText(value);
  if (!text || text === "0000-00-00") return "";
  return text;
};

const getDraftBoardMetricParts = (value, verified, date, surface = "") => ({
  value: cleanText(value) || "-",
  verified: cleanText(verified) || "-",
  date: formatDraftBoardDate(date) || "-",
  surface: cleanText(surface) || "-",
});

const getDraftBoardSpeedParts = (player) => {
  const rawSpeed = getPlayerField(player, "speed40Fastest", "SPEED40FASTEST");
  const speedNumber = Number(rawSpeed);
  const speed = rawSpeed && Number.isFinite(speedNumber) ? speedNumber.toFixed(2) : rawSpeed;
  return getDraftBoardMetricParts(
    speed,
    getPlayerField(player, "speed40FVerified", "Speed40_F_Verified", "SPEED40_F_VERIFIED"),
    getPlayerField(player, "speed40FDate", "speeddate", "Speed40_F_Date", "SPEED40_F_DATE"),
    getPlayerField(player, "speed40FSurface", "Speed40_F_Surface", "SPEED40_F_SURFACE")
  );
};

const getBoardUpdatedAt = (board = {}) => cleanText(
  board?.updatedAt ||
  board?.serverUpdatedAt ||
  board?.lastUpdated ||
  board?.boardUpdatedAt ||
  board?.updated_at
);

const getBoardCreatedAt = (board = {}) => cleanText(
  board?.createdAt ||
  board?.created_at ||
  board?.creationDate ||
  board?.boardCreatedAt
);

const getBoardCreatedBy = (board = {}) => cleanText(
  board?.scoutName ||
  board?.createdBy ||
  board?.createdByScoutName ||
  board?.ownerName
);

const getBoardUpdatedBy = (board = {}) => cleanText(
  board?.updatedByScoutName ||
  board?.updatedBy ||
  board?.lastEditedBy ||
  board?.editedBy ||
  board?.updated_by
);

const formatBoardCreationDate = (value) => {
  const text = cleanText(value);
  if (!text || text === "0000-00-00" || text === "0000-00-00 00:00:00") return "";
  const normalized = text.includes("T") ? text : text.replace(" ", "T");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return text;
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatBoardEditedDateTime = (value) => {
  const text = cleanText(value);
  if (!text || text === "0000-00-00" || text === "0000-00-00 00:00:00") return "";
  const normalized = text.includes("T") ? text : text.replace(" ", "T");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return text;
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
};

const getBoardVersion = (board = {}) => {
  const version = Number.parseInt(board?.boardVersion || board?.version || 0, 10);
  return Number.isFinite(version) ? version : 0;
};

const getBoardVersionTime = (value) => {
  const text = cleanText(value);
  if (!text) return 0;
  const normalized = text.includes("T") ? text : text.replace(" ", "T");
  const time = Date.parse(normalized);
  return Number.isFinite(time) ? time : 0;
};

const isDatabaseBoardNewer = (databaseBoard = {}, localBoard = {}) => {
  const databaseVersion = getBoardVersion(databaseBoard);
  const localVersion = getBoardVersion(localBoard);
  if (databaseVersion && localVersion) return databaseVersion > localVersion;
  if (databaseVersion && !localVersion) return true;

  const databaseUpdatedAt = getBoardUpdatedAt(databaseBoard);
  if (!databaseUpdatedAt) return false;

  const localUpdatedAt = getBoardUpdatedAt(localBoard);
  if (!localUpdatedAt) return true;

  const databaseTime = getBoardVersionTime(databaseUpdatedAt);
  const localTime = getBoardVersionTime(localUpdatedAt);

  if (databaseTime && localTime) {
    return databaseTime > localTime + 500;
  }

  return databaseUpdatedAt !== localUpdatedAt;
};

const getLeagueData = (player, league) => {
  const playerLeague = cleanText(player?.League || player?.league).toUpperCase();
  const targetLeague = league === "UFL24" ? "UFL24" : league;
  const matchesGenericLeague = playerLeague === targetLeague || (targetLeague === "UFL24" && playerLeague === "UFL");
  const fieldMap = {
    CFL: {
      team: ["cflTeam", "CFLTEAM", "CFLTeam"],
      position: ["cflGp", "CFLGP", "cflGP", "CFL_GP", "cflPos", "CFLPos", "CFLPOS"],
      status: ["cflStatus", "CFLSTATUS", "CFLStatus"],
      date: ["cflDate", "CFLDATE", "CFLDate"],
    },
    NFL: {
      team: ["nflTeam", "NFLTEAM", "NFLTeam"],
      position: ["nflGp", "NFLGP", "nflGP", "NFL_GP", "nflPos", "NFLPos", "NFLPOS"],
      status: ["nflStatus", "NFLSTATUS", "NFLStatus"],
      date: ["nflDate", "NFLDATE", "NFLDate"],
    },
    UFL24: {
      team: ["ufl24Team", "UFL24TEAM", "UFL24Team"],
      position: ["ufl24Pos", "UFL24Pos", "UFL24POS"],
      status: ["ufl24Status", "UFL24STATUS", "UFL24Status"],
      date: ["ufl24Date", "UFL24DATE", "UFL24Date"],
    },
    EVENT: {
      team: ["theEvent", "event", "EVENT", "Event"],
      position: ["eventPos", "EventPos", "EVENTPOS", "theEventPos", "TheEventPos"],
      status: ["eventStatus", "eventSide", "EventStatus", "EventSide"],
      date: ["theEventDate", "eventDate", "EVENTDATE", "EventDate"],
    },
  };

  const fields = fieldMap[league] || {};
  const genericTeam = matchesGenericLeague ? getPlayerField(player, "Team", "team") : "";
  const genericPosition = matchesGenericLeague ? getPlayerField(player, "Position", "position", "ProPosition", "proPosition", "proPos", "PRO_POS") : "";
  const genericStatus = matchesGenericLeague ? getPlayerField(player, "Status", "status") : "";
  const genericDate = matchesGenericLeague ? getPlayerField(player, "theDate", "TheDate", "date", "Date") : "";

  return {
    team: getPlayerField(player, ...(fields.team || [])) || genericTeam,
    position: getPlayerField(player, ...(fields.position || [])) || genericPosition,
    status: getPlayerField(player, ...(fields.status || [])) || genericStatus,
    date: formatDraftBoardDate(getPlayerField(player, ...(fields.date || [])) || genericDate),
  };
};

const getDraftBoardPlayerId = (player) => String(player?.FBXID || player?.DRNUM || player?.DR_Num || player?.drnum || "").trim();

const getTransactionValue = (transaction, ...keys) => {
  for (const key of keys) {
    const value = cleanText(transaction?.[key]);
    if (value) return value;
  }
  return "";
};

const getDraftBoardTransactionLeague = (transaction) => {
  const league = getTransactionValue(transaction, "League", "league").toUpperCase();
  if (league === "CFL" || league === "NFL") return league;
  if (league === "UFL" || league === "UFL24") return "UFL";
  return "";
};

const formatBoardTransactionDate = (value) => {
  const text = cleanText(value);
  if (!text) return "";
  if (text === "0000-00-00" || text === "0000-00-00 00:00:00") return "";
  const dateOnly = text.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
    const [year, month, day] = dateOnly.split("-").map(Number);
    const localDate = new Date(year, month - 1, day);
    if (!Number.isNaN(localDate.getTime())) {
      return localDate.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
  }
  const normalized = text.includes("T") ? text : text.replace(" ", "T");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return text;
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const DraftBoard = () => {
  const { listArray, sharedListArray, savedQueryArray, scoutArray = [], positionArray = [], schoolArray = [] } = useDataLayer();
  const { updateActiveSideButton, updateSearchedListName } = useNavBar();
  const { playerData, activePlayer, setPlayerData, setSavedOriginalPlayerData, setActivePlayer, setPlayerID, setListLoadID } = usePlayer();
  const [boardsOpen, setBoardsOpen] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const [boardPreferencesOpen, setBoardPreferencesOpen] = useState(false);
  const [boardPreferenceColorDraft, setBoardPreferenceColorDraft] = useState(DEFAULT_WHITE_BOARD_POSITION_MARKER_COLOR);
  const [boardPreferenceBaseTeamLeagueDraft, setBoardPreferenceBaseTeamLeagueDraft] = useState("CFL");
  const [boardPreferenceBaseTeamCodeDraft, setBoardPreferenceBaseTeamCodeDraft] = useState("");
  const [boardPreferenceNumberDisplayTypeDraft, setBoardPreferenceNumberDisplayTypeDraft] = useState("pro");
  const [boardPreferencePositionDisplayTypeDraft, setBoardPreferencePositionDisplayTypeDraft] = useState("pro");
  const [boardPreferencesMessage, setBoardPreferencesMessage] = useState("");
  const [boardPositionMarkerColor, setBoardPositionMarkerColor] = useState(DEFAULT_WHITE_BOARD_POSITION_MARKER_COLOR);
  const [boardBaseTeamLeague, setBoardBaseTeamLeague] = useState("CFL");
  const [boardBaseTeamCode, setBoardBaseTeamCode] = useState("");
  const [boardNumberDisplayType, setBoardNumberDisplayType] = useState("pro");
  const [boardPositionDisplayType, setBoardPositionDisplayType] = useState("pro");
  const [overlaysOpen, setOverlaysOpen] = useState(false);
  const [quickSearchOpen, setQuickSearchOpen] = useState(false);
  const [quickSearchValues, setQuickSearchValues] = useState(DRAFT_BOARD_SEARCH_DEFAULTS);
  const [quickSearchLoading, setQuickSearchLoading] = useState(false);
  const [quickSearchMessage, setQuickSearchMessage] = useState("");
  const [openQuickSearchCalendar, setOpenQuickSearchCalendar] = useState(null);
  const [playersOpen, setPlayersOpen] = useState(false);
  const [onBoardOpen, setOnBoardOpen] = useState(false);
  const [draftOpen, setDraftOpen] = useState(false);
  const [dataOpen, setDataOpen] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [boardPlayers, setBoardPlayers] = useState([]);
  const [boardLabels, setBoardLabels] = useState([]);
  const [boardDetails, setBoardDetails] = useState([]);
  const [boardOverlays, setBoardOverlays] = useState([]);
  const [draftPlayers, setDraftPlayers] = useState([]);
  const [boardPages, setBoardPages] = useState(() => [buildBoardPage()]);
  const [activePageId, setActivePageId] = useState("");
  const [showNewPageForm, setShowNewPageForm] = useState(false);
  const [newPageName, setNewPageName] = useState("");
  const [newPageType, setNewPageType] = useState(BOARD_PAGE_TYPES.regular);
  const [newPageNameError, setNewPageNameError] = useState("");
  const [pageEditorOpen, setPageEditorOpen] = useState(false);
  const [pageEditorDraft, setPageEditorDraft] = useState([]);
  const [pageEditorError, setPageEditorError] = useState("");
  const [user, setUser] = useState(null);
  const [whiteBoardPreferences, setWhiteBoardPreferences] = useState(() => normalizeWhiteBoardPreferences());
  const [saveMessage, setSaveMessage] = useState("");
  const [saveMessagePulse, setSaveMessagePulse] = useState(0);
  const [undoSnapshot, setUndoSnapshot] = useState(null);
  const [highlightedFBXID, setHighlightedFBXID] = useState("");
  const [boardName, setBoardName] = useState(BOARD_CHOOSER_NAME);
  const [isEditingBoardName, setIsEditingBoardName] = useState(false);
  const [editBoardName, setEditBoardName] = useState("");
  const [editBoardNameError, setEditBoardNameError] = useState("");
  const [savedBoards, setSavedBoards] = useState([]);
  const [boardsLoading, setBoardsLoading] = useState(false);
  const [boardsError, setBoardsError] = useState("");
  const [showNewBoardForm, setShowNewBoardForm] = useState(false);
  const [showShareBoardForm, setShowShareBoardForm] = useState(false);
  const [teamPresets, setTeamPresets] = useState([]);
  const [teamPresetsLoading, setTeamPresetsLoading] = useState(false);
  const [teamPresetMessage, setTeamPresetMessage] = useState("");
  const [activePresetDraft, setActivePresetDraft] = useState(null);
  const [presetsPopupPosition, setPresetsPopupPosition] = useState(getStoredPresetsPopupPosition);
  const [pendingPresetChoice, setPendingPresetChoice] = useState(null);
  const [saveBoardName, setSaveBoardName] = useState("");
  const [saveBoardNameError, setSaveBoardNameError] = useState("");
  const [showWelcomeBoardPrompt, setShowWelcomeBoardPrompt] = useState(false);
  const [welcomeBoardName, setWelcomeBoardName] = useState("");
  const [welcomeBoardNameError, setWelcomeBoardNameError] = useState("");
  const [savedBoardId, setSavedBoardId] = useState(null);
  const [currentBoardCreatedAt, setCurrentBoardCreatedAt] = useState("");
  const [currentBoardCreatedBy, setCurrentBoardCreatedBy] = useState("");
  const [currentBoardUpdatedAt, setCurrentBoardUpdatedAt] = useState("");
  const [currentBoardUpdatedBy, setCurrentBoardUpdatedBy] = useState("");
  const [currentBoardVersion, setCurrentBoardVersion] = useState(0);
  const [currentBoardIsOwner, setCurrentBoardIsOwner] = useState(true);
  const [currentBoardCanEdit, setCurrentBoardCanEdit] = useState(true);
  const [currentBoardShareCount, setCurrentBoardShareCount] = useState(0);
  const [shareScoutIds, setShareScoutIds] = useState([]);
  const [shareCanEdit, setShareCanEdit] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareMessage, setShareMessage] = useState("");
  const [adminShareScouts, setAdminShareScouts] = useState([]);
  const [adminShareLoading, setAdminShareLoading] = useState(false);
  const [adminShareMessage, setAdminShareMessage] = useState("");
  const [expandedShareTeam, setExpandedShareTeam] = useState("");
  const [notePlayer, setNotePlayer] = useState(null);
  const [playerNoteMode, setPlayerNoteMode] = useState("edit");
  const [playerNoteCache, setPlayerNoteCache] = useState({});
  const [playerDetailCache, setPlayerDetailCache] = useState({});
  const [playerDetailLoading, setPlayerDetailLoading] = useState(false);
  const [expandedTransactionPlayers, setExpandedTransactionPlayers] = useState({});
  const [playerTransactionCache, setPlayerTransactionCache] = useState({});
  const [playerNoteText, setPlayerNoteText] = useState("");
  const [playerQuickNoteVersions, setPlayerQuickNoteVersions] = useState([]);
  const [playerQuickNoteVersionIndex, setPlayerQuickNoteVersionIndex] = useState(0);
  const [playerGrade, setPlayerGrade] = useState("");
  const [playerBoardPosition, setPlayerBoardPosition] = useState("");
  const [playerBoardNumber, setPlayerBoardNumber] = useState("");
  const [playerNoteLoading, setPlayerNoteLoading] = useState(false);
  const [playerNoteMessage, setPlayerNoteMessage] = useState("");
  const [positionFilter, setPositionFilter] = useState("");
  const [lastFilter, setLastFilter] = useState("");
  const [firstFilter, setFirstFilter] = useState("");
  const [positionUnitFilter, setPositionUnitFilter] = useState("");
  const [onBoardPositionFilter, setOnBoardPositionFilter] = useState("");
  const [onBoardLastFilter, setOnBoardLastFilter] = useState("");
  const [onBoardFirstFilter, setOnBoardFirstFilter] = useState("");
  const [onBoardPositionUnitFilter, setOnBoardPositionUnitFilter] = useState("");
  const [playersOnlyOnBoard, setPlayersOnlyOnBoard] = useState(false);
  const [groupSelectOpen, setGroupSelectOpen] = useState(false);
  const [selectedBoardItems, setSelectedBoardItems] = useState({});
  const [alignmentMenuOpen, setAlignmentMenuOpen] = useState(false);
  const [groupDragPreviewDelta, setGroupDragPreviewDelta] = useState(null);
  const [boardImpNipFilter, setBoardImpNipFilter] = useState("");
  const [countScope, setCountScope] = useState("page");
  const [boardSizeMode, setBoardSizeMode] = useState(() => {
    const savedMode = window.localStorage.getItem(DRAFT_BOARD_SIZE_MODE_KEY);
    return ["full", "grid", "grid-centered"].includes(savedMode) ? savedMode : "print";
  });
  const [viewOptionsOpen, setViewOptionsOpen] = useState(false);
  const [clearOptionsOpen, setClearOptionsOpen] = useState(false);
  const [pageTransferHoverId, setPageTransferHoverId] = useState("");
  const draftBoardScreenRef = useRef(null);
  const whiteboardRef = useRef(null);
  const groupDragRef = useRef(null);
  const suppressGroupDragClickRef = useRef(false);
  const quickSearchRef = useRef(null);
  const presetsPopupDragRef = useRef(null);
  const playerListRowRefs = useRef({});
  const playerListContainerRefs = useRef({});
  const pageEditorDragPageIdRef = useRef("");
  const pageTransferDragRef = useRef(null);
  const pageTransferTimerRef = useRef(null);
  const playerSnapshotRefreshKeyRef = useRef("");
  const detailSaveTimerRef = useRef(null);
  const labelSaveTimerRef = useRef(null);
  const activeLabelEditUntilRef = useRef(0);

  useEffect(() => {
    return () => {
      if (detailSaveTimerRef.current) {
        window.clearTimeout(detailSaveTimerRef.current);
        detailSaveTimerRef.current = null;
      }
      if (labelSaveTimerRef.current) {
        window.clearTimeout(labelSaveTimerRef.current);
        labelSaveTimerRef.current = null;
      }
    };
  }, []);

  const setPlayerListRowRef = useCallback((listKey, playerId) => (element) => {
    const key = `${listKey}:${String(playerId || "").trim()}`;
    if (!key.endsWith(":")) {
      if (element) {
        playerListRowRefs.current[key] = element;
      } else {
        delete playerListRowRefs.current[key];
      }
    }
  }, []);

  const setPlayerListContainerRef = useCallback((listKey) => (element) => {
    if (element) {
      playerListContainerRefs.current[listKey] = element;
    } else {
      delete playerListContainerRefs.current[listKey];
    }
  }, []);
  const isPrintSizeMode = boardSizeMode === "print";
  const isGridSizeMode = boardSizeMode === "grid" || boardSizeMode === "grid-centered";
  const isCenteredGridSizeMode = boardSizeMode === "grid-centered";
  const boardSizeModeLabel = {
    print: "Print Size",
    grid: "Guide Right",
    "grid-centered": "Guide Centered",
  }[boardSizeMode] || "Print Size";
  const boardCardWidth = BOARD_PRINT_CARD_WIDTH;
  const boardCardHeight = BOARD_PRINT_CARD_HEIGHT;
  const boardOverlayColumnWidth = boardCardWidth + BOARD_REMOVE_RAIL_WIDTH + 8;
  const getOverlayWidth = useCallback((overlayConfig) => overlayConfig.width || (boardOverlayColumnWidth * overlayConfig.cells.length), [boardOverlayColumnWidth]);
  const getOverlayHeight = useCallback((overlayConfig) => overlayConfig.height || 32, []);
  const canEditCurrentBoard = !savedBoardId || currentBoardCanEdit;
  const currentScoutId = String(user?.scout || "").trim();
  const isAdminUser = cleanText(user?.userContactLevel || user?.security || user?.Security) === "1";
  const currentScoutRecord = (Array.isArray(scoutArray) ? scoutArray : []).find((scout) => getScoutId(scout) === currentScoutId);
  const currentScoutName = cleanText(user?.displayName) || getScoutName(currentScoutRecord) || currentScoutId || "Scout";
  const boardCreationStamp = [
    formatBoardCreationDate(currentBoardCreatedAt),
    currentBoardCreatedBy,
  ].filter(Boolean).join(" by ");
  const boardEditedStamp = formatBoardEditedDateTime(currentBoardUpdatedAt);
  const boardEditedByStamp = cleanText(currentBoardUpdatedBy);
  const showBoardEditedByStamp = Boolean(boardEditedByStamp);
  const showSavedMessage = useCallback(() => {
    setSaveMessage("Saved");
    setSaveMessagePulse((pulse) => pulse + 1);
  }, []);
  const fetchWhiteBoardPreferences = useCallback(async (team = user?.team) => {
    if (!team) {
      setWhiteBoardPreferences(normalizeWhiteBoardPreferences());
      return;
    }

    try {
      const response = await fetch(DRAFT_BOARD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "getPreferences",
          team,
        }),
      });
      const data = await response.json();
      if (data?.success) {
        setWhiteBoardPreferences(normalizeWhiteBoardPreferences(data.preferences));
      }
    } catch {}
  }, [user]);

  useEffect(() => {
    fetchWhiteBoardPreferences(user?.team);
  }, [fetchWhiteBoardPreferences, user]);

  useEffect(() => {
    const handlePreferencesChange = (event) => {
      setWhiteBoardPreferences(normalizeWhiteBoardPreferences(event?.detail?.preferences));
    };

    window.addEventListener(WHITE_BOARD_PREFERENCES_EVENT, handlePreferencesChange);
    return () => window.removeEventListener(WHITE_BOARD_PREFERENCES_EVENT, handlePreferencesChange);
  }, [user?.team]);

  useEffect(() => () => {
    if (pageTransferTimerRef.current) window.clearTimeout(pageTransferTimerRef.current);
  }, []);

  const resetWhiteBoardPopups = () => {
    DRAFT_BOARD_POPUP_POSITION_KEYS.forEach((storageKey) => {
      try {
        window.localStorage.removeItem(storageKey);
      } catch {}

      window.dispatchEvent(new CustomEvent(DRAFT_BOARD_POPUP_RESET_EVENT, {
        detail: { storageKey },
      }));
    });

    try {
      window.localStorage.removeItem(DRAFT_BOARD_PRESETS_POPUP_POSITION_KEY);
    } catch {}

    setPresetsPopupPosition(getDefaultPresetsPopupPosition());
    setPendingPresetChoice(null);
    setSaveMessage("Popup positions reset");
    setSaveMessagePulse((pulse) => pulse + 1);
  };
  const boardPositionOptions = useMemo(() => (Array.isArray(positionArray) ? positionArray : [])
    .map((position) => {
      const code = cleanText(position?.POS_SPEC_CODE);
      const description = cleanText(position?.POS_DESC);
      return {
        code,
        label: description && description !== code ? `${code} - ${description}` : code,
      };
    })
    .filter((position) => position.code)
    .filter((position, index, positions) => positions.findIndex((item) => item.code === position.code) === index),
  [positionArray]);
  const shareScoutOptions = (Array.isArray(scoutArray) ? scoutArray : [])
    .map((scout) => ({
      id: getScoutId(scout),
      name: getScoutName(scout),
    }))
    .filter((scout) => scout.id && scout.id !== currentScoutId);
  const cflShareTeams = [
    { code: "FBX", name: "FBX" },
    { code: "CFLHQ", name: "CFL HQ" },
    ...cflObject
    .filter((team) => cleanText(team?.Affiliation) === "CFL")
    .map((team) => ({
      code: cleanText(team?.Team),
      name: cleanText(team?.Team_Name) || cleanText(team?.Team),
    })),
  ].filter((team) => team.code);
  const adminShareScoutsByTeam = cflShareTeams.reduce((teams, team) => {
    teams[team.code] = (Array.isArray(adminShareScouts) ? adminShareScouts : [])
      .filter((scout) => getScoutTeam(scout) === team.code)
      .filter((scout) => isActiveScout(scout))
      .map((scout) => ({
        id: getScoutId(scout),
        name: getScoutName(scout),
      }))
      .filter((scout) => scout.id && scout.id !== currentScoutId);
    return teams;
  }, {});
  const fbxShareScoutIds = FBX_SHARE_SCOUT_IDS;
  const isSharedToFbx = fbxShareScoutIds.some((scoutId) => shareScoutIds.includes(scoutId));
  const canUseShareToFbx = !isAdminUser && cleanText(user?.team).toUpperCase() !== "FBX";
  const currentShareBadgeCanEdit = currentBoardIsOwner ? shareCanEdit : currentBoardCanEdit;
  const currentBoardIsShared = Boolean(savedBoardId && (currentBoardShareCount > 0 || !currentBoardIsOwner));

  const safePlayers = Array.isArray(playerData) ? playerData : [];
  const selectedBoardItemCount = Object.keys(selectedBoardItems).length;
  const canAlignSelectedItems = canEditCurrentBoard && groupSelectOpen && selectedBoardItemCount > 1;
  const canDistributeSelectedItems = canAlignSelectedItems && selectedBoardItemCount > 2;
  const activePage = boardPages.find((page) => page.pageId === activePageId) || boardPages[0] || buildBoardPage();
  const activePageName = activePage?.pageName || DEFAULT_BOARD_PAGE_NAME;
  const activePageType = normalizeBoardPageType(activePage?.pageType);
  const isSplitSortPage = activePageType === BOARD_PAGE_TYPES.splitSort;
  const isFlippedPage = activePageType === BOARD_PAGE_TYPES.flipped;
  const activePageHideWarnings = activePage?.hideWarnings === true;
  const boardDisplayName = `${boardName} [${activePageName}]`;
  const allBoardPlayers = boardPages
    .flatMap((page) => (
      page.pageId === activePageId
        ? boardPlayers
        : (Array.isArray(page.players) ? page.players : [])
    ));
  const boardPlayerImpNipById = allBoardPlayers.reduce((lookup, item) => {
    const playerId = String(item?.FBXID || "").trim();
    const impnip = cleanText(item?.impnip || item?.IMPNIP).toUpperCase();
    if (playerId && impnip && !lookup[playerId]) lookup[playerId] = impnip;
    return lookup;
  }, {});
  const safePlayerImpNipById = safePlayers.reduce((lookup, player) => {
    const playerId = String(player?.FBXID || "").trim();
    const impnip = cleanText(player?.impnip || player?.IMPNIP).toUpperCase();
    if (playerId && impnip && !lookup[playerId]) lookup[playerId] = impnip;
    return lookup;
  }, {});
  const safePlayerById = safePlayers.reduce((lookup, player) => {
    const playerId = String(player?.FBXID || "").trim();
    if (playerId && !lookup[playerId]) lookup[playerId] = player;
    return lookup;
  }, {});
  const boardCountPlayers = boardPages
    .filter((page) => page?.includeInBoardCount !== false)
    .flatMap((page) => (
      page.pageId === activePageId
        ? boardPlayers
        : (Array.isArray(page.players) ? page.players : [])
    ));
  const uniqueBoardPlayers = boardCountPlayers.reduce((players, item) => {
    const id = String(item?.FBXID || "").trim();
    const key = id || `item-${item?.boardItemId || players.length}`;
    const existingIndex = players.findIndex((player) => player.uniqueBoardKey === key);
    if (existingIndex >= 0) {
      const existingImpNip = cleanText(players[existingIndex]?.impnip || players[existingIndex]?.IMPNIP);
      const itemImpNip = cleanText(item?.impnip || item?.IMPNIP);
      if (!existingImpNip && itemImpNip) {
        players[existingIndex] = {
          ...players[existingIndex],
          impnip: itemImpNip,
        };
      }
      return players;
    }
    players.push({
      ...item,
      uniqueBoardKey: key,
    });
    return players;
  }, []);
  const uniquePagePlayers = boardPlayers.reduce((players, item) => {
    const id = String(item?.FBXID || "").trim();
    const key = id || `item-${item?.boardItemId || players.length}`;
    const existingIndex = players.findIndex((player) => player.uniquePageKey === key);
    if (existingIndex >= 0) {
      const existingImpNip = cleanText(players[existingIndex]?.impnip || players[existingIndex]?.IMPNIP);
      const itemImpNip = cleanText(item?.impnip || item?.IMPNIP);
      if (!existingImpNip && itemImpNip) {
        players[existingIndex] = {
          ...players[existingIndex],
          impnip: itemImpNip,
        };
      }
      return players;
    }
    players.push({
      ...item,
      uniquePageKey: key,
    });
    return players;
  }, []);
  const uniquePagePlayerTotal = uniquePagePlayers.length;
  const uniqueBoardPlayerTotal = uniqueBoardPlayers.length;
  const activePresetLabels = activePresetDraft?.presetSessionId
    ? boardLabels
    : [];
  const activePresetSnapshot = stringifyPresetLabels(activePresetLabels);
  const activePresetModified = Boolean(
    activePresetDraft &&
    boardLabels.length > 0 &&
    (activePresetSnapshot !== activePresetDraft.baseline || getPresetPageTypeFromData(activePresetDraft, activePresetDraft.name) !== activePageType)
  );
  const boardPagePresetStamp = activePresetDraft && activePresetLabels.length > 0
    ? `Page Preset: ${activePresetDraft.name || "Page Preset"}${activePresetModified ? " modified" : ""}`
    : "";
  const hasBoardInfoStamp = Boolean(boardCreationStamp || boardEditedStamp || boardPagePresetStamp);
  const renderBoardInfoStamp = () => {
    if (!hasBoardInfoStamp) return null;

    return (
      <div className="draft-board-info-stamp">
        {boardCreationStamp && (
          <div className="draft-board-info-stamp-row">
            Creation Date: {boardCreationStamp}
          </div>
        )}
        {boardEditedStamp && (
          <>
            <div className="draft-board-info-stamp-row">Edited: {boardEditedStamp}</div>
            {showBoardEditedByStamp && (
              <div className="draft-board-info-stamp-row">By: {boardEditedByStamp}</div>
            )}
          </>
        )}
        {boardPagePresetStamp && (
          <div className="draft-board-info-stamp-row">{boardPagePresetStamp}</div>
        )}
      </div>
    );
  };
  const canSavePresetAs = Boolean((activePresetDraft && boardLabels.length > 0) || boardLabels.length > 0);
  const getDetailScoutId = (item) => String(item?.createdByScoutId || item?.scoutId || "").trim();
  const getDetailScoutName = (item) => cleanText(item?.createdByScoutName) || cleanText(item?.scoutName) || getDetailScoutId(item);
  const isOwnDetail = (item) => {
    const detailScoutId = getDetailScoutId(item);
    return !detailScoutId || detailScoutId === currentScoutId;
  };
  const getPlayerGrade = (playerId) => cleanText(playerNoteCache[String(playerId || "").trim()]?.grade);
  const getMergedBoardPlayerData = (player) => {
    const playerId = String(player?.FBXID || "").trim();
    const listPlayer = playerId ? safePlayerById[playerId] : null;
    const detailPlayer = playerId ? playerDetailCache[playerId] : null;
    return { ...(player || {}), ...(listPlayer || {}), ...(detailPlayer || {}) };
  };
  const getPlayerBoardPosition = (player) => {
    const playerId = String(player?.FBXID || "").trim();
    return cleanText(playerNoteCache[playerId]?.boardPosition) || getBoardDisplayPosition(
      getMergedBoardPlayerData(player),
      boardPositionDisplayType,
      boardBaseTeamLeague
    );
  };
  const getPlayerBoardFilterPosition = (player) => {
    const playerId = String(player?.FBXID || "").trim();
    const mergedPlayer = getMergedBoardPlayerData(player);
    return cleanText(playerNoteCache[playerId]?.boardPosition) ||
      getBoardLeagueGeneralPosition(mergedPlayer, boardBaseTeamLeague) ||
      cleanText(mergedPlayer?.genPos || mergedPlayer?.GENPOS || mergedPlayer?.generalPos || mergedPlayer?.GeneralPos) ||
      getBoardDisplayPosition(mergedPlayer, "college", boardBaseTeamLeague);
  };
  const getPlayerBoardPositionValues = (player) => {
    const playerId = String(player?.FBXID || "").trim();
    const mergedPlayer = getMergedBoardPlayerData(player);
    const overridePosition = cleanText(playerNoteCache[playerId]?.boardPosition);
    const preferredPosition = overridePosition || getBoardDisplayPosition(
      mergedPlayer,
      boardPositionDisplayType,
      boardBaseTeamLeague
    );
    const preferredGeneralPosition = normalizeBoardPositionDisplayType(boardPositionDisplayType) === "pro"
      ? getBoardLeagueGeneralPosition(mergedPlayer, boardBaseTeamLeague)
      : cleanText(mergedPlayer?.genPos || mergedPlayer?.GENPOS || mergedPlayer?.generalPos || mergedPlayer?.GeneralPos);
    return getPlayerPositionValues(mergedPlayer, [preferredPosition, preferredGeneralPosition]);
  };
  const getPlayerBoardHeadingCandidates = (player) => {
    return getPlayerBoardPositionValues(player)
      .flatMap((value) => POSITION_HEADING_CANDIDATE_MAP[value] || [value])
      .filter((value, index, values) => values.indexOf(value) === index);
  };
  const isBoardOffensiveLinePlayer = (player) => {
    return getPlayerBoardPositionValues(player)
      .some((value) => OLINE_PLAYER_POSITIONS.includes(value));
  };
  const getBoardOffensiveLineHeadingCandidates = (player) => {
    const positionValues = getPlayerBoardPositionValues(player);
    const isCenter = positionValues.some((value) => CENTER_PLAYER_POSITIONS.includes(value));
    if (isCenter) return ["C"];

    return positionValues
      .flatMap((value) => POSITION_HEADING_CANDIDATE_MAP[value] || [value])
      .filter((value) => ["OL", "LT", "LG", "RG", "RT"].includes(value))
      .filter((value, index, values) => values.indexOf(value) === index);
  };
  const getPlayerBoardNumber = (player) => {
    const playerId = String(player?.FBXID || "").trim();
    return normalizeJerseyNumber(playerNoteCache[playerId]?.boardNumber) || getBoardJerseyNumber(
      getMergedBoardPlayerData(player),
      boardNumberDisplayType
    );
  };
  const getPlayerBoardStatus = (player) => {
    return getBoardLeagueStatus(
      getMergedBoardPlayerData(player),
      boardBaseTeamLeague
    );
  };
  const getPlayerBoardTeam = (player) => {
    return getBoardLeagueTeam(
      getMergedBoardPlayerData(player),
      boardBaseTeamLeague
    );
  };
  const isPlayerRosterAlert = (player) => {
    const status = getPlayerBoardStatus(player).toUpperCase();
    if (["FA", "UP"].includes(status)) return true;

    const baseTeam = cleanText(boardBaseTeamCode).toUpperCase();
    const playerTeam = getPlayerBoardTeam(player);
    return Boolean(baseTeam && playerTeam && playerTeam !== baseTeam);
  };
  const hasPlayerNote = (player) => {
    const playerId = String(player?.FBXID || "").trim();
    const noteData = playerNoteCache[playerId] || {};
    return Boolean(cleanText(noteData.noteText));
  };
  const hasPlayerBoardMetaOverride = (player) => {
    const playerId = String(player?.FBXID || "").trim();
    const noteData = playerNoteCache[playerId] || {};
    return Boolean(cleanText(noteData.boardPosition) || cleanText(noteData.boardNumber));
  };
  const getPlayerBoardMetaLine = (player) => {
    const position = getPlayerBoardPosition(player);
    const number = getPlayerBoardNumber(player);
    const boardStatus = getPlayerBoardStatus(player);
    const numberText = number ? `#${number}${boardStatus ? ` ${boardStatus}` : ""}` : boardStatus;
    return [position || cleanText(player?.genPos), numberText].filter(Boolean).join(" ");
  };
  const getBoardPlayerImpNip = (item) => {
    const playerId = String(item?.FBXID || "").trim();
    return cleanText(
      safePlayerImpNipById[playerId] ||
      boardPlayerImpNipById[playerId] ||
      item?.impnip ||
      item?.IMPNIP
    ).toUpperCase();
  };
  const getBoardPlayerImpNipClass = (item) => {
    const impnip = getBoardPlayerImpNip(item);
    if (impnip === "N") return "draft-board-card-impnip-n";
    if (impnip === "G") return "draft-board-card-impnip-g";
    if (impnip === "V3") return "draft-board-card-impnip-v3";
    if (impnip === "V4") return "draft-board-card-impnip-v4";
    if (impnip === "V5") return "draft-board-card-impnip-v5";
    return "";
  };
  const getBoardPlayerImpNipListClass = (item) => {
    const impnip = getBoardPlayerImpNip(item);
    if (impnip === "N") return "draft-board-player-row-impnip-n";
    if (impnip === "G") return "draft-board-player-row-impnip-g";
    if (impnip === "V3") return "draft-board-player-row-impnip-v3";
    if (impnip === "V4") return "draft-board-player-row-impnip-v4";
    if (impnip === "V5") return "draft-board-player-row-impnip-v5";
    return "";
  };
  const getBoardPlayerImpNipGroup = (item) => {
    const impnip = getBoardPlayerImpNip(item);
    if (impnip === "N") return "N";
    if (impnip === "G") return "G";
    if (impnip.startsWith("V")) return "V";
    return "U";
  };
  const countScopePlayers = countScope === "board" ? uniqueBoardPlayers : uniquePagePlayers;
  const boardImpNipCounts = countScopePlayers.reduce((counts, item) => {
    const group = getBoardPlayerImpNipGroup(item);
    counts[group] = (counts[group] || 0) + 1;
    return counts;
  }, { N: 0, G: 0, V: 0, U: 0 });
  const visibleBoardPlayers = sortPlayersAlpha(boardPlayers);

  useEffect(() => {
    if (!activePageId && boardPages[0]?.pageId) {
      setActivePageId(boardPages[0].pageId);
    }
  }, [activePageId, boardPages]);

  useEffect(() => {
    if (groupSelectOpen && selectedBoardItemCount > 1) return;
    setAlignmentMenuOpen(false);
  }, [groupSelectOpen, selectedBoardItemCount]);

  useEffect(() => {
    window.localStorage.setItem(DRAFT_BOARD_SIZE_MODE_KEY, boardSizeMode);
  }, [boardSizeMode]);

  useEffect(() => {
    if (!quickSearchOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!quickSearchRef.current || quickSearchRef.current.contains(event.target)) return;
      setQuickSearchOpen(false);
      setOpenQuickSearchCalendar(null);
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [quickSearchOpen]);

  const updateQuickSearchValue = (field, value) => {
    setQuickSearchValues((current) => ({
      ...current,
      ...(field === "GENUNIT" && value !== "-" ? { GENPOS: "-" } : {}),
      ...(field === "GENPOS" && value !== "-" ? { GENUNIT: "-" } : {}),
      ...(["CFLStatusDateStart", "NFLStatusDateStart", "UFL24StatusDateStart"].includes(field) && !cleanText(value)
        ? { [field.replace("Start", "End")]: "" }
        : {}),
      ...(["CFLStatusDateStart", "NFLStatusDateStart", "UFL24StatusDateStart"].includes(field) && cleanText(value) && !cleanText(current[field.replace("Start", "End")])
        ? { [field.replace("Start", "End")]: getTodayInputDate() }
        : {}),
      [field]: value,
    }));
    setQuickSearchMessage("");
  };
  const toggleQuickSearchCalendar = (fieldName) => {
    setOpenQuickSearchCalendar((current) => current === fieldName ? null : fieldName);
  };

  const clearQuickSearch = () => {
    setQuickSearchValues(DRAFT_BOARD_SEARCH_DEFAULTS);
    setOpenQuickSearchCalendar(null);
    setQuickSearchMessage("");
  };

  const buildQuickSearchPayload = () => {
    const payload = {};
    Object.entries(quickSearchValues).forEach(([field, value]) => {
      if (field === "GENUNIT") return;
      const textValue = String(value ?? "").trim();
      if (!textValue || textValue === "-") return;
      payload[field] = textValue;
    });
    return payload;
  };

  const buildQuickSearchPayloads = () => {
    const payload = buildQuickSearchPayload();
    const selectedUnit = POSITION_UNIT_OPTIONS.find((unit) => unit.code === quickSearchValues.GENUNIT);
    if (!selectedUnit) return [payload];
    return selectedUnit.positions.map((position) => ({
      ...payload,
      GENPOS: position,
    }));
  };

  const runDraftBoardQuickSearch = async () => {
    const payloads = buildQuickSearchPayloads();
    if (payloads.every((payload) => Object.keys(payload).length === 0)) {
      setQuickSearchMessage("Enter at least one search field");
      return;
    }

    setQuickSearchLoading(true);
    setQuickSearchMessage("Searching...");

    try {
      const searchResults = await Promise.all(payloads.map((payload) => runS1Search(payload)));
      const tooManyResult = searchResults
        .flatMap((results) => (Array.isArray(results) ? results : []))
        .find((item) => item?.playerTotal);
      const resultArray = searchResults
        .flatMap((results) => (Array.isArray(results) ? results : []))
        .filter((player) => player && !player.playerTotal)
        .reduce((players, player) => {
          const key = String(player?.FBXID || player?.DR_Num || player?.DRNUM || `${player?.last || ""}-${player?.first || ""}-${player?.school || ""}`).trim();
          if (!key || players.seen.has(key)) return players;
          players.seen.add(key);
          players.items.push(player);
          return players;
        }, { seen: new Set(), items: [] }).items;
      if (tooManyResult) {
        setPlayerData([]);
        setSavedOriginalPlayerData([]);
        setPlayerID("");
        setActivePlayer([]);
        setListLoadID(Math.trunc(Math.random() * 1_000_000) + 1);
        setLastFilter("");
        setFirstFilter("");
        setPositionFilter("");
        setPositionUnitFilter("");
        setPlayersOnlyOnBoard(false);
        closeWhiteBoardPopups("players");
        setPlayersOpen(true);
        updateSearchedListName("White Board Search: 0 players");
        setQuickSearchMessage(`Too many players found (${tooManyResult.playerTotal}). Narrow search.`);
        return;
      }

      const cappedResults = resultArray.slice(0, DRAFT_BOARD_SEARCH_LIMIT);
      const firstPlayer = cappedResults[0] || null;

      setPlayerData(cappedResults);
      setSavedOriginalPlayerData(cappedResults);
      setPlayerID(firstPlayer?.FBXID ? String(firstPlayer.FBXID) : "");
      setActivePlayer(firstPlayer || []);
      setListLoadID(Math.trunc(Math.random() * 1_000_000) + 1);
      setLastFilter("");
      setFirstFilter("");
      setPositionFilter("");
      setPositionUnitFilter("");
      setPlayersOnlyOnBoard(false);
      closeWhiteBoardPopups("players");
      setPlayersOpen(true);
      updateSearchedListName(`White Board Search: ${cappedResults.length} players`);

      if (resultArray.length > DRAFT_BOARD_SEARCH_LIMIT) {
        setQuickSearchMessage(`Showing first ${DRAFT_BOARD_SEARCH_LIMIT} of ${resultArray.length}`);
        return;
      }

      if (cappedResults.length === 0) {
        setQuickSearchMessage("No players found");
        return;
      }

      setQuickSearchMessage("");
      setQuickSearchOpen(false);
    } catch {
      setQuickSearchMessage("Unable to run search");
    } finally {
      setQuickSearchLoading(false);
    }
  };

  const openBoardPlayerDataBox = useCallback((event, boardPlayer) => {
    event.stopPropagation();

    const playerId = String(boardPlayer?.FBXID || "").trim();
    if (!playerId) return;

    const fullPlayer = Array.isArray(playerData)
      ? playerData.find((player) => String(player?.FBXID || "").trim() === playerId)
      : null;
    const nextPlayer = fullPlayer || boardPlayer;

    setPlayerID(playerId);
    setActivePlayer(nextPlayer);
    setHighlightedFBXID(playerId);
    setDataOpen(true);
  }, [playerData, setActivePlayer, setPlayerID]);

  const getPlayerGradeClass = (grade) => {
    return getWhiteBoardGradeKey(grade) ? "" : "draft-board-card-grade-neutral";
  };
  const getPlayerGradeStyle = (grade) => {
    const gradeKey = getWhiteBoardGradeKey(grade);
    const backgroundColor = gradeKey ? whiteBoardPreferences.gradeColors?.[gradeKey] : "";
    if (!backgroundColor) return undefined;

    return {
      backgroundColor,
      color: getReadableTextColor(backgroundColor),
    };
  };
  const getPositionMarkerStyle = (item) => {
    const markerColor = boardPositionMarkerColor || DEFAULT_WHITE_BOARD_POSITION_MARKER_COLOR;
    const isTrimmedMarker = isFlippedPage || (isSplitSortPage && Number(item?.y || 0) < BOARD_SPLIT_SORT_Y);

    return {
      "--draft-board-position-marker-color": markerColor,
      "--draft-board-position-marker-text-color": getReadableTextColor(markerColor),
      "--draft-board-position-marker-split-text-color": markerColor,
      "--draft-board-position-marker-split-border-color": markerColor,
      "--draft-board-position-marker-split-background": "#ffffff",
      ...(isTrimmedMarker ? {} : {
        "--draft-board-position-marker-border-color": markerColor,
        "--draft-board-position-marker-background": markerColor,
      }),
    };
  };
  const boardCounts = allBoardPlayers.reduce((counts, item) => {
    const id = String(item?.FBXID || "").trim();
    if (!id) return counts;
    counts[id] = (counts[id] || 0) + 1;
    return counts;
  }, {});
  const activePageCounts = boardPlayers.reduce((counts, item) => {
    const id = String(item?.FBXID || "").trim();
    if (!id) return counts;
    counts[id] = (counts[id] || 0) + 1;
    return counts;
  }, {});
  const filteredPlayers = safePlayers
    .filter((player) => {
      const posFilter = positionFilter.trim();
      const lastNameFilter = lastFilter.trim().toLowerCase();
      const firstNameFilter = firstFilter.trim().toLowerCase();
      const selectedUnit = POSITION_UNIT_OPTIONS.find((unit) => unit.code === positionUnitFilter);
      const playerPosition = getPlayerBoardFilterPosition(player);
      const playerId = String(player?.FBXID || "").trim();
      const matchesOnBoard = !playersOnlyOnBoard || (playerId && boardCounts[playerId] > 0);
      const matchesPosition = selectedUnit
        ? selectedUnit.positions.includes(playerPosition)
        : !posFilter || playerPosition === posFilter;
      const matchesLast = !lastNameFilter || cleanText(player?.last).toLowerCase().includes(lastNameFilter);
      const matchesFirst = !firstNameFilter || cleanText(player?.first).toLowerCase().includes(firstNameFilter);
      return matchesOnBoard && matchesPosition && matchesLast && matchesFirst;
    })
    .sort(comparePlayersAlpha);
  const showSearchedPlayersProPositionNote = normalizeBoardPositionDisplayType(boardPositionDisplayType) === "pro" &&
    filteredPlayers.length > 0 &&
    filteredPlayers.every((player) => !cleanText(getPlayerBoardPosition(player)));
  const onBoardPlayers = boardPlayers.reduce((players, item) => {
    const id = String(item?.FBXID || "").trim();
    const key = id || `item-${item?.boardItemId || players.length}`;
    const existingIndex = players.findIndex((player) => player.groupKey === key);

    if (existingIndex >= 0) {
      players[existingIndex] = {
        ...players[existingIndex],
        count: id ? (boardCounts[id] || players[existingIndex].count + 1) : players[existingIndex].count + 1,
      };
      return players;
    }

    players.push({
      ...item,
      groupKey: key,
      count: id ? (boardCounts[id] || 1) : 1,
    });
    return players;
  }, []);
  const filteredOnBoardPlayers = onBoardPlayers.filter((player) => {
    const posFilter = onBoardPositionFilter.trim();
    const lastNameFilter = onBoardLastFilter.trim().toLowerCase();
    const firstNameFilter = onBoardFirstFilter.trim().toLowerCase();
    const selectedUnit = POSITION_UNIT_OPTIONS.find((unit) => unit.code === onBoardPositionUnitFilter);
    const playerPosition = getPlayerBoardFilterPosition(player);
    const matchesPosition = selectedUnit
      ? selectedUnit.positions.includes(playerPosition)
      : !posFilter || playerPosition === posFilter;
    const matchesLast = !lastNameFilter || cleanText(player?.last).toLowerCase().includes(lastNameFilter);
    const matchesFirst = !firstNameFilter || cleanText(player?.first).toLowerCase().includes(firstNameFilter);
    return matchesPosition && matchesLast && matchesFirst;
  }).sort(comparePlayersAlpha);
  const selectedBoardPlayer = highlightedFBXID
    ? boardPlayers.find((item) => String(item?.FBXID || "").trim() === highlightedFBXID)
    : null;
  const selectedListPlayer = highlightedFBXID
    ? safePlayers.find((player) => String(player?.FBXID || "").trim() === highlightedFBXID)
    : null;
  const selectedActivePlayer = highlightedFBXID && String(activePlayer?.FBXID || "").trim() === highlightedFBXID
    ? activePlayer
    : null;
  const selectedFetchedPlayer = highlightedFBXID
    ? playerDetailCache[String(highlightedFBXID || "").trim()] || null
    : null;
  const selectedDataPlayer = highlightedFBXID
    ? {
      ...(selectedBoardPlayer || {}),
      ...(selectedListPlayer || {}),
      ...(selectedActivePlayer || {}),
      ...(selectedFetchedPlayer || {}),
    }
    : null;
  const selectedDataNote = highlightedFBXID ? playerNoteCache[String(highlightedFBXID || "").trim()] || {} : {};
  const selectedDataLastName = getPlayerField(selectedDataPlayer, "last", "LAST");
  const selectedDataFirstName = getPlayerField(selectedDataPlayer, "first", "FIRST");
  const selectedDataSchoolCode = getPlayerField(selectedDataPlayer, "school", "SCHOOL", "schoolCode", "SchoolCode", "SCHOOL_CODE");
  const selectedDataSchoolRecord = selectedDataSchoolCode
    ? (Array.isArray(schoolArray) ? schoolArray : []).find((school) =>
      cleanText(school?.schoolcode || school?.SCHOOL_code || school?.schoolCode).toUpperCase() === selectedDataSchoolCode.toUpperCase()
    )
    : null;
  const selectedDataSchoolLongName = getPlayerField(
    selectedDataPlayer,
    "longSchool",
    "LONGSCHOOL",
    "schoolLong",
    "SchoolLong",
    "school_short_name"
  ) || cleanText(selectedDataSchoolRecord?.school_short_name);
  const selectedDataSchoolLine = [selectedDataSchoolCode, selectedDataSchoolLongName].filter(Boolean).join(" - ");
  const selectedDataBoardPosition = getPlayerBoardPosition(selectedDataPlayer);
  const selectedDataCollegeJersey = getCollegeJerseyNumber(selectedDataPlayer);
  const selectedDataJerseyNumber = getPlayerBoardNumber(selectedDataPlayer) || selectedDataCollegeJersey;
  const selectedDataBoardPositionLine = [
    selectedDataBoardPosition,
    selectedDataJerseyNumber ? `#${selectedDataJerseyNumber}` : "",
  ].filter(Boolean).join(" ");
  const selectedDataHeaderTitle = selectedDataPlayer
    ? selectedDataBoardPositionLine || "-"
    : "No Player Selected";
  const selectedDataPlayerId = String(selectedDataPlayer?.FBXID || "").trim();
  const editingSelectedDataNote = Boolean(
    selectedDataPlayerId &&
    playerNoteMode === "edit" &&
    String(notePlayer?.FBXID || "").trim() === selectedDataPlayerId
  );
  const selectedDataTeamGrade = cleanText(selectedDataNote?.grade);
  const selectedDataTeamPosition = cleanText(selectedDataNote?.boardPosition);
  const selectedDataTeamNumber = normalizeJerseyNumber(selectedDataNote?.boardNumber);
  const selectedPlayerQuickNoteVersions = Array.isArray(selectedDataNote?.quickNoteVersions) ? selectedDataNote.quickNoteVersions : [];
  const selectedPlayerCurrentQuickNote = selectedPlayerQuickNoteVersions[0] || null;
  const playerQuickNoteVersion = playerQuickNoteVersions[playerQuickNoteVersionIndex] || null;
  const playerQuickNoteVersionCount = playerQuickNoteVersions.length;
  const playerQuickNoteIsLatestVersion = playerQuickNoteVersionIndex === 0;
  const latestPlayerQuickNote = playerQuickNoteVersions[0] || null;
  const canSaveWhiteBoardQuickNote = Boolean(
    cleanText(playerNoteText) &&
    cleanText(playerNoteText) !== cleanText(latestPlayerQuickNote?.noteText)
  );
  const selectedDataHasTeamNote = Boolean(
    selectedDataTeamGrade ||
    selectedDataTeamPosition ||
    selectedDataTeamNumber
  );

  const pageHasHighlightedPlayer = (page) => {
    if (!highlightedFBXID) return false;
    const pagePlayers = page?.pageId === activePageId ? boardPlayers : page?.players;
    return (Array.isArray(pagePlayers) ? pagePlayers : []).some((player) => String(player?.FBXID || "").trim() === highlightedFBXID);
  };
  const boardPrintMetrics = { width: BOARD_PRINT_PAGE_WIDTH, height: BOARD_PRINT_PAGE_HEIGHT, scale: 1 };
  const getPrintItemSize = (item, type = "player") => {
    const overlayConfig = type === "overlay" ? getBoardOverlayConfig(item?.type) : null;
    return {
      width: type === "player"
        ? boardCardWidth
        : type === "label"
          ? BOARD_LABEL_PRINT_WIDTH
          : type === "detail"
            ? BOARD_DETAIL_PRINT_WIDTH
            : getOverlayWidth(overlayConfig),
      height: type === "player"
        ? boardCardHeight
        : type === "label"
          ? BOARD_LABEL_PRINT_HEIGHT
          : type === "detail"
            ? BOARD_DETAIL_PRINT_HEIGHT
            : getOverlayHeight(overlayConfig),
    };
  };
  const isPointInsidePrintPage = (point, size) => (
    point &&
    point.x >= 0 &&
    point.y >= 0 &&
    point.x + size.width <= BOARD_PRINT_PAGE_WIDTH &&
    point.y + size.height <= BOARD_PRINT_PAGE_HEIGHT
  );
  const getPrintItemPoint = (item, type = "player") => {
    const x = Number(item?.x || 0);
    const y = Number(item?.y || 0);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

    if (type !== "detail") return { x, y };

    const size = getPrintItemSize(item, type);
    const rect = whiteboardRef.current?.getBoundingClientRect();
    const guidePoint = {
      x: x - getPrintOriginOffsetX(rect),
      y: y - getPrintOriginOffsetY(rect),
    };
    if (isPointInsidePrintPage(guidePoint, size)) return guidePoint;

    const pagePoint = { x, y };
    return isPrintSizeMode && isPointInsidePrintPage(pagePoint, size) ? pagePoint : guidePoint;
  };
  const isPrintItemInsidePage = (item, type = "player") => {
    const point = getPrintItemPoint(item, type);
    if (!point) return false;

    return isPointInsidePrintPage(point, getPrintItemSize(item, type));
  };
  const myBoards = savedBoards
    .filter((board) => board?.isOwner !== false)
    .sort(compareBoardsAlpha);
  const sharedBoards = savedBoards
    .filter((board) => board?.isOwner === false)
    .sort(compareBoardsAlpha);

  const isBoardItemSelected = useCallback((type, id) => (
    Boolean(selectedBoardItems[getBoardSelectionKey(type, id)])
  ), [selectedBoardItems]);

  const toggleBoardItemSelection = useCallback((event, type, id) => {
    const isShiftSelectingPlayer = Boolean(event?.shiftKey && type === "player");
    if ((!groupSelectOpen && !isShiftSelectingPlayer) || !canEditCurrentBoard) return false;
    event.stopPropagation();
    if (suppressGroupDragClickRef.current) {
      suppressGroupDragClickRef.current = false;
      return true;
    }
    if (isShiftSelectingPlayer) setGroupSelectOpen(true);
    const selectionKey = getBoardSelectionKey(type, id);
    setSelectedBoardItems((current) => {
      const next = { ...current };
      if (next[selectionKey]) {
        delete next[selectionKey];
      } else {
        next[selectionKey] = true;
      }
      return next;
    });
    return true;
  }, [canEditCurrentBoard, groupSelectOpen]);

  const clearBoardSelection = useCallback(() => {
    setSelectedBoardItems({});
    setAlignmentMenuOpen(false);
  }, []);

  const clearGroupDragPreview = useCallback(() => {
    groupDragRef.current = null;
    setGroupDragPreviewDelta(null);
  }, []);

  const selectAllBoardItems = useCallback(() => {
    if (!canEditCurrentBoard) return;
    const nextSelection = {};
    boardPlayers.forEach((item) => {
      if (item?.boardItemId) nextSelection[getBoardSelectionKey("player", item.boardItemId)] = true;
    });
    boardLabels.forEach((item) => {
      if (item?.labelItemId) nextSelection[getBoardSelectionKey("label", item.labelItemId)] = true;
    });
    boardDetails.forEach((item) => {
      if (item?.detailItemId) nextSelection[getBoardSelectionKey("detail", item.detailItemId)] = true;
    });
    boardOverlays.forEach((item) => {
      if (item?.overlayItemId) nextSelection[getBoardSelectionKey("overlay", item.overlayItemId)] = true;
    });
    setGroupSelectOpen(true);
    setSelectedBoardItems(nextSelection);
  }, [boardDetails, boardLabels, boardOverlays, boardPlayers, canEditCurrentBoard]);

  const getPrintOriginOffsetX = useCallback((rect) => (!isPrintSizeMode && rect
    ? isCenteredGridSizeMode
      ? Math.max(0, (rect.width - BOARD_PRINT_PAGE_WIDTH) / 2)
      : Math.max(0, rect.width - BOARD_PRINT_PAGE_WIDTH - BOARD_PRINT_RIGHT_INSET)
    : 0), [isCenteredGridSizeMode, isPrintSizeMode]);

  const getPrintOriginOffsetY = useCallback(() => 0, []);

  const getBoardItemSize = useCallback((type, item = null) => {
    if (type === "player") return { width: boardCardWidth, height: boardCardHeight };
    if (type === "label") return { width: BOARD_LABEL_PRINT_WIDTH, height: BOARD_LABEL_PRINT_HEIGHT };
    if (type === "detail") return { width: BOARD_DETAIL_PRINT_WIDTH, height: BOARD_DETAIL_PRINT_HEIGHT };

    const overlayConfig = getBoardOverlayConfig(item?.type || item || "oline");
    return {
      width: getOverlayWidth(overlayConfig),
      height: getOverlayHeight(overlayConfig),
    };
  }, [boardCardHeight, boardCardWidth, getOverlayHeight, getOverlayWidth]);

  const clampBoardPointToPrintableArea = useCallback((point, type = "player", item = null) => {
    const { width, height } = getBoardItemSize(type, item);
    if (type === "player") {
      const rect = whiteboardRef.current?.getBoundingClientRect();
      const boardWidth = rect?.width || BOARD_PRINT_PAGE_WIDTH;
      const boardHeight = rect?.height || BOARD_PRINT_PAGE_HEIGHT;
      const printOriginOffsetX = getPrintOriginOffsetX(rect);
      const printOriginOffsetY = getPrintOriginOffsetY(rect);
      const minX = -printOriginOffsetX;
      const minY = -printOriginOffsetY;
      const maxX = Math.max(minX, boardWidth - printOriginOffsetX - width);
      const maxY = Math.max(minY, boardHeight - printOriginOffsetY - height);

      return {
        x: Math.min(Math.max(Number(point?.x || 0), minX), maxX),
        y: Math.min(Math.max(Number(point?.y || 0), minY), maxY),
      };
    }

    if (type === "detail" || type === "label") {
      const rect = whiteboardRef.current?.getBoundingClientRect();
      const boardWidth = rect?.width || BOARD_PRINT_PAGE_WIDTH;
      const boardHeight = rect?.height || BOARD_PRINT_PAGE_HEIGHT;
      const printOriginOffsetX = type === "label" ? getPrintOriginOffsetX(rect) : 0;
      const printOriginOffsetY = type === "label" ? getPrintOriginOffsetY(rect) : 0;
      const minX = -printOriginOffsetX;
      const minY = -printOriginOffsetY;
      const maxX = Math.max(minX, boardWidth - printOriginOffsetX - width);
      const maxY = Math.max(minY, boardHeight - printOriginOffsetY - height);

      return {
        x: Math.min(Math.max(Number(point?.x || 0), minX), maxX),
        y: Math.min(Math.max(Number(point?.y || 0), minY), maxY),
      };
    }

    const minX = BOARD_PRINT_SAFE_INSET;
    const minY = BOARD_PRINT_SAFE_INSET;
    const maxX = Math.max(minX, BOARD_PRINT_PAGE_WIDTH - BOARD_PRINT_SAFE_INSET - width);
    const maxY = Math.max(minY, BOARD_PRINT_PAGE_HEIGHT - BOARD_PRINT_SAFE_INSET - height);

    return {
      x: Math.min(Math.max(Number(point?.x || 0), minX), maxX),
      y: Math.min(Math.max(Number(point?.y || 0), minY), maxY),
    };
  }, [getBoardItemSize, getPrintOriginOffsetX, getPrintOriginOffsetY]);

  const isPlayerPointInPrintArea = useCallback((point, options = {}) => {
    const x = Number(point?.x || 0);
    const y = Number(point?.y || 0);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return false;

    if (options.requireFullCard) {
      return x >= 0 &&
        y >= 0 &&
        x + boardCardWidth <= BOARD_PRINT_PAGE_WIDTH &&
        y + boardCardHeight <= BOARD_PRINT_PAGE_HEIGHT;
    }

    const centerX = x + (boardCardWidth / 2);
    const centerY = y + (boardCardHeight / 2);
    return centerX >= 0 &&
      centerX <= BOARD_PRINT_PAGE_WIDTH &&
      centerY >= 0 &&
      centerY <= BOARD_PRINT_PAGE_HEIGHT;
  }, [boardCardHeight, boardCardWidth]);

  const getStackPointInPrintArea = useCallback((point) => {
    const clampedPoint = clampBoardPointToPrintableArea(point, "player");
    return isPlayerPointInPrintArea(clampedPoint, { requireFullCard: true })
      ? clampedPoint
      : null;
  }, [clampBoardPointToPrintableArea, isPlayerPointInPrintArea]);

  const isStackTopInBounds = (top, minTop, maxTop) => {
    const numericTop = Number(top);
    return Number.isFinite(numericTop) &&
      numericTop >= minTop &&
      numericTop <= maxTop;
  };

  const getBoardItemMinimums = useCallback((type) => {
    if (type === "detail") {
      return { x: 0, y: 0 };
    }

    const rect = whiteboardRef.current?.getBoundingClientRect();
    const printOriginOffsetX = getPrintOriginOffsetX(rect);
    const printOriginOffsetY = getPrintOriginOffsetY(rect);
    if (type === "player") {
      return {
        x: -printOriginOffsetX,
        y: -printOriginOffsetY,
      };
    }

    if (type === "label") {
      return {
        x: -printOriginOffsetX,
        y: -printOriginOffsetY,
      };
    }

    return {
      x: isPrintSizeMode ? BOARD_PRINT_SAFE_INSET : BOARD_PRINT_SAFE_INSET - printOriginOffsetX,
      y: isPrintSizeMode ? BOARD_PRINT_SAFE_INSET : BOARD_PRINT_SAFE_INSET - printOriginOffsetY,
    };
  }, [getPrintOriginOffsetX, getPrintOriginOffsetY, isPrintSizeMode]);

  const getBoardItemPositionStyle = (item, type, idKey) => {
    const left = Number(item?.x || 0);
    const top = Number(item?.y || 0);
    const usesPrintOffset = type !== "detail" && !isPrintSizeMode;
    const printOffsetX = usesPrintOffset ? "var(--draft-board-print-offset-x, 0px)" : "0px";
    const printOffsetY = usesPrintOffset ? "var(--draft-board-print-offset-y, 0px)" : "0px";
    if (!groupDragPreviewDelta || !isBoardItemSelected(type, item?.[idKey])) {
      return { left: `calc(${printOffsetX} + ${left}px)`, top: `calc(${printOffsetY} + ${top}px)` };
    }

    const point = clampBoardPointToPrintableArea({
      x: left + groupDragPreviewDelta.x,
      y: top + groupDragPreviewDelta.y,
    }, type, item);
    return {
      left: `calc(${printOffsetX} + ${point.x}px)`,
      top: `calc(${printOffsetY} + ${point.y}px)`,
    };
  };

  const mergeCurrentPage = useCallback((nextPlayers = boardPlayers, nextDraftPlayers = draftPlayers, nextLabels = boardLabels, nextDetails = boardDetails, nextOverlays = boardOverlays, options = {}) => {
    void nextDraftPlayers;
    const resolvedOverlays = Array.isArray(nextOverlays) ? nextOverlays : boardOverlays;
    const resolvedOptions = Array.isArray(nextOverlays) ? options : nextOverlays || {};
    const pageId = resolvedOptions.pageId || activePageId || boardPages[0]?.pageId || buildBoardPage().pageId;
    const sourcePages = Array.isArray(resolvedOptions.pages) && resolvedOptions.pages.length > 0 ? resolvedOptions.pages : boardPages;
    const pages = sourcePages.length > 0 ? sourcePages : [buildBoardPage()];
    const resolvedPresetDraft = Object.prototype.hasOwnProperty.call(resolvedOptions, "presetDraft")
      ? normalizePagePresetDraft(resolvedOptions.presetDraft)
      : normalizePagePresetDraft(activePresetDraft);
    let foundPage = false;

    const nextPages = pages.map((page) => {
      if (page.pageId !== pageId) return page;
      foundPage = true;
      return {
        ...stripPageDraftPlayers(page),
        players: nextPlayers,
        labels: nextLabels,
        details: nextDetails,
        overlays: resolvedOverlays,
        presetDraft: resolvedPresetDraft,
      };
    });

    if (!foundPage) {
      nextPages.push(buildBoardPage(DEFAULT_BOARD_PAGE_NAME, {
        pageId,
        players: nextPlayers,
        labels: nextLabels,
        details: nextDetails,
        overlays: resolvedOverlays,
        presetDraft: resolvedPresetDraft,
      }));
    }

    return nextPages.map(stripPageDraftPlayers);
  }, [activePageId, activePresetDraft, boardDetails, boardLabels, boardOverlays, boardPages, boardPlayers, draftPlayers]);

  const captureUndoSnapshot = useCallback(() => {
    if (!canEditCurrentBoard) return;

    const currentPages = mergeCurrentPage(boardPlayers, draftPlayers, boardLabels, boardDetails, boardOverlays);
    setUndoSnapshot({
      players: cloneBoardValue(boardPlayers),
      draftPlayers: cloneBoardValue(draftPlayers),
      labels: cloneBoardValue(boardLabels),
      details: cloneBoardValue(boardDetails),
      overlays: cloneBoardValue(boardOverlays),
      pages: cloneBoardValue(currentPages),
      activePageId,
    });
  }, [activePageId, boardDetails, boardLabels, boardOverlays, boardPlayers, canEditCurrentBoard, draftPlayers, mergeCurrentPage]);

  const saveBoardLocal = useCallback((nextPlayers, nextMeta = {}, nextDraftPlayers = draftPlayers, nextLabels = boardLabels, nextDetails = boardDetails, nextOverlays = boardOverlays, nextPageOptions = {}) => {
    const resolvedOverlays = Array.isArray(nextOverlays) ? nextOverlays : boardOverlays;
    const resolvedPageOptions = Array.isArray(nextOverlays) ? nextPageOptions : nextOverlays || {};
    const nextBoardId = nextMeta.boardId !== undefined ? nextMeta.boardId : savedBoardId;
    const nextBoardName = nextMeta.boardName || boardName;
    const nextIsOwner = nextMeta.isOwner !== undefined ? nextMeta.isOwner : currentBoardIsOwner;
    const nextCanEdit = nextMeta.canEdit !== undefined ? nextMeta.canEdit : currentBoardCanEdit;
    const nextShareCount = nextMeta.shareCount !== undefined ? nextMeta.shareCount : currentBoardShareCount;
    const nextShareCanEdit = nextMeta.shareCanEdit !== undefined ? nextMeta.shareCanEdit : shareCanEdit;
    const nextCreatedAt = nextMeta.createdAt !== undefined ? nextMeta.createdAt : currentBoardCreatedAt;
    const nextCreatedBy = nextMeta.createdBy !== undefined ? nextMeta.createdBy : currentBoardCreatedBy;
    const nextUpdatedAt = nextMeta.updatedAt !== undefined ? nextMeta.updatedAt : currentBoardUpdatedAt;
    const nextUpdatedBy = nextMeta.updatedBy !== undefined ? nextMeta.updatedBy : currentBoardUpdatedBy;
    const nextBoardVersion = nextMeta.boardVersion !== undefined ? nextMeta.boardVersion : currentBoardVersion;
    const nextPositionMarkerColor = normalizeWhiteBoardPositionMarkerColor(
      nextMeta.positionMarkerColor !== undefined ? nextMeta.positionMarkerColor : boardPositionMarkerColor
    );
    const nextBaseTeamLeague = nextMeta.baseTeamLeague !== undefined ? getBoardBaseTeamLeague(nextMeta, user?.team || boardBaseTeamCode) : boardBaseTeamLeague;
    const nextBaseTeamCode = nextMeta.baseTeamCode !== undefined ? getBoardBaseTeamCode(nextMeta, user?.team || boardBaseTeamCode) : boardBaseTeamCode;
    const nextNumberDisplayType = nextMeta.numberDisplayType !== undefined ? getBoardNumberDisplayType(nextMeta) : boardNumberDisplayType;
    const nextPositionDisplayType = nextMeta.positionDisplayType !== undefined ? getBoardPositionDisplayType(nextMeta) : boardPositionDisplayType;
    const nextPages = Array.isArray(resolvedPageOptions.pages)
      ? resolvedPageOptions.pages
      : mergeCurrentPage(nextPlayers, nextDraftPlayers, nextLabels, nextDetails, resolvedOverlays, resolvedPageOptions);
    const nextActivePageId = resolvedPageOptions.activePageId || activePageId || nextPages[0]?.pageId || "";

    try {
      localStorage.setItem(DRAFT_BOARD_LOCAL_KEY, JSON.stringify({
        boardId: nextBoardId,
        boardName: nextBoardName,
        isOwner: nextIsOwner,
        canEdit: nextCanEdit,
        shareCanEdit: nextShareCanEdit,
        shareCount: nextShareCount,
        createdAt: nextCreatedAt,
        createdBy: nextCreatedBy,
        updatedAt: nextUpdatedAt,
        updatedBy: nextUpdatedBy,
        serverUpdatedAt: nextUpdatedAt,
        boardVersion: nextBoardVersion,
        localSavedAt: new Date().toISOString(),
        players: nextPlayers,
        draftPlayers: nextDraftPlayers,
        labels: nextLabels,
        details: nextDetails,
        overlays: resolvedOverlays,
        pages: nextPages,
        activePageId: nextActivePageId,
        positionMarkerColor: nextPositionMarkerColor,
        baseTeamLeague: nextBaseTeamLeague,
        baseTeamCode: nextBaseTeamCode,
        numberDisplayType: nextNumberDisplayType,
        positionDisplayType: nextPositionDisplayType,
      }));
      showSavedMessage();
    } catch {
      setSaveMessage("Unable to save locally");
    }
  }, [activePageId, boardBaseTeamCode, boardBaseTeamLeague, boardDetails, boardLabels, boardName, boardNumberDisplayType, boardOverlays, boardPositionDisplayType, boardPositionMarkerColor, currentBoardCanEdit, currentBoardCreatedAt, currentBoardCreatedBy, currentBoardIsOwner, currentBoardShareCount, currentBoardUpdatedAt, currentBoardUpdatedBy, currentBoardVersion, draftPlayers, mergeCurrentPage, savedBoardId, shareCanEdit, showSavedMessage, user]);

  const saveBoardToDatabase = useCallback(async (nextPlayers = boardPlayers, options = {}) => {
    if (savedBoardId && !currentBoardCanEdit && options.forceNew !== true) {
      setSaveMessage("Board is view only");
      return { success: false, message: "Board is view only" };
    }

    const nextBoardName = options.boardName || (savedBoardId ? boardName : "Current Board");
    const nextDraftPlayers = Array.isArray(options.draftPlayers) ? options.draftPlayers : draftPlayers;
    const nextLabels = Array.isArray(options.labels) ? options.labels : boardLabels;
    const nextDetails = Array.isArray(options.details) ? options.details : boardDetails;
    const nextOverlays = Array.isArray(options.overlays) ? options.overlays : boardOverlays;
    const nextPages = Array.isArray(options.pages)
      ? options.pages
      : mergeCurrentPage(nextPlayers, nextDraftPlayers, nextLabels, nextDetails, nextOverlays, options);
    const nextActivePageId = options.activePageId || activePageId || nextPages[0]?.pageId || "";
    const nextPositionMarkerColor = normalizeWhiteBoardPositionMarkerColor(
      options.positionMarkerColor !== undefined ? options.positionMarkerColor : boardPositionMarkerColor
    );
    const nextBaseTeamLeague = options.baseTeamLeague !== undefined ? getBoardBaseTeamLeague(options, user?.team || boardBaseTeamCode) : boardBaseTeamLeague;
    const nextBaseTeamCode = options.baseTeamCode !== undefined ? getBoardBaseTeamCode(options, user?.team || boardBaseTeamCode) : boardBaseTeamCode;
    const nextNumberDisplayType = options.numberDisplayType !== undefined ? getBoardNumberDisplayType(options) : boardNumberDisplayType;
    const nextPositionDisplayType = options.positionDisplayType !== undefined ? getBoardPositionDisplayType(options) : boardPositionDisplayType;
    const payload = {
      scriptName: "saveBoard",
      boardId: options.boardId !== undefined ? options.boardId : savedBoardId,
      forceNew: options.forceNew === true,
      boardName: nextBoardName,
      team: user?.team || "",
      scoutId: user?.scout || "",
      scoutName: currentScoutName,
      visibleTo: user?.team || "",
      expectedUpdatedAt: options.forceNew === true ? "" : currentBoardUpdatedAt,
      expectedBoardVersion: options.forceNew === true ? 0 : currentBoardVersion,
      boardData: {
        players: nextPlayers,
        draftPlayers: nextDraftPlayers,
        labels: nextLabels,
        details: nextDetails,
        overlays: nextOverlays,
        pages: nextPages,
        activePageId: nextActivePageId,
        positionMarkerColor: nextPositionMarkerColor,
        baseTeamLeague: nextBaseTeamLeague,
        baseTeamCode: nextBaseTeamCode,
        numberDisplayType: nextNumberDisplayType,
        positionDisplayType: nextPositionDisplayType,
      },
    };

    try {
      localStorage.setItem(DRAFT_BOARD_LOCAL_KEY, JSON.stringify({
        boardId: payload.boardId || null,
        boardName: nextBoardName,
        isOwner: options.forceNew === true ? true : currentBoardIsOwner,
        canEdit: options.forceNew === true ? true : currentBoardCanEdit,
        shareCount: options.forceNew === true ? 0 : currentBoardShareCount,
        createdAt: options.forceNew === true ? "" : currentBoardCreatedAt,
        createdBy: options.forceNew === true ? currentScoutName : currentBoardCreatedBy,
        updatedAt: currentBoardUpdatedAt,
        updatedBy: options.forceNew === true ? currentScoutName : currentBoardUpdatedBy,
        serverUpdatedAt: currentBoardUpdatedAt,
        boardVersion: currentBoardVersion,
        localSavedAt: new Date().toISOString(),
        players: nextPlayers,
        draftPlayers: nextDraftPlayers,
        labels: nextLabels,
        details: nextDetails,
        overlays: nextOverlays,
        pages: nextPages,
        activePageId: nextActivePageId,
        positionMarkerColor: nextPositionMarkerColor,
        baseTeamLeague: nextBaseTeamLeague,
        baseTeamCode: nextBaseTeamCode,
        numberDisplayType: nextNumberDisplayType,
        positionDisplayType: nextPositionDisplayType,
      }));
    } catch {}

    if (!payload.team || !payload.scoutId) {
      showSavedMessage();
      return null;
    }

    try {
      const response = await fetch(DRAFT_BOARD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data?.success) {
        const nextBoardId = data?.boardId || payload.boardId || null;
        const savedName = data?.boardName || nextBoardName;
        const nextCreatedAt = getBoardCreatedAt(data) || currentBoardCreatedAt;
        const nextCreatedBy = getBoardCreatedBy(data) || currentBoardCreatedBy || currentScoutName;
        const nextUpdatedAt = getBoardUpdatedAt(data) || currentBoardUpdatedAt;
        const nextUpdatedBy = getBoardUpdatedBy(data) || currentScoutName;
        const nextBoardVersion = getBoardVersion(data) || currentBoardVersion;
        setSavedBoardId(nextBoardId);
        setBoardName(savedName);
        setCurrentBoardCreatedAt(nextCreatedAt);
        setCurrentBoardCreatedBy(nextCreatedBy);
        setCurrentBoardUpdatedAt(nextUpdatedAt);
        setCurrentBoardUpdatedBy(nextUpdatedBy);
        setCurrentBoardVersion(nextBoardVersion);
        if (options.forceNew === true) {
          setCurrentBoardIsOwner(true);
          setCurrentBoardCanEdit(true);
          setCurrentBoardShareCount(0);
          setShareScoutIds([]);
          setShareCanEdit(false);
        }
        const savedActivePage = nextPages.find((page) => page.pageId === nextActivePageId) || nextPages[0] || buildBoardPage();
        setBoardPages(nextPages);
        setActivePageId(savedActivePage.pageId);
        setBoardPlayers(savedActivePage.players);
        setBoardLabels(savedActivePage.labels);
        setBoardDetails(savedActivePage.details);
        setBoardOverlays(savedActivePage.overlays);
        setActivePresetDraft(savedActivePage.presetDraft || null);
        setDraftPlayers(nextDraftPlayers);
        setBoardPositionMarkerColor(nextPositionMarkerColor);
        setBoardBaseTeamLeague(nextBaseTeamLeague);
        setBoardBaseTeamCode(nextBaseTeamCode);
        setBoardNumberDisplayType(nextNumberDisplayType);
        setBoardPositionDisplayType(nextPositionDisplayType);
        saveBoardLocal(nextPlayers, {
          boardId: nextBoardId,
          boardName: savedName,
          isOwner: options.forceNew === true ? true : currentBoardIsOwner,
          canEdit: options.forceNew === true ? true : currentBoardCanEdit,
          shareCount: options.forceNew === true ? 0 : currentBoardShareCount,
          createdAt: nextCreatedAt,
          createdBy: nextCreatedBy,
          updatedAt: nextUpdatedAt,
          updatedBy: nextUpdatedBy,
          boardVersion: nextBoardVersion,
          positionMarkerColor: nextPositionMarkerColor,
          baseTeamLeague: nextBaseTeamLeague,
          baseTeamCode: nextBaseTeamCode,
          numberDisplayType: nextNumberDisplayType,
          positionDisplayType: nextPositionDisplayType,
        }, nextDraftPlayers, savedActivePage.labels, savedActivePage.details, savedActivePage.overlays, { pages: nextPages, activePageId: savedActivePage.pageId });
        showSavedMessage();
        return data;
      } else if (data?.conflict && data?.currentBoard) {
        const currentBoard = data.currentBoard;
        const loadedPages = normalizeBoardPages(currentBoard?.boardData || {});
        const nextActivePageId = currentBoard?.boardData?.activePageId || loadedPages[0]?.pageId || "";
        const loadedPage = loadedPages.find((page) => page.pageId === nextActivePageId) || loadedPages[0] || buildBoardPage();
        const loadedDraftPlayers = getBoardLevelDraftPlayers(currentBoard?.boardData || {}, loadedPages);
        const loadedPositionMarkerColor = getBoardPositionMarkerColor(currentBoard?.boardData || {});
        const loadedBaseTeamLeague = getBoardBaseTeamLeague(currentBoard?.boardData || {}, user?.team || "");
        const loadedBaseTeamCode = getBoardBaseTeamCode(currentBoard?.boardData || {}, user?.team || "");
        const loadedNumberDisplayType = getBoardNumberDisplayType(currentBoard?.boardData || {});
        const loadedPositionDisplayType = getBoardPositionDisplayType(currentBoard?.boardData || {});
        const nextCreatedAt = getBoardCreatedAt(currentBoard);
        const nextCreatedBy = getBoardCreatedBy(currentBoard);
        const nextUpdatedAt = getBoardUpdatedAt(currentBoard);
        const nextUpdatedBy = getBoardUpdatedBy(currentBoard);
        const nextBoardVersion = getBoardVersion(currentBoard);

        setSavedBoardId(currentBoard.boardId || null);
        setBoardName(currentBoard.boardName || "Saved Board");
        setCurrentBoardCreatedAt(nextCreatedAt);
        setCurrentBoardCreatedBy(nextCreatedBy);
        setCurrentBoardUpdatedAt(nextUpdatedAt);
        setCurrentBoardUpdatedBy(nextUpdatedBy);
        setCurrentBoardVersion(nextBoardVersion);
        setCurrentBoardIsOwner(currentBoard?.isOwner !== false);
        setCurrentBoardCanEdit(currentBoard?.canEdit !== false);
        setBoardPages(loadedPages);
        setActivePageId(loadedPage.pageId);
        setBoardPlayers(loadedPage.players);
        setBoardLabels(loadedPage.labels);
        setBoardDetails(loadedPage.details);
        setBoardOverlays(loadedPage.overlays);
        setActivePresetDraft(loadedPage.presetDraft || null);
        setDraftPlayers(loadedDraftPlayers);
        setBoardPositionMarkerColor(loadedPositionMarkerColor);
        setBoardBaseTeamLeague(loadedBaseTeamLeague);
        setBoardBaseTeamCode(loadedBaseTeamCode);
        setBoardNumberDisplayType(loadedNumberDisplayType);
        setBoardPositionDisplayType(loadedPositionDisplayType);
        saveBoardLocal(loadedPage.players, {
          boardId: currentBoard.boardId || null,
          boardName: currentBoard.boardName || "Saved Board",
          isOwner: currentBoard?.isOwner !== false,
          canEdit: currentBoard?.canEdit !== false,
          createdAt: nextCreatedAt,
          createdBy: nextCreatedBy,
          updatedAt: nextUpdatedAt,
          updatedBy: nextUpdatedBy,
          boardVersion: nextBoardVersion,
          positionMarkerColor: loadedPositionMarkerColor,
          baseTeamLeague: loadedBaseTeamLeague,
          baseTeamCode: loadedBaseTeamCode,
          numberDisplayType: loadedNumberDisplayType,
          positionDisplayType: loadedPositionDisplayType,
        }, loadedDraftPlayers, loadedPage.labels, loadedPage.details, loadedPage.overlays, { pages: loadedPages, activePageId: loadedPage.pageId });
        setSaveMessage(data.message || "Not saved. Board updated since you last saw it; refreshed so you can try again.");
        return data;
      } else {
        showSavedMessage();
        return data;
      }
    } catch {
      showSavedMessage();
      return null;
    }
  }, [activePageId, boardBaseTeamCode, boardBaseTeamLeague, boardDetails, boardLabels, boardName, boardNumberDisplayType, boardOverlays, boardPlayers, boardPositionDisplayType, boardPositionMarkerColor, currentBoardCanEdit, currentBoardCreatedAt, currentBoardCreatedBy, currentBoardIsOwner, currentBoardShareCount, currentBoardUpdatedAt, currentBoardUpdatedBy, currentBoardVersion, currentScoutName, draftPlayers, mergeCurrentPage, saveBoardLocal, savedBoardId, showSavedMessage, user]);

  const persistActivePagePresetDraft = useCallback((presetDraft, nextLabels = boardLabels) => {
    const normalizedPresetDraft = normalizePagePresetDraft(presetDraft);
    const nextPages = mergeCurrentPage(boardPlayers, draftPlayers, nextLabels, boardDetails, boardOverlays, {
      presetDraft: normalizedPresetDraft,
    });

    setBoardPages(nextPages);
    saveBoardLocal(boardPlayers, {}, draftPlayers, nextLabels, boardDetails, boardOverlays, {
      pages: nextPages,
      activePageId,
    });

    if (savedBoardId && currentBoardCanEdit) {
      saveBoardToDatabase(boardPlayers, {
        draftPlayers,
        labels: nextLabels,
        details: boardDetails,
        overlays: boardOverlays,
        pages: nextPages,
        activePageId,
      });
    }
  }, [activePageId, boardDetails, boardLabels, boardOverlays, boardPlayers, currentBoardCanEdit, draftPlayers, mergeCurrentPage, saveBoardLocal, saveBoardToDatabase, savedBoardId]);

  const fetchSavedBoards = useCallback(async () => {
    if (!user?.team || !user?.scout) {
      setSavedBoards([]);
      setBoardsError("");
      return;
    }

    setBoardsLoading(true);
    setBoardsError("");

    try {
      const response = await fetch(DRAFT_BOARD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "getBoards",
          team: user.team,
          scoutId: user.scout,
          security: user.userContactLevel || user.security || "",
        }),
      });
      const data = await response.json();
      if (data?.success) {
        const databaseBoards = Array.isArray(data.boards) ? data.boards : [];
        setSavedBoards(databaseBoards);
        const currentDatabaseBoard = databaseBoards.find((board) => String(board?.boardId || "") === String(savedBoardId || ""));
        if (currentDatabaseBoard) {
          setShareCanEdit(isTruthyFlag(currentDatabaseBoard?.shareCanEdit));
          setCurrentBoardShareCount(Number.parseInt(currentDatabaseBoard?.shareCount || 0, 10) || 0);
        }
      } else {
        setSavedBoards([]);
        setBoardsError(data?.message || "Unable to load boards.");
      }
    } catch {
      setSavedBoards([]);
      setBoardsError("Unable to load boards.");
    } finally {
      setBoardsLoading(false);
    }
  }, [savedBoardId, user]);

  useEffect(() => {
    if (!showWelcomeBoardPrompt || !user?.team || !user?.scout) return;
    fetchSavedBoards();
  }, [fetchSavedBoards, showWelcomeBoardPrompt, user]);

  const fetchTeamPresets = useCallback(async () => {
    if (!user?.team) {
      setTeamPresets([]);
      setTeamPresetMessage("");
      return;
    }

    setTeamPresetsLoading(true);

    try {
      const response = await fetch(DRAFT_BOARD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "getPresets",
          team: user.team,
        }),
      });
      const data = await response.json();
      if (data?.success) {
        setTeamPresets(Array.isArray(data.presets) ? data.presets : []);
        setTeamPresetMessage("");
      } else {
        setTeamPresets([]);
      setTeamPresetMessage(data?.message || "Unable to load page presets.");
      }
    } catch {
      setTeamPresets([]);
      setTeamPresetMessage("Unable to load page presets.");
    } finally {
      setTeamPresetsLoading(false);
    }
  }, [user]);

  const saveTeamPreset = useCallback(async ({ presetId = 0, presetName = "", labels = [], pageType = activePageType, applyToPageLabels = false, activateSavedPreset = true } = {}) => {
    if (!user?.team || !user?.scout) {
      setTeamPresetMessage("Sign in required to save page presets");
      return null;
    }

    const cleanName = cleanText(presetName);
    if (!cleanName) {
      setTeamPresetMessage("Page preset name required");
      return null;
    }

    const normalizedLabels = normalizePresetLabels(labels);
    if (normalizedLabels.length === 0) {
      setTeamPresetMessage("No page preset positions to save");
      return null;
    }

    setTeamPresetMessage("Saving...");

    try {
      const response = await fetch(DRAFT_BOARD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "savePreset",
          team: user.team,
          scoutId: user.scout,
          presetId,
          presetName: cleanName,
          presetData: {
            labels: normalizedLabels,
            pageType: normalizeBoardPageType(pageType),
          },
        }),
      });
      const data = await response.json();
      if (data?.success) {
        await fetchTeamPresets();
        const savedPreset = data.preset || {};
        const nextPresetId = savedPreset.presetId || presetId;
        const nextBaseline = JSON.stringify(normalizedLabels);
        const shouldActivateSavedPreset = activateSavedPreset && (activePresetDraft || applyToPageLabels);
        const nextPresetSessionId = activePresetDraft?.presetSessionId || `preset-team-${nextPresetId}-${Date.now()}`;
        const nextPresetDraft = shouldActivateSavedPreset ? {
          ...(activePresetDraft || {}),
          source: "team",
          presetId: nextPresetId,
          name: cleanName,
          presetSessionId: nextPresetSessionId,
          baseline: nextBaseline,
          pageType: normalizeBoardPageType(pageType),
        } : null;
        const nextLabels = nextPresetDraft?.presetSessionId
          ? boardLabels.map((label) => (applyToPageLabels || label?.presetSessionId === nextPresetDraft.presetSessionId)
            ? {
              ...label,
              presetSessionId: nextPresetDraft.presetSessionId,
              presetSource: "team",
              presetId: nextPresetId,
            }
            : label)
          : boardLabels;

        if (nextPresetDraft) {
          setBoardLabels(nextLabels);
          setActivePresetDraft(nextPresetDraft);
          persistActivePagePresetDraft(nextPresetDraft, nextLabels);
        }
        setTeamPresetMessage(presetId ? "Page preset updated" : "Page preset saved to team");
        return data;
      }
      setTeamPresetMessage(data?.message || "Unable to save page preset");
      return data;
    } catch {
      setTeamPresetMessage("Unable to save page preset");
      return null;
    }
  }, [activePageType, activePresetDraft, boardLabels, fetchTeamPresets, persistActivePagePresetDraft, user]);

  const deleteTeamPreset = useCallback(async (preset) => {
    const presetId = Number(preset?.presetId || 0);
    if (!presetId || !user?.team || !user?.scout) return;

    const confirmed = window.confirm(`Delete "${preset?.presetName || "page preset"}" for the team?`);
    if (!confirmed) return;

    setTeamPresetMessage("Deleting...");

    try {
      const response = await fetch(DRAFT_BOARD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "deletePreset",
          team: user.team,
          scoutId: user.scout,
          presetId,
        }),
      });
      const data = await response.json();
      if (data?.success) {
        setTeamPresets((current) => current.filter((item) => Number(item?.presetId || 0) !== presetId));
        if (Number(activePresetDraft?.presetId || 0) === presetId) {
          setActivePresetDraft(null);
          persistActivePagePresetDraft(null);
        } else {
          setActivePresetDraft((current) => Number(current?.presetId || 0) === presetId ? null : current);
        }
        setTeamPresetMessage("Page preset deleted");
      } else {
        setTeamPresetMessage(data?.message || "Unable to delete page preset");
      }
    } catch {
      setTeamPresetMessage("Unable to delete page preset");
    }
  }, [activePresetDraft, persistActivePagePresetDraft, user]);

  useEffect(() => {
    if (overlaysOpen) fetchTeamPresets();
  }, [fetchTeamPresets, overlaysOpen]);

  useEffect(() => {
    if (!overlaysOpen) return undefined;

    const handleResize = () => {
      setPresetsPopupPosition((current) => clampPresetsPopupPosition(current));
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [overlaysOpen]);

  const renameSavedBoard = useCallback(async (nextBoardName) => {
    if (!savedBoardId || !currentBoardIsOwner || !user?.team || !user?.scout) return { success: false };

    try {
      const response = await fetch(DRAFT_BOARD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "renameBoard",
          boardId: savedBoardId,
          boardName: nextBoardName,
          team: user.team,
          scoutId: user.scout,
          scoutName: currentScoutName,
        }),
      });
      return await response.json();
    } catch {
      return { success: false };
    }
  }, [currentBoardIsOwner, currentScoutName, savedBoardId, user]);

  const fetchBoardShares = useCallback(async () => {
    if (!savedBoardId || !currentBoardIsOwner || !user?.team || !user?.scout) return;

    setShareLoading(true);
    setShareMessage("");

    try {
      const response = await fetch(DRAFT_BOARD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "getBoardShares",
          boardId: savedBoardId,
          team: user.team,
          scoutId: user.scout,
        }),
      });
      const data = await response.json();
      if (data?.success) {
        const shares = Array.isArray(data.shares) ? data.shares : [];
        setShareScoutIds(shares.map((share) => String(share.scoutId)));
        setCurrentBoardShareCount(shares.length);
        setShareCanEdit(shares.some((share) => share.canEdit));
      } else {
        setShareMessage(data?.message || "Unable to load shares.");
      }
    } catch {
      setShareMessage("Unable to load shares.");
    } finally {
      setShareLoading(false);
    }
  }, [currentBoardIsOwner, savedBoardId, user]);

  const fetchAdminShareScouts = useCallback(async () => {
    if (!isAdminUser || !user?.scout) return;

    setAdminShareLoading(true);
    setAdminShareMessage("");

    try {
      const response = await fetch(DRAFT_BOARD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "getActiveCflShareScouts",
          scoutId: user.scout,
          security: user.userContactLevel || user.security || "",
        }),
      });
      const data = await response.json();
      if (data?.success) {
        setAdminShareScouts(Array.isArray(data.scouts) ? data.scouts : []);
      } else {
        setAdminShareMessage(data?.message || "Unable to load CFL scouts.");
      }
    } catch {
      setAdminShareMessage("Unable to load CFL scouts.");
    } finally {
      setAdminShareLoading(false);
    }
  }, [isAdminUser, user]);

  const saveBoardShares = useCallback(async (nextScoutIds = shareScoutIds, nextCanEdit = shareCanEdit, options = {}) => {
    if (!savedBoardId || !currentBoardIsOwner || !user?.team || !user?.scout) return;

    setShareLoading(true);
    setShareMessage("Saving...");
    const nextShareToFbx = options.shareToFbx !== undefined
      ? options.shareToFbx === true
      : FBX_SHARE_SCOUT_IDS.some((scoutId) => nextScoutIds.includes(scoutId));

    try {
      const response = await fetch(DRAFT_BOARD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "saveBoardShares",
          boardId: savedBoardId,
          team: user.team,
          scoutId: user.scout,
          scoutName: currentScoutName,
          scouts: nextScoutIds,
          shareToFbx: nextShareToFbx,
          canEdit: nextCanEdit,
        }),
      });
      const data = await response.json();
      if (data?.success) {
        const savedScoutIds = Array.isArray(data.scouts) ? data.scouts.map((scoutId) => String(scoutId)) : nextScoutIds;
        const nextShareCount = Number.parseInt(data.shareCount || savedScoutIds.length || 0, 10) || 0;
        const nextUpdatedAt = getBoardUpdatedAt(data) || currentBoardUpdatedAt;
        const nextUpdatedBy = getBoardUpdatedBy(data) || currentScoutName;
        const nextBoardVersion = getBoardVersion(data) || currentBoardVersion;
        setShareScoutIds(savedScoutIds);
        setCurrentBoardShareCount(nextShareCount);
        setCurrentBoardUpdatedAt(nextUpdatedAt);
        setCurrentBoardUpdatedBy(nextUpdatedBy);
        setCurrentBoardVersion(nextBoardVersion);
        saveBoardLocal(boardPlayers, { shareCount: nextShareCount, shareCanEdit: nextCanEdit, updatedAt: nextUpdatedAt, updatedBy: nextUpdatedBy, boardVersion: nextBoardVersion }, draftPlayers, boardLabels, boardDetails);
        setShareMessage(nextShareCount > 0 ? "Shared" : "Not shared");
        await fetchSavedBoards();
      } else {
        setShareMessage(data?.message || "Unable to save shares.");
      }
    } catch {
      setShareMessage("Unable to save shares.");
    } finally {
      setShareLoading(false);
    }
  }, [boardDetails, boardLabels, boardPlayers, currentBoardIsOwner, currentBoardUpdatedAt, currentBoardVersion, currentScoutName, draftPlayers, fetchSavedBoards, saveBoardLocal, savedBoardId, shareCanEdit, shareScoutIds, user]);

  const updateShareCanEdit = (nextCanEdit) => {
    setShareCanEdit(nextCanEdit);
    saveBoardShares(shareScoutIds, nextCanEdit);
  };

  const updateSharedScout = (scoutId, checked) => {
    const nextScoutIds = checked
      ? [...shareScoutIds.filter((id) => id !== scoutId), scoutId]
      : shareScoutIds.filter((id) => id !== scoutId);

    setShareScoutIds(nextScoutIds);
    setCurrentBoardShareCount(nextScoutIds.length);
    saveBoardShares(nextScoutIds, shareCanEdit, { shareToFbx: checked ? undefined : false });
  };

  const updateSharedToFbx = (checked) => {
    const nextScoutIds = checked
      ? Array.from(new Set([...shareScoutIds, ...fbxShareScoutIds]))
      : shareScoutIds.filter((id) => !FBX_SHARE_SCOUT_IDS.includes(id));

    setShareScoutIds(nextScoutIds);
    setCurrentBoardShareCount(nextScoutIds.length);
    saveBoardShares(nextScoutIds, shareCanEdit, { shareToFbx: checked });
  };

  const removeAllBoardShares = () => {
    setShareScoutIds([]);
    setShareCanEdit(false);
    setCurrentBoardShareCount(0);
    saveBoardShares([], false, { shareToFbx: false });
  };

  const loadPlayerTransactions = useCallback(async (playerId) => {
    if (!playerId) return;

    setPlayerTransactionCache((cache) => ({
      ...cache,
      [playerId]: {
        transactions: cache[playerId]?.transactions || [],
        loading: true,
        error: "",
      },
    }));

    try {
      const response = await fetch(DRAFT_BOARD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "getPlayerTransactions",
          FBXID: playerId,
        }),
      });
      const data = await response.json();
      if (data?.success) {
        setPlayerTransactionCache((cache) => ({
          ...cache,
          [playerId]: {
            transactions: Array.isArray(data.transactions) ? data.transactions : [],
            loading: false,
            error: "",
          },
        }));
      } else {
        setPlayerTransactionCache((cache) => ({
          ...cache,
          [playerId]: {
            transactions: [],
            loading: false,
            error: data?.message || "Unable to load transactions",
          },
        }));
      }
    } catch {
      setPlayerTransactionCache((cache) => ({
        ...cache,
        [playerId]: {
          transactions: [],
          loading: false,
          error: "Unable to load transactions",
        },
      }));
    }
  }, []);

  const togglePlayerTransactions = useCallback((player) => {
    const playerId = getDraftBoardPlayerId(player);
    if (!playerId) return;

    setExpandedTransactionPlayers((expanded) => {
      const nextOpen = !expanded[playerId];
      const nextExpanded = {
        ...expanded,
        [playerId]: nextOpen,
      };
      if (!nextOpen) delete nextExpanded[playerId];
      return nextExpanded;
    });

    const cacheEntry = playerTransactionCache[playerId];
    const cachedTransactions = Array.isArray(cacheEntry?.transactions) ? cacheEntry.transactions : null;
    if (!cacheEntry || (!cacheEntry.loading && (!cachedTransactions || cachedTransactions.length === 0))) {
      loadPlayerTransactions(playerId);
    }
  }, [loadPlayerTransactions, playerTransactionCache]);

  const loadPlayerQuickNotes = useCallback(async (playerId) => {
    const cleanPlayerId = String(playerId || "").trim();
    if (!cleanPlayerId || !user?.team || !user?.scout) {
      setPlayerQuickNoteVersions([]);
      setPlayerQuickNoteVersionIndex(0);
      return [];
    }

    try {
      const response = await fetch(QUICK_NOTES_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "getQuickNotes",
          team: user.team,
          scoutId: user.scout,
          FBXID: cleanPlayerId,
        }),
      });
      const data = await response.json();
      const nextVersions = data?.success && Array.isArray(data.notes) ? data.notes : [];
      const latestText = nextVersions[0]?.noteText || "";

      setPlayerQuickNoteVersions(nextVersions);
      setPlayerQuickNoteVersionIndex(0);
      setPlayerNoteText(latestText);
      setPlayerNoteCache((cache) => ({
        ...cache,
        [cleanPlayerId]: {
          ...(cache[cleanPlayerId] || {}),
          noteText: latestText,
          quickNoteVersions: nextVersions,
        },
      }));

      return nextVersions;
    } catch {
      setPlayerQuickNoteVersions([]);
      setPlayerQuickNoteVersionIndex(0);
      return [];
    }
  }, [user]);

  const notifyQuickNotesChanged = useCallback((playerId, versions) => {
    window.dispatchEvent(new CustomEvent("FBX_QUICK_NOTES_CHANGED", {
      detail: {
        FBXID: String(playerId || "").trim(),
        hasQuickNote: Array.isArray(versions) && versions.length > 0 ? "Y" : "N",
        quickNoteCount: Array.isArray(versions) && versions.length > 0 ? 1 : 0,
        versionCount: Array.isArray(versions) ? versions.length : 0,
      },
    }));
  }, []);

  const goToPlayerQuickNoteVersion = useCallback((nextIndex) => {
    const boundedIndex = Math.min(Math.max(nextIndex, 0), Math.max(playerQuickNoteVersions.length - 1, 0));
    setPlayerQuickNoteVersionIndex(boundedIndex);
    setPlayerNoteText(playerQuickNoteVersions[boundedIndex]?.noteText || "");
    setPlayerNoteMessage("");
  }, [playerQuickNoteVersions]);

  const deletePlayerQuickNoteVersion = useCallback(async () => {
    const playerId = String(notePlayer?.FBXID || highlightedFBXID || "").trim();
    const version = playerQuickNoteVersions[playerQuickNoteVersionIndex];
    if (!playerId || !version?.quickNoteId || !user?.team || !user?.scout) return;

    setPlayerNoteLoading(true);
    setPlayerNoteMessage("");
    try {
      const response = await fetch(QUICK_NOTES_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "deleteQuickNote",
          team: user.team,
          scoutId: user.scout,
          quickNoteId: version.quickNoteId,
        }),
      });
      const data = await response.json();
      if (data?.success) {
        const nextVersions = await loadPlayerQuickNotes(playerId);
        notifyQuickNotesChanged(playerId, nextVersions);
        setPlayerNoteMessage("QN version deleted");
      } else {
        setPlayerNoteMessage(data?.message || "Unable to delete QN version");
      }
    } catch {
      setPlayerNoteMessage("Unable to delete QN version");
    } finally {
      setPlayerNoteLoading(false);
    }
  }, [highlightedFBXID, loadPlayerQuickNotes, notePlayer, notifyQuickNotesChanged, playerQuickNoteVersionIndex, playerQuickNoteVersions, user]);


  useEffect(() => {
    if (!highlightedFBXID || !user?.team || !user?.scout) return;
    loadPlayerQuickNotes(highlightedFBXID);
  }, [highlightedFBXID, loadPlayerQuickNotes, user]);

  useEffect(() => {
    const handleQuickNotesChanged = (event) => {
      const changedFBXID = String(event?.detail?.FBXID || "").trim();
      if (!changedFBXID || changedFBXID === highlightedFBXID) {
        loadPlayerQuickNotes(changedFBXID || highlightedFBXID);
      }
    };

    window.addEventListener("FBX_QUICK_NOTES_CHANGED", handleQuickNotesChanged);
    return () => window.removeEventListener("FBX_QUICK_NOTES_CHANGED", handleQuickNotesChanged);
  }, [highlightedFBXID, loadPlayerQuickNotes]);

  const openPlayerNote = useCallback(async (player, mode = "edit") => {
    const playerId = String(player?.FBXID || "").trim();
    if (!playerId) return;

    setNotePlayer(player);
    setPlayerNoteMode(mode);
    setPlayerNoteText(playerNoteCache[playerId]?.noteText || "");
    setPlayerQuickNoteVersions(Array.isArray(playerNoteCache[playerId]?.quickNoteVersions) ? playerNoteCache[playerId].quickNoteVersions : []);
    setPlayerQuickNoteVersionIndex(0);
    setPlayerGrade(playerNoteCache[playerId]?.grade || "");
    setPlayerBoardPosition(playerNoteCache[playerId]?.boardPosition || "");
    setPlayerBoardNumber(normalizeJerseyNumber(playerNoteCache[playerId]?.boardNumber));
    setPlayerNoteMessage("");

    if (!user?.team || !user?.scout) {
      setPlayerNoteMessage("Sign in required to save memo");
      return;
    }

    setPlayerNoteLoading(true);

    try {
      const response = await fetch(DRAFT_BOARD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "getPlayerNote",
          team: user.team,
          scoutId: user.scout,
          FBXID: playerId,
        }),
      });
      const data = await response.json();
      if (data?.success) {
        const nextNote = {
          grade: data?.note?.grade || "",
          boardPosition: data?.note?.boardPosition || "",
          boardNumber: data?.note?.boardNumber || "",
          updatedAt: data?.note?.updatedAt || "",
        };
        setPlayerNoteCache((cache) => ({
          ...cache,
          [playerId]: {
            ...(cache[playerId] || {}),
            ...nextNote,
          },
        }));
        setPlayerGrade(nextNote.grade);
        setPlayerBoardPosition(nextNote.boardPosition);
        setPlayerBoardNumber(normalizeJerseyNumber(nextNote.boardNumber));
        await loadPlayerQuickNotes(playerId);
      } else {
        setPlayerNoteMessage(data?.message || "Unable to load memo");
      }
    } catch {
      setPlayerNoteMessage("Unable to load memo");
    } finally {
      setPlayerNoteLoading(false);
    }
  }, [loadPlayerQuickNotes, playerNoteCache, user]);

  const savePlayerNote = useCallback(async () => {
    const playerId = String(notePlayer?.FBXID || "").trim();
    if (!playerId || !user?.team || !user?.scout) {
      setPlayerNoteMessage("Unable to save team data");
      return;
    }

    setPlayerNoteLoading(true);
    setPlayerNoteMessage("");
    const nextBoardNumber = serializeJerseyNumber(playerBoardNumber);

    try {
      const response = await fetch(DRAFT_BOARD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "savePlayerNote",
          team: user.team,
          scoutId: user.scout,
          FBXID: playerId,
          noteText: "",
          grade: playerGrade,
          boardPosition: playerBoardPosition,
          boardNumber: nextBoardNumber,
        }),
      });
      const data = await response.json();
      if (data?.success) {
        setPlayerNoteCache((cache) => ({
          ...cache,
          [playerId]: {
            ...(cache[playerId] || {}),
            grade: playerGrade,
            boardPosition: playerBoardPosition,
            boardNumber: nextBoardNumber,
          },
        }));
        setPlayerNoteMode("view");
        setPlayerNoteMessage("Team data saved");
      } else {
        setPlayerNoteMessage(data?.message || "Unable to save team data");
      }
    } catch {
      setPlayerNoteMessage("Unable to save team data");
    } finally {
      setPlayerNoteLoading(false);
    }
  }, [notePlayer, playerBoardNumber, playerBoardPosition, playerGrade, user]);

  const saveWhiteBoardQuickNote = useCallback(async () => {
    const playerId = String(notePlayer?.FBXID || highlightedFBXID || "").trim();
    if (!playerId || !user?.team || !user?.scout) {
      setPlayerNoteMessage("Unable to save QN");
      return;
    }

    const latestQuickNote = playerQuickNoteVersions[0] || null;
    const nextQuickNoteText = cleanText(playerNoteText);
    const quickNoteChanged = nextQuickNoteText !== cleanText(latestQuickNote?.noteText);

    if (!nextQuickNoteText) {
      setPlayerNoteMessage("Enter a QN first");
      return;
    }
    if (latestQuickNote && !quickNoteChanged) {
      setPlayerNoteMessage("No QN changes to save");
      return;
    }

    setPlayerNoteLoading(true);
    setPlayerNoteMessage("");
    try {
      const quickNoteResponse = await fetch(QUICK_NOTES_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: latestQuickNote?.quickNoteId ? "updateQuickNote" : "createQuickNote",
          team: user.team,
          scoutId: user.scout,
          scoutName: currentScoutName,
          FBXID: playerId,
          quickNoteId: latestQuickNote?.quickNoteId,
          noteText: nextQuickNoteText,
        }),
      });
      const quickNoteData = await quickNoteResponse.json();
      if (!quickNoteData?.success) {
        setPlayerNoteMessage(quickNoteData?.message || "Unable to save QN");
        return;
      }

      const nextQuickNoteVersions = await loadPlayerQuickNotes(playerId);
      notifyQuickNotesChanged(playerId, nextQuickNoteVersions);
      setPlayerNoteMessage(latestQuickNote ? "Saved as new QN version" : "QN added");
    } catch {
      setPlayerNoteMessage("Unable to save QN");
    } finally {
      setPlayerNoteLoading(false);
    }
  }, [currentScoutName, highlightedFBXID, loadPlayerQuickNotes, notePlayer, notifyQuickNotesChanged, playerNoteText, playerQuickNoteVersions, user]);

  const clearPlayerNoteSelection = useCallback(() => {
    setNotePlayer(null);
    setPlayerNoteMode("edit");
    setPlayerNoteText("");
    setPlayerGrade("");
    setPlayerBoardPosition("");
    setPlayerBoardNumber("");
    setPlayerNoteMessage("");
  }, []);

  useEffect(() => {
    const playerIds = [...new Set(boardPlayers
      .map((item) => String(item?.FBXID || "").trim())
      .filter(Boolean))];

    if (!user?.team || !user?.scout || playerIds.length === 0) return;

    let isCurrent = true;

    const loadBoardNotes = async () => {
      try {
        const response = await fetch(DRAFT_BOARD_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            scriptName: "getPlayerNotes",
            team: user.team,
            scoutId: user.scout,
            FBXIDs: playerIds,
          }),
        });
        const data = await response.json();
        if (isCurrent && data?.success) {
          const boardNotes = Object.entries(data.notes || {}).reduce((next, [playerId, note]) => {
            next[playerId] = {
              grade: note?.grade || "",
              boardPosition: note?.boardPosition || "",
              boardNumber: note?.boardNumber || "",
              updatedAt: note?.updatedAt || "",
            };
            return next;
          }, {});
          setPlayerNoteCache((cache) => Object.entries(boardNotes).reduce((next, [playerId, note]) => ({
            ...next,
            [playerId]: {
              ...(next[playerId] || {}),
              ...note,
            },
          }), { ...cache }));
        }
      } catch {}
    };

    loadBoardNotes();

    return () => {
      isCurrent = false;
    };
  }, [boardPlayers, user]);

  useEffect(() => {
    if (!activePageId || (!savedBoardId && boardName === BOARD_CHOOSER_NAME)) return undefined;

    const currentPages = mergeCurrentPage(boardPlayers, draftPlayers, boardLabels, boardDetails, boardOverlays);
    const playerIds = [...new Set(currentPages
      .flatMap((page) => Array.isArray(page?.players) ? page.players : [])
      .map((item) => String(item?.FBXID || "").trim())
      .filter(Boolean))];

    if (playerIds.length === 0) return undefined;

    const refreshKey = [
      savedBoardId || "local",
      currentBoardVersion || 0,
      activePageId,
      boardBaseTeamLeague,
      boardNumberDisplayType,
      boardPositionDisplayType,
      playerIds.sort().join(","),
    ].join(":");

    if (playerSnapshotRefreshKeyRef.current === refreshKey) return undefined;
    playerSnapshotRefreshKeyRef.current = refreshKey;

    let isCurrent = true;

    const refreshBoardPlayerSnapshots = async () => {
      try {
        const detailResults = await Promise.all(playerIds.map(async (playerId) => {
          try {
            const response = await fetch(DRAFT_BOARD_ENDPOINT, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                scriptName: "getPlayerDetail",
                FBXID: playerId,
              }),
            });
            const data = await response.json();
            return data?.success && data?.player ? [playerId, data.player] : null;
          } catch {
            return null;
          }
        }));

        if (!isCurrent) return;

        const freshPlayersById = detailResults.reduce((lookup, result) => {
          if (result) lookup[result[0]] = result[1];
          return lookup;
        }, {});

        if (Object.keys(freshPlayersById).length === 0) return;

        setPlayerDetailCache((cache) => ({
          ...cache,
          ...freshPlayersById,
        }));

        let changed = false;
        const refreshedPages = currentPages.map((page) => ({
          ...page,
          players: (Array.isArray(page?.players) ? page.players : []).map((item) => {
            const playerId = String(item?.FBXID || "").trim();
            const refreshed = refreshBoardPlayerSnapshot(item, freshPlayersById[playerId], {
              baseTeamLeague: boardBaseTeamLeague,
              numberDisplayType: boardNumberDisplayType,
              positionDisplayType: boardPositionDisplayType,
            });
            if (refreshed.changed) changed = true;
            return refreshed.player;
          }),
        }));
        const refreshedDraftPlayers = draftPlayers.map((item) => {
          const playerId = String(item?.FBXID || "").trim();
          const refreshed = refreshBoardPlayerSnapshot(item, freshPlayersById[playerId], {
            baseTeamLeague: boardBaseTeamLeague,
            numberDisplayType: boardNumberDisplayType,
            positionDisplayType: boardPositionDisplayType,
          });
          if (refreshed.changed) changed = true;
          return refreshed.player;
        });

        if (!changed || !isCurrent) return;

        const refreshedActivePage = refreshedPages.find((page) => page.pageId === activePageId) || refreshedPages[0] || buildBoardPage();
        setBoardPages(refreshedPages);
        setBoardPlayers(refreshedActivePage.players);
        setBoardLabels(refreshedActivePage.labels);
        setBoardDetails(refreshedActivePage.details);
        setBoardOverlays(refreshedActivePage.overlays);
        setActivePresetDraft(refreshedActivePage.presetDraft || null);
        setDraftPlayers(refreshedDraftPlayers);

        if (!canEditCurrentBoard) return;

        saveBoardLocal(refreshedActivePage.players, {}, refreshedDraftPlayers, refreshedActivePage.labels, refreshedActivePage.details, refreshedActivePage.overlays, {
          pages: refreshedPages,
          activePageId: refreshedActivePage.pageId,
        });
      } catch {}
    };

    const timer = window.setTimeout(refreshBoardPlayerSnapshots, 250);

    return () => {
      isCurrent = false;
      window.clearTimeout(timer);
    };
  }, [activePageId, boardBaseTeamLeague, boardDetails, boardLabels, boardName, boardNumberDisplayType, boardOverlays, boardPlayers, boardPages, boardPositionDisplayType, canEditCurrentBoard, currentBoardVersion, draftPlayers, mergeCurrentPage, saveBoardLocal, savedBoardId]);

  useEffect(() => {
    const playerId = String(highlightedFBXID || "").trim();
    if (!dataOpen || !playerId) {
      setPlayerDetailLoading(false);
      return;
    }

    let isCurrent = true;
    setPlayerDetailLoading(true);

    const loadPlayerDetail = async () => {
      try {
        const response = await fetch(DRAFT_BOARD_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            scriptName: "getPlayerDetail",
            FBXID: playerId,
          }),
        });
        const data = await response.json();
        if (isCurrent && data?.success && data?.player) {
          setPlayerDetailCache((cache) => ({
            ...cache,
            [playerId]: data.player,
          }));
        }
      } catch {
        // Data falls back to the board/list record if the fresh lookup is unavailable.
      } finally {
        if (isCurrent) setPlayerDetailLoading(false);
      }
    };

    loadPlayerDetail();

    return () => {
      isCurrent = false;
    };
  }, [dataOpen, highlightedFBXID]);

  useEffect(() => {
    const playerId = String(highlightedFBXID || "").trim();
    if (!playerId) return;

    const openListKeys = [
      playersOpen ? "players" : "",
      onBoardOpen ? "onBoard" : "",
      draftOpen ? "draft" : "",
    ].filter(Boolean);
    if (openListKeys.length === 0) return;

    const timer = window.setTimeout(() => {
      openListKeys.forEach((listKey) => {
        const element = playerListRowRefs.current[`${listKey}:${playerId}`];
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
          return;
        }

        playerListContainerRefs.current[listKey]?.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      });
    }, 0);

    return () => window.clearTimeout(timer);
  }, [
    highlightedFBXID,
    playersOpen,
    onBoardOpen,
    draftOpen,
    filteredPlayers.length,
    filteredOnBoardPlayers.length,
    draftPlayers.length,
  ]);

  const renderPlayerTransactionToggle = (player) => {
    const playerId = getDraftBoardPlayerId(player);
    const isOpen = !!expandedTransactionPlayers[playerId];
    return (
      <button
        type="button"
        className={`draft-board-player-transaction-toggle ${isOpen ? "draft-board-player-transaction-toggle-open" : ""}`}
        disabled={!playerId}
        title={isOpen ? "Hide transactions" : "Show last 2 CFL, NFL, and UFL transactions"}
        aria-label={isOpen ? "Hide transactions" : "Show last 2 CFL, NFL, and UFL transactions"}
        onClick={(event) => {
          event.stopPropagation();
          togglePlayerTransactions(player);
        }}
        onMouseDown={(event) => event.stopPropagation()}
        onDragStart={(event) => event.preventDefault()}
      >
        {isOpen ? "▾" : "▸"}
      </button>
    );
  };

  const renderPlayerTransactionPanel = (player) => {
    const playerId = getDraftBoardPlayerId(player);
    if (!playerId || !expandedTransactionPlayers[playerId]) return null;

    const cacheEntry = playerTransactionCache[playerId] || { transactions: [], loading: true, error: "" };
    const transactions = (Array.isArray(cacheEntry.transactions) ? cacheEntry.transactions : [])
      .filter((transaction) => getDraftBoardTransactionLeague(transaction) || getTransactionValue(transaction, "transactionID", "TransactionRowID", "ID"));

    return (
      <div className="draft-board-player-transactions-panel">
        {cacheEntry.loading ? (
          <div className="draft-board-player-transactions-state">Loading transactions...</div>
        ) : cacheEntry.error ? (
          <div className="draft-board-player-transactions-state">{cacheEntry.error}</div>
        ) : transactions.length === 0 ? (
          <div className="draft-board-player-transactions-state">There are no transactions.</div>
        ) : (
          transactions.map((transaction, index) => {
            const transactionId = getTransactionValue(transaction, "transactionID", "TransactionRowID", "ID") || `transaction-${index}`;
            const transactionDate = formatBoardTransactionDate(getTransactionValue(transaction, "ProTeamDate", "transDate", "theDate", "Date"));
            const league = getDraftBoardTransactionLeague(transaction) || "TX";
            const team = getTransactionValue(transaction, "A_ProTeam", "ProTeam", "Team", "team");
            const position = getTransactionValue(transaction, "A_ProPos", "PRO_POS", "Position", "position");
            const status = getTransactionValue(transaction, "ProTeamStatus", "Status", "status");
            const note = getTransactionValue(transaction, "ST_NOTES", "noteData", "notes");
            const summary = [team, status, position].filter(Boolean).join(" | ");

            return (
              <div className="draft-board-player-transaction" key={`${playerId}-${transactionId}-${index}`}>
                <div className="draft-board-player-transaction-line">
                  <span className={`draft-board-player-transaction-league draft-board-player-transaction-league-${league.toLowerCase()}`}>
                    {league}
                  </span>
                  <span className="draft-board-player-transaction-date">{transactionDate || "No date"}</span>
                  {summary && <span className="draft-board-player-transaction-summary">{summary}</span>}
                </div>
                {note && <div className="draft-board-player-transaction-note">{note}</div>}
              </div>
            );
          })
        )}
      </div>
    );
  };

  const closePresetsMenu = () => {
    setOverlaysOpen(false);
  };

  const getDefaultPresetsPopupPosition = () => {
    const rect = whiteboardRef.current?.getBoundingClientRect();
    return clampPresetsPopupPosition({
      left: rect ? rect.left + 10 : PRESETS_POPUP_DEFAULT_POSITION.left,
      top: rect ? rect.top + DRAFT_BOARD_POPUP_DEFAULT_OFFSET_Y : PRESETS_POPUP_DEFAULT_POSITION.top,
    });
  };

  const getDefaultBoardPopupPosition = () => {
    const screenRect = draftBoardScreenRef.current?.getBoundingClientRect();
    const boardRect = whiteboardRef.current?.getBoundingClientRect();
    const boardLeft = screenRect && boardRect
      ? boardRect.left - screenRect.left
      : 0;
    const boardTop = screenRect && boardRect
      ? boardRect.top - screenRect.top
      : 68;
    return {
      x: Math.max(10, Math.round(boardLeft + 10)),
      y: Math.max(10, Math.round(boardTop + DRAFT_BOARD_POPUP_DEFAULT_OFFSET_Y)),
    };
  };

  const closeWhiteBoardPopups = (except = "") => {
    if (except !== "boards") setBoardsOpen(false);
    if (except !== "add") setAddMenuOpen(false);
    if (except !== "actions") setActionMenuOpen(false);
    if (except !== "boardPreferences") setBoardPreferencesOpen(false);
    if (except !== "share") setShowShareBoardForm(false);
    if (except !== "newBoard") setShowNewBoardForm(false);
    if (except !== "newPage") setShowNewPageForm(false);
    if (except !== "pageEditor") setPageEditorOpen(false);
    if (except !== "presets") setOverlaysOpen(false);
    if (except !== "quickSearch") setQuickSearchOpen(false);
    if (except !== "players") setPlayersOpen(false);
    if (except !== "onBoard") setOnBoardOpen(false);
    if (except !== "draft") setDraftOpen(false);
    if (except !== "data") setDataOpen(false);
    if (except !== "tutorial") setTutorialOpen(false);
    if (except !== "align") setAlignmentMenuOpen(false);
    if (except !== "clear") setClearOptionsOpen(false);
    if (except !== "view") setViewOptionsOpen(false);
  };

  const togglePresetsMenu = () => {
    const nextOpen = !overlaysOpen;
    closeWhiteBoardPopups(nextOpen ? "presets" : "");
    if (nextOpen) setPresetsPopupPosition(getStoredPresetsPopupPosition(getDefaultPresetsPopupPosition()));
    setOverlaysOpen(nextOpen);
  };

  const handlePresetsPopupMouseDown = useCallback((event) => {
    if (event.button !== 0) return;
    event.preventDefault();

    const startPosition = clampPresetsPopupPosition(presetsPopupPosition);
    presetsPopupDragRef.current = {
      startClientX: event.clientX,
      startClientY: event.clientY,
      startLeft: startPosition.left,
      startTop: startPosition.top,
      lastPosition: startPosition,
    };
    setPresetsPopupPosition(startPosition);
    saveStoredPresetsPopupPosition(startPosition);

    const handleMouseMove = (moveEvent) => {
      if (!presetsPopupDragRef.current) return;
      const nextPosition = clampPresetsPopupPosition({
        left: presetsPopupDragRef.current.startLeft + moveEvent.clientX - presetsPopupDragRef.current.startClientX,
        top: presetsPopupDragRef.current.startTop + moveEvent.clientY - presetsPopupDragRef.current.startClientY,
      });
      presetsPopupDragRef.current.lastPosition = nextPosition;
      setPresetsPopupPosition(nextPosition);
      saveStoredPresetsPopupPosition(nextPosition);
    };

    const handleMouseUp = () => {
      if (presetsPopupDragRef.current?.lastPosition) {
        saveStoredPresetsPopupPosition(presetsPopupDragRef.current.lastPosition);
      }
      presetsPopupDragRef.current = null;
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  }, [presetsPopupPosition]);

  const openBoardsMenu = () => {
    const nextOpen = !boardsOpen;
    closeWhiteBoardPopups(nextOpen ? "boards" : "");
    setBoardsOpen(nextOpen);
    setSaveBoardName("");
    setSaveBoardNameError("");
    setNewPageName("");
    setNewPageType(BOARD_PAGE_TYPES.regular);
    setNewPageNameError("");
    setShareMessage("");

    if (nextOpen) {
      fetchSavedBoards();
    }
  };

  const openAddMenu = () => {
    const nextOpen = !addMenuOpen;
    closeWhiteBoardPopups(nextOpen ? "add" : "");
    setAddMenuOpen(nextOpen);
  };

  const openActionMenu = () => {
    const nextOpen = !actionMenuOpen;
    closeWhiteBoardPopups(nextOpen ? "actions" : "");
    setActionMenuOpen(nextOpen);
  };

  const openBoardPreferences = () => {
    closeWhiteBoardPopups("boardPreferences");
    setActionMenuOpen(false);
    setBoardPreferenceColorDraft(boardPositionMarkerColor);
    setBoardPreferenceBaseTeamLeagueDraft(boardBaseTeamLeague);
    setBoardPreferenceBaseTeamCodeDraft(boardBaseTeamCode || "");
    setBoardPreferenceNumberDisplayTypeDraft(boardNumberDisplayType);
    setBoardPreferencePositionDisplayTypeDraft(boardPositionDisplayType);
    setBoardPreferencesMessage("");
    setBoardPreferencesOpen(true);
  };

  const saveBoardPreferences = async () => {
    if (!canEditCurrentBoard) {
      setBoardPreferencesMessage("Board is view only.");
      return;
    }

    const nextPositionMarkerColor = normalizeWhiteBoardPositionMarkerColor(boardPreferenceColorDraft);
    const nextBaseTeamLeague = getBoardBaseTeamLeague({
      baseTeamLeague: boardPreferenceBaseTeamLeagueDraft,
      baseTeamCode: boardPreferenceBaseTeamCodeDraft,
    }, user?.team || boardBaseTeamCode);
    const nextBaseTeamCode = getBoardBaseTeamCode({ baseTeamCode: boardPreferenceBaseTeamCodeDraft }, user?.team || boardBaseTeamCode);
    const nextNumberDisplayType = normalizeBoardNumberDisplayType(boardPreferenceNumberDisplayTypeDraft);
    const nextPositionDisplayType = normalizeBoardPositionDisplayType(boardPreferencePositionDisplayTypeDraft);
    const nextPages = mergeCurrentPage(boardPlayers, draftPlayers, boardLabels, boardDetails, boardOverlays);
    setBoardPositionMarkerColor(nextPositionMarkerColor);
    setBoardBaseTeamLeague(nextBaseTeamLeague);
    setBoardBaseTeamCode(nextBaseTeamCode);
    setBoardNumberDisplayType(nextNumberDisplayType);
    setBoardPositionDisplayType(nextPositionDisplayType);
    setBoardPages(nextPages);
    saveBoardLocal(boardPlayers, {
      positionMarkerColor: nextPositionMarkerColor,
      baseTeamLeague: nextBaseTeamLeague,
      baseTeamCode: nextBaseTeamCode,
      numberDisplayType: nextNumberDisplayType,
      positionDisplayType: nextPositionDisplayType,
    }, draftPlayers, boardLabels, boardDetails, boardOverlays, {
      pages: nextPages,
      activePageId,
    });

    if (savedBoardId && currentBoardCanEdit) {
      setBoardPreferencesMessage("Saving...");
      await saveBoardToDatabase(boardPlayers, {
        draftPlayers,
        labels: boardLabels,
        details: boardDetails,
        overlays: boardOverlays,
        pages: nextPages,
        activePageId,
        positionMarkerColor: nextPositionMarkerColor,
        baseTeamLeague: nextBaseTeamLeague,
        baseTeamCode: nextBaseTeamCode,
        numberDisplayType: nextNumberDisplayType,
        positionDisplayType: nextPositionDisplayType,
      });
    }

    setBoardPreferencesMessage("");
    setBoardPreferencesOpen(false);
  };

  const openShareBoardMenu = () => {
    if (!savedBoardId || !currentBoardIsOwner) return;
    const nextOpen = !showShareBoardForm;
    closeWhiteBoardPopups(nextOpen ? "share" : "");
    setShowShareBoardForm(nextOpen);
    setShareMessage("");
    setAdminShareMessage("");
    if (nextOpen) {
      fetchBoardShares();
      if (isAdminUser) fetchAdminShareScouts();
    }
  };

  const switchBoardPage = (pageId) => {
    if (pageId === activePageId) return;
    const nextPages = mergeCurrentPage(boardPlayers, draftPlayers, boardLabels, boardDetails);
    const nextPage = nextPages.find((page) => page.pageId === pageId);
    if (!nextPage) return;

    setBoardPages(nextPages);
    setActivePageId(nextPage.pageId);
    setBoardPlayers(nextPage.players);
    setBoardLabels(nextPage.labels);
    setBoardDetails(nextPage.details);
    setBoardOverlays(nextPage.overlays);
    setActivePresetDraft(nextPage.presetDraft || null);
    clearPlayerNoteSelection();
    saveBoardLocal(nextPage.players, {}, draftPlayers, nextPage.labels, nextPage.details, nextPage.overlays, {
      pages: nextPages,
      activePageId: nextPage.pageId,
    });

    if (savedBoardId && currentBoardCanEdit) {
      saveBoardToDatabase(nextPage.players, {
        draftPlayers,
        labels: nextPage.labels,
        details: nextPage.details,
        overlays: nextPage.overlays,
        pages: nextPages,
        activePageId: nextPage.pageId,
      });
    }
  };

  const clearPageTransferHover = () => {
    if (pageTransferTimerRef.current) {
      window.clearTimeout(pageTransferTimerRef.current);
      pageTransferTimerRef.current = null;
    }
    setPageTransferHoverId("");
  };

  const clearPageTransferDrag = () => {
    clearPageTransferHover();
    pageTransferDragRef.current = null;
  };

  const getPageTransferFallbackPoint = (page = {}, playerIndex = 0) => {
    const pagePlayers = Array.isArray(page?.players) ? page.players : [];
    const fallbackPlayers = pagePlayers.filter((player) => {
      const playerY = Number(player?.y || 0);
      return playerY >= BOARD_PRINT_PAGE_HEIGHT - (boardCardHeight * 3);
    });
    const index = fallbackPlayers.length + playerIndex;
    const columnWidth = boardCardWidth + BOARD_REMOVE_RAIL_WIDTH;
    const fallbackColumns = Math.max(1, Math.floor((BOARD_PRINT_PAGE_WIDTH - BOARD_REMOVE_RAIL_WIDTH) / columnWidth));
    const column = index % fallbackColumns;
    const rowFromBottom = Math.floor(index / fallbackColumns);

    return {
      x: BOARD_REMOVE_RAIL_WIDTH + (column * columnWidth),
      y: Math.max(BOARD_GRID_SIZE, BOARD_PRINT_PAGE_HEIGHT - boardCardHeight - BOARD_GRID_SIZE - (rowFromBottom * (boardCardHeight + BOARD_HEADING_STACK_GAP))),
    };
  };

  const getPageTransferPointForPlayer = (player, targetPage, playerIndex = 0) => {
    const targetLabels = Array.isArray(targetPage?.labels) ? targetPage.labels : [];
    const targetPlayers = Array.isArray(targetPage?.players) ? targetPage.players : [];
    const targetPageType = normalizeBoardPageType(targetPage?.pageType);
    const stackStepY = boardCardHeight + BOARD_HEADING_STACK_GAP;
    const candidates = getPlayerBoardHeadingCandidates(player);

    const buckets = targetLabels
      .map((label, index) => {
        const heading = normalizeHeadingText(label?.text);
        if (!heading || !candidates.includes(heading)) return null;

        const headingX = getHeadingPlayerX(label);
        const headingY = Number(label?.y || 0);
        const isSplitTopHeading = targetPageType === BOARD_PAGE_TYPES.splitSort && headingY < BOARD_SPLIT_SORT_Y;
        const isFlippedHeading = targetPageType === BOARD_PAGE_TYPES.flipped;
        const stackDirection = (isSplitTopHeading || isFlippedHeading) ? "above" : "below";
        const sameColumnLabels = targetLabels.filter((candidateLabel) => {
          if (candidateLabel?.labelItemId === label?.labelItemId) return false;
          const candidateY = Number(candidateLabel?.y || 0);
          const candidateX = getHeadingPlayerX(candidateLabel);
          return Math.abs(candidateX - headingX) <= BOARD_GRID_SIZE && (stackDirection === "above" ? candidateY < headingY : candidateY > headingY);
        });
        const limitHeadingY = sameColumnLabels
          .map((candidateLabel) => Number(candidateLabel?.y || 0))
          .sort((left, right) => stackDirection === "above" ? right - left : left - right)[0];
        const minY = stackDirection === "above"
          ? (Number.isFinite(limitHeadingY) ? limitHeadingY + BOARD_LABEL_PRINT_HEIGHT + BOARD_HEADING_STACK_GAP : BOARD_GRID_SIZE)
          : headingY + BOARD_LABEL_PRINT_HEIGHT + BOARD_HEADING_STACK_GAP;
        const maxY = stackDirection === "above"
          ? headingY - BOARD_HEADING_STACK_GAP - boardCardHeight
          : (Number.isFinite(limitHeadingY)
            ? limitHeadingY - BOARD_HEADING_STACK_GAP - boardCardHeight
            : BOARD_PRINT_PAGE_HEIGHT - BOARD_GRID_SIZE - boardCardHeight);
        const bounds = {
          minY,
          maxY: Math.max(minY, maxY),
        };
        const count = targetPlayers.filter((item) => {
          const itemX = Number(item?.x || 0);
          const itemY = Number(item?.y || 0);
          return Math.abs(itemX - headingX) <= BOARD_GRID_SIZE && itemY >= bounds.minY && itemY <= bounds.maxY;
        }).length;
        const nextY = stackDirection === "above"
          ? Math.max(bounds.minY, headingY - BOARD_HEADING_STACK_GAP - boardCardHeight - (count * stackStepY))
          : minY + (count * stackStepY);

        if (nextY < bounds.minY || nextY > bounds.maxY) return null;

        return {
          x: headingX,
          y: nextY,
          count,
          labelY: headingY,
          labelX: headingX,
          candidateIndex: candidates.indexOf(heading),
          index,
        };
      })
      .filter(Boolean)
      .sort((left, right) =>
        left.count - right.count ||
        left.labelY - right.labelY ||
        left.labelX - right.labelX ||
        left.candidateIndex - right.candidateIndex ||
        left.index - right.index
      );

    return buckets[0] || getPageTransferFallbackPoint(targetPage, playerIndex);
  };

  const moveBoardPlayerToPage = (boardItemId, targetPageId, placementPoint = null, sourcePageIdOverride = "") => {
    if (!canEditCurrentBoard) {
      setSaveMessage("Board is view only");
      return;
    }
    if (!boardItemId || !targetPageId) return;

    const currentPages = mergeCurrentPage(boardPlayers, draftPlayers, boardLabels, boardDetails, boardOverlays);
    const sourcePageId = sourcePageIdOverride || activePageId || currentPages[0]?.pageId || "";
    if (sourcePageId === targetPageId && !placementPoint) return;
    const sourcePage = currentPages.find((page) => page.pageId === sourcePageId);
    const targetPage = currentPages.find((page) => page.pageId === targetPageId);
    if (!targetPage) return;

    const currentPlayer = (Array.isArray(sourcePage?.players) ? sourcePage.players : []).find((player) => player.boardItemId === boardItemId) ||
      pageTransferDragRef.current?.player;
    if (!currentPlayer) return;

    const nextPoint = clampBoardPointToPrintableArea(
      placementPoint || getPageTransferPointForPlayer(currentPlayer, targetPage),
      "player"
    );
    const movedPlayer = {
      ...currentPlayer,
      ...nextPoint,
    };
    const nextPages = currentPages.map((page) => {
      if (page.pageId === sourcePageId) {
        return {
          ...page,
          players: (Array.isArray(page.players) ? page.players : []).filter((player) => player.boardItemId !== boardItemId),
        };
      }
      if (page.pageId === targetPageId) {
        const targetPlayersWithMoved = [...(Array.isArray(page.players) ? page.players : []), movedPlayer];
        return {
          ...page,
          players: placementPoint
            ? applyBoardStackLayout(targetPlayersWithMoved, boardItemId, placementPoint)
            : targetPlayersWithMoved,
        };
      }
      return page;
    });
    const nextPage = nextPages.find((page) => page.pageId === targetPageId) || targetPage;

    setBoardPages(nextPages);
    setActivePageId(nextPage.pageId);
    setBoardPlayers(nextPage.players);
    setBoardLabels(nextPage.labels);
    setBoardDetails(nextPage.details);
    setBoardOverlays(nextPage.overlays);
    setActivePresetDraft(nextPage.presetDraft || null);
    setHighlightedFBXID(String(movedPlayer.FBXID || "").trim());
    clearPlayerNoteSelection();
    saveBoardLocal(nextPage.players, {}, draftPlayers, nextPage.labels, nextPage.details, nextPage.overlays, {
      pages: nextPages,
      activePageId: nextPage.pageId,
    });

    if (savedBoardId && currentBoardCanEdit) {
      saveBoardToDatabase(nextPage.players, {
        draftPlayers,
        labels: nextPage.labels,
        details: nextPage.details,
        overlays: nextPage.overlays,
        pages: nextPages,
        activePageId: nextPage.pageId,
      });
    }

    setSaveMessage(`Moved player to ${nextPage.pageName || "page"}`);
  };

  const schedulePageTransferOpen = (page) => {
    if (!canEditCurrentBoard || !page?.pageId || page.pageId === activePageId) return;
    const dragState = pageTransferDragRef.current;
    if (!dragState?.boardItemId) return;
    if (dragState.hoverPageId === page.pageId && pageTransferTimerRef.current) return;

    setPageTransferHoverId(page.pageId);
    if (pageTransferTimerRef.current) window.clearTimeout(pageTransferTimerRef.current);
    pageTransferTimerRef.current = window.setTimeout(() => {
      pageTransferTimerRef.current = null;
      const latestDragState = pageTransferDragRef.current;
      if (!latestDragState?.boardItemId || latestDragState.hoverPageId !== page.pageId) return;
      switchBoardPage(page.pageId);
      setSaveMessage(`Place player on ${page.pageName || "page"}`);
    }, PAGE_TAB_TRANSFER_OPEN_DELAY_MS);
    pageTransferDragRef.current = {
      ...dragState,
      hoverPageId: page.pageId,
    };
  };

  const addBoardPage = () => {
    if (!canEditCurrentBoard) {
      setSaveMessage("Board is view only");
      return;
    }

    const trimmedName = newPageName.trim();
    if (!trimmedName) {
      setNewPageNameError("Page name required.");
      return;
    }

    const currentPages = mergeCurrentPage(boardPlayers, draftPlayers, boardLabels, boardDetails);
    if (currentPages.some((page) => page.pageName.toLowerCase() === trimmedName.toLowerCase())) {
      setNewPageNameError("Page name already exists.");
      return;
    }

    const nextPage = buildBoardPage(trimmedName, { pageType: newPageType });
    const nextPages = [...currentPages, nextPage];

    setBoardPages(nextPages);
    setActivePageId(nextPage.pageId);
    setBoardPlayers(nextPage.players);
    setBoardLabels(nextPage.labels);
    setBoardDetails(nextPage.details);
    setBoardOverlays(nextPage.overlays);
    setActivePresetDraft(nextPage.presetDraft || null);
    clearPlayerNoteSelection();
    setShowNewPageForm(false);
    setNewPageName("");
    setNewPageType(BOARD_PAGE_TYPES.regular);
    setNewPageNameError("");
    saveBoardLocal(nextPage.players, {}, draftPlayers, nextPage.labels, nextPage.details, nextPage.overlays, {
      pages: nextPages,
      activePageId: nextPage.pageId,
    });

    if (savedBoardId && currentBoardCanEdit) {
      saveBoardToDatabase(nextPage.players, {
        draftPlayers,
        labels: nextPage.labels,
        details: nextPage.details,
        overlays: nextPage.overlays,
        pages: nextPages,
        activePageId: nextPage.pageId,
      });
    }
  };

  const openPageEditor = () => {
    const nextOpen = !pageEditorOpen;
    closeWhiteBoardPopups(nextOpen ? "pageEditor" : "");
    const currentPages = mergeCurrentPage(boardPlayers, draftPlayers, boardLabels, boardDetails);
    setBoardPages(currentPages);
    setPageEditorDraft(currentPages.map((page) => ({
      pageId: page.pageId,
      pageName: page.pageName,
      pageType: normalizeBoardPageType(page.pageType),
      includeInBoardCount: page.includeInBoardCount !== false,
      hideWarnings: page.hideWarnings === true,
    })));
    setPageEditorError("");
    setNewPageName("");
    setNewPageType(BOARD_PAGE_TYPES.regular);
    setNewPageNameError("");
    setPageEditorOpen(nextOpen);
  };

  const updatePageEditorName = (pageId, pageName) => {
    setPageEditorDraft((pages) =>
      pages.map((page) => page.pageId === pageId ? { ...page, pageName } : page)
    );
    if (pageEditorError) setPageEditorError("");
  };

  const updatePageEditorIncludeInBoardCount = (pageId, includeInBoardCount) => {
    setPageEditorDraft((pages) =>
      pages.map((page) => page.pageId === pageId ? { ...page, includeInBoardCount } : page)
    );
    if (pageEditorError) setPageEditorError("");
  };

  const updatePageEditorHideWarnings = (pageId, hideWarnings) => {
    setPageEditorDraft((pages) =>
      pages.map((page) => page.pageId === pageId ? { ...page, hideWarnings } : page)
    );
    if (pageEditorError) setPageEditorError("");
  };

  const updatePageEditorType = (pageId, pageType) => {
    setPageEditorDraft((pages) =>
      pages.map((page) => page.pageId === pageId ? { ...page, pageType: normalizeBoardPageType(pageType) } : page)
    );
    if (pageEditorError) setPageEditorError("");
  };

  const movePageEditorRow = (dragPageId, targetPageId) => {
    if (!dragPageId || !targetPageId || dragPageId === targetPageId) return;
    setPageEditorDraft((pages) => {
      const currentIndex = pages.findIndex((page) => page.pageId === dragPageId);
      const nextIndex = pages.findIndex((page) => page.pageId === targetPageId);
      if (currentIndex < 0 || nextIndex < 0 || currentIndex === nextIndex) return pages;
      const nextPages = [...pages];
      const [movedPage] = nextPages.splice(currentIndex, 1);
      nextPages.splice(nextIndex, 0, movedPage);
      return nextPages;
    });
    if (pageEditorError) setPageEditorError("");
  };

  const deletePageEditorRow = (pageId) => {
    const page = pageEditorDraft.find((draftPage) => draftPage.pageId === pageId);
    if (!page) return;
    if (pageEditorDraft.length <= 1) {
      setPageEditorError("At least one page is required.");
      return;
    }

    const pageName = cleanText(page.pageName) || "this page";
    const confirmed = window.confirm(`Are you sure you want to delete "${pageName}"?`);
    if (!confirmed) return;

    const currentPages = mergeCurrentPage(boardPlayers, draftPlayers, boardLabels, boardDetails);
    const pagesById = currentPages.reduce((pages, sourcePage) => {
      pages[sourcePage.pageId] = sourcePage;
      return pages;
    }, {});
    const nextDraft = pageEditorDraft.filter((draftPage) => draftPage.pageId !== pageId);
    const nextPages = nextDraft
      .map((draftPage) => {
        const sourcePage = pagesById[draftPage.pageId];
        if (!sourcePage) return null;
        return {
          ...sourcePage,
          pageName: cleanText(draftPage.pageName) || sourcePage.pageName,
          pageType: normalizeBoardPageType(draftPage.pageType),
          includeInBoardCount: draftPage.includeInBoardCount !== false,
          hideWarnings: draftPage.hideWarnings === true,
        };
      })
      .filter(Boolean);
    const nextActivePage = nextPages.find((nextPage) => nextPage.pageId === activePageId) || nextPages[0];
    if (!nextActivePage) {
      setPageEditorError("At least one page is required.");
      return;
    }

    setPageEditorDraft(nextDraft);
    setBoardPages(nextPages);
    setActivePageId(nextActivePage.pageId);
    setBoardPlayers(nextActivePage.players);
    setBoardLabels(nextActivePage.labels);
    setBoardDetails(nextActivePage.details);
    setBoardOverlays(nextActivePage.overlays);
    setActivePresetDraft(nextActivePage.presetDraft || null);
    setPageEditorError("");
    saveBoardLocal(nextActivePage.players, {}, draftPlayers, nextActivePage.labels, nextActivePage.details, nextActivePage.overlays, {
      pages: nextPages,
      activePageId: nextActivePage.pageId,
    });

    if (savedBoardId && currentBoardCanEdit) {
      saveBoardToDatabase(nextActivePage.players, {
        draftPlayers,
        labels: nextActivePage.labels,
        details: nextActivePage.details,
        overlays: nextActivePage.overlays,
        pages: nextPages,
        activePageId: nextActivePage.pageId,
      });
    }
  };

  const savePageEditor = () => {
    const trimmedDraft = pageEditorDraft.map((page) => ({
      ...page,
      pageName: cleanText(page.pageName),
    }));

    if (trimmedDraft.some((page) => !page.pageName)) {
      setPageEditorError("Every page needs a name.");
      return;
    }

    const names = trimmedDraft.map((page) => page.pageName.toLowerCase());
    if (new Set(names).size !== names.length) {
      setPageEditorError("Page names must be unique.");
      return;
    }

    const currentPages = mergeCurrentPage(boardPlayers, draftPlayers, boardLabels, boardDetails);
    const pagesById = currentPages.reduce((pages, page) => {
      pages[page.pageId] = page;
      return pages;
    }, {});
    const nextPages = trimmedDraft
      .map((draftPage) => {
        const sourcePage = pagesById[draftPage.pageId];
        return sourcePage ? {
          ...sourcePage,
          pageName: draftPage.pageName,
          pageType: normalizeBoardPageType(draftPage.pageType),
          includeInBoardCount: draftPage.includeInBoardCount !== false,
          hideWarnings: draftPage.hideWarnings === true,
        } : null;
      })
      .filter(Boolean);
    const nextActivePage = nextPages.find((page) => page.pageId === activePageId) || nextPages[0] || buildBoardPage();

    setBoardPages(nextPages);
    setActivePageId(nextActivePage.pageId);
    setBoardPlayers(nextActivePage.players);
    setBoardLabels(nextActivePage.labels);
    setBoardDetails(nextActivePage.details);
    setBoardOverlays(nextActivePage.overlays);
    setActivePresetDraft(nextActivePage.presetDraft || null);
    setPageEditorOpen(false);
    setPageEditorError("");
    saveBoardLocal(nextActivePage.players, {}, draftPlayers, nextActivePage.labels, nextActivePage.details, nextActivePage.overlays, {
      pages: nextPages,
      activePageId: nextActivePage.pageId,
    });

    if (savedBoardId && currentBoardCanEdit) {
      saveBoardToDatabase(nextActivePage.players, {
        draftPlayers,
        labels: nextActivePage.labels,
        details: nextActivePage.details,
        overlays: nextActivePage.overlays,
        pages: nextPages,
        activePageId: nextActivePage.pageId,
      });
    }
  };

  const loadSavedBoard = (board) => {
    const loadedPages = normalizeBoardPages(board?.boardData || {});
    const nextActivePageId = board?.boardData?.activePageId || loadedPages[0]?.pageId || "";
    const loadedPage = loadedPages.find((page) => page.pageId === nextActivePageId) || loadedPages[0] || buildBoardPage();
    const players = loadedPage.players;
    const loadedDraftPlayers = getBoardLevelDraftPlayers(board?.boardData || {}, loadedPages);
    const loadedLabels = loadedPage.labels;
    const loadedDetails = loadedPage.details;
    const nextBoardId = board?.boardId || null;
    const nextBoardName = board?.boardName || "Saved Board";
    const nextIsOwner = board?.isOwner !== false;
    const nextCanEdit = board?.canEdit !== false;
    const nextShareCanEdit = isTruthyFlag(board?.shareCanEdit);
    const nextShareCount = Number.parseInt(board?.shareCount || 0, 10) || 0;
    const nextCreatedAt = getBoardCreatedAt(board);
    const nextCreatedBy = getBoardCreatedBy(board);
    const nextUpdatedAt = getBoardUpdatedAt(board);
    const nextUpdatedBy = getBoardUpdatedBy(board);
    const nextBoardVersion = getBoardVersion(board);
    const nextPositionMarkerColor = getBoardPositionMarkerColor(board?.boardData || {});
    const nextBaseTeamLeague = getBoardBaseTeamLeague(board?.boardData || {}, user?.team || "");
    const nextBaseTeamCode = getBoardBaseTeamCode(board?.boardData || {}, user?.team || "");
    const nextNumberDisplayType = getBoardNumberDisplayType(board?.boardData || {});
    const nextPositionDisplayType = getBoardPositionDisplayType(board?.boardData || {});

    setSavedBoardId(nextBoardId);
    setBoardName(nextBoardName);
    setCurrentBoardCreatedAt(nextCreatedAt);
    setCurrentBoardCreatedBy(nextCreatedBy);
    setCurrentBoardUpdatedAt(nextUpdatedAt);
    setCurrentBoardUpdatedBy(nextUpdatedBy);
    setCurrentBoardVersion(nextBoardVersion);
    setCurrentBoardIsOwner(nextIsOwner);
    setCurrentBoardCanEdit(nextCanEdit);
    setShareCanEdit(nextShareCanEdit);
    setCurrentBoardShareCount(nextShareCount);
    setBoardPages(loadedPages);
    setActivePageId(loadedPage.pageId);
    setBoardPlayers(players);
    setBoardLabels(loadedLabels);
    setBoardDetails(loadedDetails);
    setBoardOverlays(loadedPage.overlays);
    setActivePresetDraft(loadedPage.presetDraft || null);
    setDraftPlayers(loadedDraftPlayers);
    setBoardPositionMarkerColor(nextPositionMarkerColor);
    setBoardBaseTeamLeague(nextBaseTeamLeague);
    setBoardBaseTeamCode(nextBaseTeamCode);
    setBoardNumberDisplayType(nextNumberDisplayType);
    setBoardPositionDisplayType(nextPositionDisplayType);
    setHighlightedFBXID("");
    setNotePlayer(null);
    setPlayerNoteMode("edit");
    setPlayerNoteText("");
    setPlayerGrade("");
    setPlayerNoteMessage("");
    setShowWelcomeBoardPrompt(false);
    setWelcomeBoardName("");
    setWelcomeBoardNameError("");
    saveBoardLocal(players, {
      boardId: nextBoardId,
      boardName: nextBoardName,
      isOwner: nextIsOwner,
      canEdit: nextCanEdit,
      shareCanEdit: nextShareCanEdit,
      shareCount: nextShareCount,
      createdAt: nextCreatedAt,
      createdBy: nextCreatedBy,
      updatedAt: nextUpdatedAt,
      updatedBy: nextUpdatedBy,
      boardVersion: nextBoardVersion,
      positionMarkerColor: nextPositionMarkerColor,
      baseTeamLeague: nextBaseTeamLeague,
      baseTeamCode: nextBaseTeamCode,
      numberDisplayType: nextNumberDisplayType,
      positionDisplayType: nextPositionDisplayType,
    }, loadedDraftPlayers, loadedLabels, loadedDetails, loadedPage.overlays, { pages: loadedPages, activePageId: loadedPage.pageId });
    setSaveMessage("Board loaded");
    setBoardsOpen(false);
  };

  const refreshCurrentBoard = useCallback(async (options = {}) => {
    if (!savedBoardId || !user?.team || !user?.scout) return null;
    if (Date.now() < activeLabelEditUntilRef.current) return null;

    const localBoard = {
      boardVersion: currentBoardVersion,
      updatedAt: currentBoardUpdatedAt,
    };

    try {
      const response = await fetch(DRAFT_BOARD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "getBoards",
          team: user.team,
          scoutId: user.scout,
          security: user.userContactLevel || user.security || "",
        }),
      });
      const data = await response.json();
      if (!data?.success) return data;

      const databaseBoards = Array.isArray(data.boards) ? data.boards : [];
      setSavedBoards(databaseBoards);
      const databaseBoard = databaseBoards.find((board) => String(board?.boardId || "") === String(savedBoardId));
      if (!databaseBoard || !isDatabaseBoardNewer(databaseBoard, localBoard)) return data;

      const loadedPages = normalizeBoardPages(databaseBoard?.boardData || {});
      const nextActivePageId = databaseBoard?.boardData?.activePageId || loadedPages[0]?.pageId || "";
      const loadedPage = loadedPages.find((page) => page.pageId === nextActivePageId) || loadedPages[0] || buildBoardPage();
      const loadedDraftPlayers = getBoardLevelDraftPlayers(databaseBoard?.boardData || {}, loadedPages);
      const nextCreatedAt = getBoardCreatedAt(databaseBoard);
      const nextCreatedBy = getBoardCreatedBy(databaseBoard);
      const nextUpdatedAt = getBoardUpdatedAt(databaseBoard);
      const nextUpdatedBy = getBoardUpdatedBy(databaseBoard);
      const nextBoardVersion = getBoardVersion(databaseBoard);
      const nextShareCount = Number.parseInt(databaseBoard?.shareCount || 0, 10) || 0;
      const nextShareCanEdit = isTruthyFlag(databaseBoard?.shareCanEdit);
      const nextPositionMarkerColor = getBoardPositionMarkerColor(databaseBoard?.boardData || {});
      const nextBaseTeamLeague = getBoardBaseTeamLeague(databaseBoard?.boardData || {}, user?.team || "");
      const nextBaseTeamCode = getBoardBaseTeamCode(databaseBoard?.boardData || {}, user?.team || "");
      const nextNumberDisplayType = getBoardNumberDisplayType(databaseBoard?.boardData || {});
      const nextPositionDisplayType = getBoardPositionDisplayType(databaseBoard?.boardData || {});

      setSavedBoardId(databaseBoard.boardId || null);
      setBoardName(databaseBoard.boardName || "Saved Board");
      setCurrentBoardCreatedAt(nextCreatedAt);
      setCurrentBoardCreatedBy(nextCreatedBy);
      setCurrentBoardUpdatedAt(nextUpdatedAt);
      setCurrentBoardUpdatedBy(nextUpdatedBy);
      setCurrentBoardVersion(nextBoardVersion);
      setCurrentBoardIsOwner(databaseBoard?.isOwner !== false);
      setCurrentBoardCanEdit(databaseBoard?.canEdit !== false);
      setShareCanEdit(nextShareCanEdit);
      setCurrentBoardShareCount(nextShareCount);
      setBoardPages(loadedPages);
      setActivePageId(loadedPage.pageId);
      setBoardPlayers(loadedPage.players);
      setBoardLabels(loadedPage.labels);
      setBoardDetails(loadedPage.details);
      setBoardOverlays(loadedPage.overlays);
      setActivePresetDraft(loadedPage.presetDraft || null);
      setDraftPlayers(loadedDraftPlayers);
      setBoardPositionMarkerColor(nextPositionMarkerColor);
      setBoardBaseTeamLeague(nextBaseTeamLeague);
      setBoardBaseTeamCode(nextBaseTeamCode);
      setBoardNumberDisplayType(nextNumberDisplayType);
      setBoardPositionDisplayType(nextPositionDisplayType);
      saveBoardLocal(loadedPage.players, {
        boardId: databaseBoard.boardId || null,
        boardName: databaseBoard.boardName || "Saved Board",
        isOwner: databaseBoard?.isOwner !== false,
        canEdit: databaseBoard?.canEdit !== false,
        shareCanEdit: nextShareCanEdit,
        shareCount: nextShareCount,
        createdAt: nextCreatedAt,
        createdBy: nextCreatedBy,
        updatedAt: nextUpdatedAt,
        updatedBy: nextUpdatedBy,
        boardVersion: nextBoardVersion,
        positionMarkerColor: nextPositionMarkerColor,
        baseTeamLeague: nextBaseTeamLeague,
        baseTeamCode: nextBaseTeamCode,
        numberDisplayType: nextNumberDisplayType,
        positionDisplayType: nextPositionDisplayType,
      }, loadedDraftPlayers, loadedPage.labels, loadedPage.details, loadedPage.overlays, { pages: loadedPages, activePageId: loadedPage.pageId });
      setSaveMessage(options.conflict
        ? "Not saved. Board updated since you last saw it; refreshed so you can try again."
        : "Board refreshed with latest shared changes");
      return { success: true, refreshed: true, board: databaseBoard };
    } catch {
      return { success: false };
    }
  }, [currentBoardUpdatedAt, currentBoardVersion, saveBoardLocal, savedBoardId, user]);

  useEffect(() => {
    if (!savedBoardId || !user?.team || !user?.scout) return undefined;
    const shouldPollCurrentBoard = currentBoardIsOwner === false || currentBoardShareCount > 0;
    if (!shouldPollCurrentBoard) return undefined;

    const refreshMs = currentBoardCanEdit ? DRAFT_BOARD_EDITABLE_REFRESH_MS : DRAFT_BOARD_READONLY_REFRESH_MS;
    const intervalId = window.setInterval(() => {
      refreshCurrentBoard();
    }, refreshMs);

    return () => window.clearInterval(intervalId);
  }, [currentBoardCanEdit, currentBoardIsOwner, currentBoardShareCount, refreshCurrentBoard, savedBoardId, user]);

  const resetToBoardChooser = useCallback(() => {
    const emptyPlayers = [];
    const emptyDraftPlayers = [];
    const emptyLabels = [];
    const emptyDetails = [];
    const emptyOverlays = [];
    const emptyPage = buildBoardPage(DEFAULT_BOARD_PAGE_NAME, {
      players: emptyPlayers,
      labels: emptyLabels,
      details: emptyDetails,
      overlays: emptyOverlays,
    });

    setSavedBoardId(null);
    setCurrentBoardCreatedAt("");
    setCurrentBoardCreatedBy("");
    setCurrentBoardUpdatedAt("");
    setCurrentBoardUpdatedBy("");
    setCurrentBoardVersion(0);
    setCurrentBoardIsOwner(true);
    setCurrentBoardCanEdit(true);
    setShareCanEdit(false);
    setCurrentBoardShareCount(0);
    setBoardName(BOARD_CHOOSER_NAME);
    setBoardPages([emptyPage]);
    setActivePageId(emptyPage.pageId);
    setBoardPlayers(emptyPlayers);
    setBoardLabels(emptyLabels);
    setBoardDetails(emptyDetails);
    setBoardOverlays(emptyOverlays);
    setBoardPositionMarkerColor(DEFAULT_WHITE_BOARD_POSITION_MARKER_COLOR);
    setBoardBaseTeamLeague(getBoardBaseTeamLeague({}, user?.team || ""));
    setBoardBaseTeamCode(getBoardBaseTeamCode({}, user?.team || ""));
    setBoardNumberDisplayType("pro");
    setBoardPositionDisplayType("pro");
    setActivePresetDraft(null);
    setDraftPlayers(emptyDraftPlayers);
    setHighlightedFBXID("");
    setNotePlayer(null);
    setPlayerNoteMode("edit");
    setPlayerNoteText("");
    setPlayerGrade("");
    setPlayerNoteMessage("");
    setIsEditingBoardName(false);
    setEditBoardName("");
    setEditBoardNameError("");
    setShowNewBoardForm(false);
    setShowShareBoardForm(false);
    setShowNewPageForm(false);
    setSaveBoardName("");
    setSaveBoardNameError("");
    setNewPageName("");
    setNewPageType(BOARD_PAGE_TYPES.regular);
    setNewPageNameError("");
    setBoardsOpen(false);
    setShowWelcomeBoardPrompt(true);
    setWelcomeBoardName("");
    setWelcomeBoardNameError("");
    localStorage.removeItem(DRAFT_BOARD_LOCAL_KEY);
    setSaveMessage("Choose a saved board or create a new board.");
  }, [user?.team]);

  const deleteSavedBoard = useCallback(async (targetBoard = null, event = null) => {
    event?.stopPropagation?.();
    const targetBoardId = targetBoard?.boardId || savedBoardId;
    const targetBoardName = targetBoard?.boardName || boardName;
    const isDeletingCurrentBoard = String(targetBoardId || "") === String(savedBoardId || "");
    const isOwnedBoard = targetBoard ? targetBoard?.isOwner !== false : currentBoardIsOwner;

    if (!targetBoardId || !isOwnedBoard || !user?.team || !user?.scout) return;
    const confirmed = window.confirm(`Delete "${targetBoardName}"? This cannot be undone.`);
    if (!confirmed) return;

    try {
      const response = await fetch(DRAFT_BOARD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "deleteBoard",
          boardId: targetBoardId,
          team: user.team,
          scoutId: user.scout,
        }),
      });
      const data = await response.json();
      if (data?.success) {
        if (isDeletingCurrentBoard) resetToBoardChooser();
        setSavedBoards((current) => current.filter((board) => String(board?.boardId || "") !== String(targetBoardId)));
        await fetchSavedBoards();
        setSaveMessage(isDeletingCurrentBoard ? "Board deleted. Choose a saved board or create a new board." : "Board deleted");
      } else {
        setSaveMessage(data?.message || "Unable to delete board");
      }
    } catch {
      setSaveMessage("Unable to delete board");
    }
  }, [boardName, currentBoardIsOwner, fetchSavedBoards, resetToBoardChooser, savedBoardId, user]);

  const copySavedBoard = useCallback(async (targetBoard, event = null) => {
    event?.stopPropagation?.();
    if (!targetBoard?.boardData || !user?.team || !user?.scout) return;

    const sourceName = targetBoard?.boardName || "Saved Board";
    const enteredName = window.prompt("Copy board as", `Copy of ${sourceName}`);
    if (enteredName === null) return;

    const trimmedName = enteredName.trim();
    if (!trimmedName) {
      setSaveMessage("Enter a name to copy the board");
      return;
    }

    const sourceData = targetBoard.boardData || {};
    const copiedPages = cloneBoardValue(normalizeBoardPages(sourceData));
    const copiedActivePageId = sourceData.activePageId || copiedPages[0]?.pageId || "";
    const activeCopiedPage = copiedPages.find((page) => page.pageId === copiedActivePageId) || copiedPages[0] || buildBoardPage();
    const copiedDraftPlayers = cloneBoardValue(getBoardLevelDraftPlayers(sourceData, copiedPages));

    const result = await saveBoardToDatabase(activeCopiedPage.players, {
      boardId: null,
      forceNew: true,
      boardName: trimmedName,
      draftPlayers: copiedDraftPlayers,
      labels: activeCopiedPage.labels,
      details: activeCopiedPage.details,
      overlays: activeCopiedPage.overlays,
      pages: copiedPages,
      activePageId: activeCopiedPage.pageId,
    });

    if (result?.success) {
      await fetchSavedBoards();
      setBoardsOpen(false);
      setSaveMessage("Board copied");
    } else if (result?.message) {
      setSaveMessage(result.message);
    }
  }, [fetchSavedBoards, saveBoardToDatabase, user]);

  const renderBoardRow = (board) => {
    const isActiveBoard = String(board.boardId) === String(savedBoardId || "");
    const canDeleteBoard = board?.isOwner !== false;
    const sharedByName = board?.isOwner === false ? getCompactScoutName(board?.scoutName) : "";
    const isSharedOwnedBoard = board?.isOwner !== false && (Number.parseInt(board?.shareCount || 0, 10) || 0) > 0;
    const boardTitle = sharedByName ? `${board.boardName} [${sharedByName}]` : board.boardName;

    return (
      <div
        key={board.boardId}
        className={`draft-board-board-row ${isActiveBoard ? "draft-board-board-row-active" : ""}`}
        title={boardTitle}
      >
        <button
          type="button"
          className="draft-board-board-load-button"
          onClick={() => loadSavedBoard(board)}
        >
          <span className="draft-board-board-text">
            <span className={`draft-board-board-name ${isSharedOwnedBoard ? "draft-board-board-name-shared" : ""}`}>
              {board.boardName}
              {sharedByName && <span className="draft-board-board-owner"> [{sharedByName}]</span>}
            </span>
          </span>
          <span className="draft-board-board-count">
            {getBoardDataPlayerCount(board?.boardData)}
          </span>
        </button>
        <button
          type="button"
          className="draft-board-board-copy-button"
          onClick={(event) => copySavedBoard(board, event)}
          title={`Copy ${board.boardName}`}
          aria-label={`Copy ${board.boardName}`}
        >
          Copy
        </button>
        {canDeleteBoard && (
          <button
            type="button"
            className="draft-board-board-delete-button"
            onClick={(event) => deleteSavedBoard(board, event)}
            title={`Delete ${board.boardName}`}
            aria-label={`Delete ${board.boardName}`}
          >
            <img src={deleteIcon} alt="" />
          </button>
        )}
      </div>
    );
  };

  const createEmptyNamedBoard = async (trimmedName) => {
    const emptyPlayers = [];
    const emptyDraftPlayers = [];
    const emptyLabels = [];
    const emptyDetails = [];
    const emptyOverlays = [];
    const emptyPage = buildBoardPage(DEFAULT_BOARD_PAGE_NAME, {
      players: emptyPlayers,
      labels: emptyLabels,
      details: emptyDetails,
      overlays: emptyOverlays,
    });
    const result = await saveBoardToDatabase(emptyPlayers, {
      boardId: null,
      boardName: trimmedName,
      forceNew: true,
      draftPlayers: emptyDraftPlayers,
      labels: emptyLabels,
      details: emptyDetails,
      overlays: emptyOverlays,
      pages: [emptyPage],
      activePageId: emptyPage.pageId,
    });

    if (result?.success) {
      setShowWelcomeBoardPrompt(false);
      setWelcomeBoardName("");
      setWelcomeBoardNameError("");
      setShowNewBoardForm(false);
      setSaveBoardName("");
      setBoardsOpen(false);
      await fetchSavedBoards();
    }

    return result;
  };

  const handleNamedBoardSave = async () => {
    const trimmedName = saveBoardName.trim();

    if (trimmedName.length < 10) {
      setSaveBoardNameError("Board name must be at least 10 characters.");
      return;
    }

    setSaveBoardNameError("");
    await createEmptyNamedBoard(trimmedName);
  };

  const handleWelcomeBoardCreate = async () => {
    const trimmedName = welcomeBoardName.trim();

    if (trimmedName.length < 10) {
      setWelcomeBoardNameError("Board name must be at least 10 characters.");
      return;
    }

    setWelcomeBoardNameError("");
    await createEmptyNamedBoard(trimmedName);
  };

  const startBoardNameEdit = () => {
    if (savedBoardId && !currentBoardIsOwner) return;
    setEditBoardName(boardName === BOARD_CHOOSER_NAME ? "" : boardName);
    setEditBoardNameError("");
    setIsEditingBoardName(true);
  };

  const cancelBoardNameEdit = () => {
    setEditBoardName("");
    setEditBoardNameError("");
    setIsEditingBoardName(false);
  };

  const saveBoardNameEdit = async () => {
    const trimmedName = editBoardName.trim();
    let savedUpdatedAt = currentBoardUpdatedAt;
    let savedUpdatedBy = currentBoardUpdatedBy;
    let savedBoardVersion = currentBoardVersion;

    if (trimmedName.length < 10) {
      setEditBoardNameError("Minimum 10 characters.");
      return;
    }

    if (savedBoardId && currentBoardIsOwner) {
      const result = await renameSavedBoard(trimmedName);

      if (!result?.success) {
        setEditBoardNameError("Unable to save name.");
        return;
      }

      const nextUpdatedAt = getBoardUpdatedAt(result) || currentBoardUpdatedAt;
      const nextUpdatedBy = getBoardUpdatedBy(result) || currentScoutName;
      const nextBoardVersion = getBoardVersion(result) || currentBoardVersion;
      savedUpdatedAt = nextUpdatedAt;
      savedUpdatedBy = nextUpdatedBy;
      savedBoardVersion = nextBoardVersion;
      setCurrentBoardUpdatedAt(nextUpdatedAt);
      setCurrentBoardUpdatedBy(nextUpdatedBy);
      setCurrentBoardVersion(nextBoardVersion);
      setSavedBoards((boards) =>
        boards.map((board) =>
          String(board.boardId) === String(savedBoardId) ? { ...board, boardName: trimmedName, updatedAt: nextUpdatedAt, updatedByScoutName: nextUpdatedBy, boardVersion: nextBoardVersion } : board
        )
      );
      await fetchSavedBoards();
    }

    setBoardName(trimmedName);
    saveBoardLocal(boardPlayers, { boardId: savedBoardId, boardName: trimmedName, updatedAt: savedUpdatedAt, updatedBy: savedUpdatedBy, boardVersion: savedBoardVersion }, draftPlayers, boardLabels, boardDetails);

    setEditBoardName("");
    setEditBoardNameError("");
    setIsEditingBoardName(false);
  };

  const saveBoardTransaction = useCallback((nextPlayers, nextDraftPlayers = draftPlayers) => {
    captureUndoSnapshot();
    const nextPages = mergeCurrentPage(nextPlayers, nextDraftPlayers, boardLabels, boardDetails);
    setBoardPages(nextPages);
    setBoardPlayers(nextPlayers);
    setDraftPlayers(nextDraftPlayers);
    saveBoardLocal(nextPlayers, {}, nextDraftPlayers, boardLabels, boardDetails, { pages: nextPages, activePageId });

    if (savedBoardId && currentBoardCanEdit) {
      saveBoardToDatabase(nextPlayers, { draftPlayers: nextDraftPlayers, labels: boardLabels, details: boardDetails, pages: nextPages, activePageId });
    } else if (savedBoardId && !currentBoardCanEdit) {
      setSaveMessage("Board is view only");
    }
  }, [activePageId, boardDetails, boardLabels, captureUndoSnapshot, currentBoardCanEdit, draftPlayers, mergeCurrentPage, saveBoardLocal, saveBoardToDatabase, savedBoardId]);

  const saveDraftTransaction = useCallback((nextDraftPlayers) => {
    captureUndoSnapshot();
    const nextPages = mergeCurrentPage(boardPlayers, nextDraftPlayers, boardLabels, boardDetails);
    setBoardPages(nextPages);
    setDraftPlayers(nextDraftPlayers);
    saveBoardLocal(boardPlayers, {}, nextDraftPlayers, boardLabels, boardDetails, { pages: nextPages, activePageId });

    if (savedBoardId && currentBoardCanEdit) {
      saveBoardToDatabase(boardPlayers, { draftPlayers: nextDraftPlayers, labels: boardLabels, details: boardDetails, pages: nextPages, activePageId });
    } else if (savedBoardId && !currentBoardCanEdit) {
      setSaveMessage("Board is view only");
    }
  }, [activePageId, boardDetails, boardLabels, boardPlayers, captureUndoSnapshot, currentBoardCanEdit, mergeCurrentPage, saveBoardLocal, saveBoardToDatabase, savedBoardId]);

  const saveLabelTransaction = useCallback((nextLabels, nextPresetDraft = activePresetDraft, options = {}) => {
    captureUndoSnapshot();
    const nextPages = mergeCurrentPage(boardPlayers, draftPlayers, nextLabels, boardDetails, {
      presetDraft: nextPresetDraft,
    });
    setBoardPages(nextPages);
    setBoardLabels(nextLabels);
    saveBoardLocal(boardPlayers, {}, draftPlayers, nextLabels, boardDetails, { pages: nextPages, activePageId });

    if (labelSaveTimerRef.current) {
      window.clearTimeout(labelSaveTimerRef.current);
      labelSaveTimerRef.current = null;
    }

    if (savedBoardId && currentBoardCanEdit) {
      const saveLabelsToDatabase = () => {
        saveBoardToDatabase(boardPlayers, { draftPlayers, labels: nextLabels, details: boardDetails, pages: nextPages, activePageId });
      };

      if (options.debounceDatabase === true) {
        activeLabelEditUntilRef.current = Date.now() + DRAFT_BOARD_LABEL_SAVE_DELAY_MS + 500;
        labelSaveTimerRef.current = window.setTimeout(() => {
          labelSaveTimerRef.current = null;
          activeLabelEditUntilRef.current = 0;
          saveLabelsToDatabase();
        }, DRAFT_BOARD_LABEL_SAVE_DELAY_MS);
      } else {
        activeLabelEditUntilRef.current = 0;
        saveLabelsToDatabase();
      }
    } else if (savedBoardId && !currentBoardCanEdit) {
      setSaveMessage("Board is view only");
    }
  }, [activePageId, activePresetDraft, boardDetails, boardPlayers, captureUndoSnapshot, currentBoardCanEdit, draftPlayers, mergeCurrentPage, saveBoardLocal, saveBoardToDatabase, savedBoardId]);

  const saveDetailTransaction = useCallback((nextDetails, options = {}) => {
    captureUndoSnapshot();
    const nextPages = mergeCurrentPage(boardPlayers, draftPlayers, boardLabels, nextDetails);
    setBoardPages(nextPages);
    setBoardDetails(nextDetails);
    saveBoardLocal(boardPlayers, {}, draftPlayers, boardLabels, nextDetails, { pages: nextPages, activePageId });

    if (detailSaveTimerRef.current) {
      window.clearTimeout(detailSaveTimerRef.current);
      detailSaveTimerRef.current = null;
    }

    if (savedBoardId && currentBoardCanEdit) {
      const saveDetailsToDatabase = () => {
        saveBoardToDatabase(boardPlayers, { draftPlayers, labels: boardLabels, details: nextDetails, pages: nextPages, activePageId });
      };

      if (options.debounceDatabase === true) {
        detailSaveTimerRef.current = window.setTimeout(() => {
          detailSaveTimerRef.current = null;
          saveDetailsToDatabase();
        }, DRAFT_BOARD_DETAIL_SAVE_DELAY_MS);
      } else {
        saveDetailsToDatabase();
      }
    } else if (savedBoardId && !currentBoardCanEdit) {
      setSaveMessage("Board is view only");
    }
  }, [activePageId, boardLabels, boardPlayers, captureUndoSnapshot, currentBoardCanEdit, draftPlayers, mergeCurrentPage, saveBoardLocal, saveBoardToDatabase, savedBoardId]);

  const saveBoardItemsTransaction = useCallback((nextPlayers, nextLabels, nextDetails, nextOverlays = boardOverlays, nextDraftPlayers = draftPlayers) => {
    captureUndoSnapshot();
    const nextPages = mergeCurrentPage(nextPlayers, nextDraftPlayers, nextLabels, nextDetails, nextOverlays);
    setBoardPages(nextPages);
    setBoardPlayers(nextPlayers);
    setBoardLabels(nextLabels);
    setBoardDetails(nextDetails);
    setBoardOverlays(nextOverlays);
    setDraftPlayers(nextDraftPlayers);
    saveBoardLocal(nextPlayers, {}, nextDraftPlayers, nextLabels, nextDetails, nextOverlays, { pages: nextPages, activePageId });

    if (savedBoardId && currentBoardCanEdit) {
      saveBoardToDatabase(nextPlayers, { draftPlayers: nextDraftPlayers, labels: nextLabels, details: nextDetails, overlays: nextOverlays, pages: nextPages, activePageId });
    } else if (savedBoardId && !currentBoardCanEdit) {
      setSaveMessage("Board is view only");
    }
  }, [activePageId, boardOverlays, captureUndoSnapshot, currentBoardCanEdit, draftPlayers, mergeCurrentPage, saveBoardLocal, saveBoardToDatabase, savedBoardId]);

  const saveOverlayTransaction = useCallback((nextOverlays) => {
    captureUndoSnapshot();
    const nextPages = mergeCurrentPage(boardPlayers, draftPlayers, boardLabels, boardDetails, nextOverlays);
    setBoardPages(nextPages);
    setBoardOverlays(nextOverlays);
    saveBoardLocal(boardPlayers, {}, draftPlayers, boardLabels, boardDetails, nextOverlays, { pages: nextPages, activePageId });

    if (savedBoardId && currentBoardCanEdit) {
      saveBoardToDatabase(boardPlayers, { draftPlayers, labels: boardLabels, details: boardDetails, overlays: nextOverlays, pages: nextPages, activePageId });
    } else if (savedBoardId && !currentBoardCanEdit) {
      setSaveMessage("Board is view only");
    }
  }, [activePageId, boardDetails, boardLabels, boardPlayers, captureUndoSnapshot, currentBoardCanEdit, draftPlayers, mergeCurrentPage, saveBoardLocal, saveBoardToDatabase, savedBoardId]);

  const undoLastBoardChange = useCallback(() => {
    if (!undoSnapshot || !canEditCurrentBoard) return;

    const restoredPlayers = cloneBoardValue(undoSnapshot.players || []);
    const restoredDraftPlayers = cloneBoardValue(undoSnapshot.draftPlayers || []);
    const restoredLabels = cloneBoardValue(undoSnapshot.labels || []);
    const restoredDetails = cloneBoardValue(undoSnapshot.details || []);
    const restoredOverlays = cloneBoardValue(undoSnapshot.overlays || []);
    const restoredPages = cloneBoardValue(undoSnapshot.pages || []);
    const restoredActivePageId = undoSnapshot.activePageId || activePageId;

    setBoardPages(restoredPages);
    setActivePageId(restoredActivePageId);
    setBoardPlayers(restoredPlayers);
    setDraftPlayers(restoredDraftPlayers);
    setBoardLabels(restoredLabels);
    setBoardDetails(restoredDetails);
    setBoardOverlays(restoredOverlays);
    setUndoSnapshot(null);
    saveBoardLocal(restoredPlayers, {}, restoredDraftPlayers, restoredLabels, restoredDetails, restoredOverlays, {
      pages: restoredPages,
      activePageId: restoredActivePageId,
    });

    if (savedBoardId && currentBoardCanEdit) {
      saveBoardToDatabase(restoredPlayers, {
        draftPlayers: restoredDraftPlayers,
        labels: restoredLabels,
        details: restoredDetails,
        overlays: restoredOverlays,
        pages: restoredPages,
        activePageId: restoredActivePageId,
      });
    }

    setSaveMessage("Undid last change");
  }, [activePageId, canEditCurrentBoard, currentBoardCanEdit, saveBoardLocal, saveBoardToDatabase, savedBoardId, undoSnapshot]);

  const moveSelectedBoardItems = useCallback((deltaX, deltaY) => {
    if (!deltaX && !deltaY) return;

    const moveItem = (item, type, idKey) => {
      if (!isBoardItemSelected(type, item?.[idKey])) return item;
      const point = clampBoardPointToPrintableArea({
        x: Number(item?.x || 0) + deltaX,
        y: Number(item?.y || 0) + deltaY,
      }, type, item);
      return {
        ...item,
        ...point,
      };
    };

    saveBoardItemsTransaction(
      boardPlayers.map((item) => moveItem(item, "player", "boardItemId")),
      boardLabels.map((item) => moveItem(item, "label", "labelItemId")),
      boardDetails.map((item) => moveItem(item, "detail", "detailItemId")),
      boardOverlays.map((item) => moveItem(item, "overlay", "overlayItemId"))
    );
  }, [boardDetails, boardLabels, boardOverlays, boardPlayers, clampBoardPointToPrintableArea, isBoardItemSelected, saveBoardItemsTransaction]);

  const getBoardItemBounds = useCallback((item, type) => {
    const overlayConfig = type === "overlay" ? getBoardOverlayConfig(item?.type) : null;
    const width = type === "player"
      ? boardCardWidth
      : type === "label"
        ? BOARD_LABEL_PRINT_WIDTH
        : type === "detail"
          ? BOARD_DETAIL_PRINT_WIDTH
          : getOverlayWidth(overlayConfig);
    const height = type === "player"
      ? boardCardHeight
      : type === "label"
        ? BOARD_LABEL_PRINT_HEIGHT
        : type === "detail"
          ? BOARD_DETAIL_PRINT_HEIGHT
          : getOverlayHeight(overlayConfig);
    const x = Number(item?.x || 0);
    const y = Number(item?.y || 0);

    return {
      x,
      y,
      width,
      height,
      right: x + width,
      bottom: y + height,
      centerX: x + (width / 2),
      centerY: y + (height / 2),
    };
  }, [boardCardHeight, boardCardWidth, getOverlayHeight, getOverlayWidth]);

  const getSelectedBoardItemSpecs = useCallback(() => {
    const specs = [];
    boardPlayers.forEach((item) => {
      if (isBoardItemSelected("player", item?.boardItemId)) specs.push({ type: "player", idKey: "boardItemId", id: item.boardItemId, item, bounds: getBoardItemBounds(item, "player") });
    });
    boardLabels.forEach((item) => {
      if (isBoardItemSelected("label", item?.labelItemId)) specs.push({ type: "label", idKey: "labelItemId", id: item.labelItemId, item, bounds: getBoardItemBounds(item, "label") });
    });
    boardDetails.forEach((item) => {
      if (isBoardItemSelected("detail", item?.detailItemId)) specs.push({ type: "detail", idKey: "detailItemId", id: item.detailItemId, item, bounds: getBoardItemBounds(item, "detail") });
    });
    boardOverlays.forEach((item) => {
      if (isBoardItemSelected("overlay", item?.overlayItemId)) specs.push({ type: "overlay", idKey: "overlayItemId", id: item.overlayItemId, item, bounds: getBoardItemBounds(item, "overlay") });
    });
    return specs;
  }, [boardDetails, boardLabels, boardOverlays, boardPlayers, getBoardItemBounds, isBoardItemSelected]);

  const applySelectedBoardItemPositions = useCallback((positionByKey) => {
    const updateItem = (item, type, idKey) => {
      const nextPosition = positionByKey[getBoardSelectionKey(type, item?.[idKey])];
      if (!nextPosition) return item;
      const minimums = getBoardItemMinimums(type);
      const point = clampBoardPointToPrintableArea({
        x: Math.max(minimums.x, Math.round(nextPosition.x)),
        y: Math.max(minimums.y, Math.round(nextPosition.y)),
      }, type, item);
      return {
        ...item,
        ...point,
      };
    };

    saveBoardItemsTransaction(
      boardPlayers.map((item) => updateItem(item, "player", "boardItemId")),
      boardLabels.map((item) => updateItem(item, "label", "labelItemId")),
      boardDetails.map((item) => updateItem(item, "detail", "detailItemId")),
      boardOverlays.map((item) => updateItem(item, "overlay", "overlayItemId"))
    );
  }, [boardDetails, boardLabels, boardOverlays, boardPlayers, clampBoardPointToPrintableArea, getBoardItemMinimums, saveBoardItemsTransaction]);

  const alignSelectedBoardItems = useCallback((mode) => {
    const specs = getSelectedBoardItemSpecs();
    if (specs.length < 2) return;

    const left = Math.min(...specs.map((spec) => spec.bounds.x));
    const right = Math.max(...specs.map((spec) => spec.bounds.right));
    const top = Math.min(...specs.map((spec) => spec.bounds.y));
    const bottom = Math.max(...specs.map((spec) => spec.bounds.bottom));
    const centerX = left + ((right - left) / 2);
    const centerY = top + ((bottom - top) / 2);
    const positionByKey = {};

    specs.forEach((spec) => {
      let nextX = spec.bounds.x;
      let nextY = spec.bounds.y;
      if (mode === "left") nextX = left;
      if (mode === "center") nextX = centerX - (spec.bounds.width / 2);
      if (mode === "right") nextX = right - spec.bounds.width;
      if (mode === "top") nextY = top;
      if (mode === "middle") nextY = centerY - (spec.bounds.height / 2);
      if (mode === "bottom") nextY = bottom - spec.bounds.height;
      positionByKey[getBoardSelectionKey(spec.type, spec.id)] = { x: nextX, y: nextY };
    });

    applySelectedBoardItemPositions(positionByKey);
    setAlignmentMenuOpen(false);
  }, [applySelectedBoardItemPositions, getSelectedBoardItemSpecs]);

  const distributeSelectedBoardItems = useCallback((axis) => {
    const specs = getSelectedBoardItemSpecs();
    if (specs.length < 3) return;

    const isHorizontal = axis === "horizontal";
    const sortedSpecs = [...specs].sort((left, right) => (
      isHorizontal
        ? left.bounds.x - right.bounds.x || left.bounds.y - right.bounds.y
        : left.bounds.y - right.bounds.y || left.bounds.x - right.bounds.x
    ));
    const first = sortedSpecs[0];
    const last = sortedSpecs[sortedSpecs.length - 1];
    const start = isHorizontal ? first.bounds.x : first.bounds.y;
    const end = isHorizontal ? last.bounds.right : last.bounds.bottom;
    const totalSize = sortedSpecs.reduce((total, spec) => total + (isHorizontal ? spec.bounds.width : spec.bounds.height), 0);
    const availableGap = (end - start - totalSize) / (sortedSpecs.length - 1);
    const positionByKey = {};

    if (availableGap >= 0) {
      let cursor = start;
      sortedSpecs.forEach((spec) => {
        positionByKey[getBoardSelectionKey(spec.type, spec.id)] = {
          x: isHorizontal ? cursor : spec.bounds.x,
          y: isHorizontal ? spec.bounds.y : cursor,
        };
        cursor += (isHorizontal ? spec.bounds.width : spec.bounds.height) + availableGap;
      });
    } else {
      const firstCenter = isHorizontal ? first.bounds.centerX : first.bounds.centerY;
      const lastCenter = isHorizontal ? last.bounds.centerX : last.bounds.centerY;
      const centerStep = (lastCenter - firstCenter) / (sortedSpecs.length - 1);
      sortedSpecs.forEach((spec, index) => {
        const center = firstCenter + (centerStep * index);
        positionByKey[getBoardSelectionKey(spec.type, spec.id)] = {
          x: isHorizontal ? center - (spec.bounds.width / 2) : spec.bounds.x,
          y: isHorizontal ? spec.bounds.y : center - (spec.bounds.height / 2),
        };
      });
    }

    applySelectedBoardItemPositions(positionByKey);
    setAlignmentMenuOpen(false);
  }, [applySelectedBoardItemPositions, getSelectedBoardItemSpecs]);

  const handleGroupMoveMouseDown = useCallback((event, type, id) => {
    if (!canEditCurrentBoard || !groupSelectOpen || selectedBoardItemCount <= 1 || !isBoardItemSelected(type, id)) return;
    if (event.button !== 0) return;

    event.preventDefault();
    event.stopPropagation();
    suppressGroupDragClickRef.current = true;

    const dragData = {
      startClientX: event.clientX,
      startClientY: event.clientY,
      latestDeltaX: 0,
      latestDeltaY: 0,
    };
    groupDragRef.current = dragData;
    setGroupDragPreviewDelta({ x: 0, y: 0 });

    const handleMouseMove = (moveEvent) => {
      if (!groupDragRef.current) return;
      const deltaX = snapDeltaToGrid(moveEvent.clientX - groupDragRef.current.startClientX);
      const deltaY = snapDeltaToGrid(moveEvent.clientY - groupDragRef.current.startClientY);
      groupDragRef.current.latestDeltaX = deltaX;
      groupDragRef.current.latestDeltaY = deltaY;
      setGroupDragPreviewDelta((current) => (
        current?.x === deltaX && current?.y === deltaY ? current : { x: deltaX, y: deltaY }
      ));
    };

    const handleMouseUp = () => {
      const deltaX = Number(groupDragRef.current?.latestDeltaX || 0);
      const deltaY = Number(groupDragRef.current?.latestDeltaY || 0);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      clearGroupDragPreview();
      moveSelectedBoardItems(deltaX, deltaY);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  }, [canEditCurrentBoard, clearGroupDragPreview, groupSelectOpen, isBoardItemSelected, moveSelectedBoardItems, selectedBoardItemCount]);

  useEffect(() => {
    clearBoardSelection();
  }, [activePageId, clearBoardSelection]);

  useEffect(() => {
    let isMounted = true;

    const loadBoard = async () => {
      const loadedUser = await getUserFromToken();
      if (!isMounted) return;
      setUser(loadedUser);
      let savedLocal = {};
      let loadedLocalBoardId = null;

      try {
        savedLocal = JSON.parse(localStorage.getItem(DRAFT_BOARD_LOCAL_KEY) || "{}");
        if (Array.isArray(savedLocal.players) || Array.isArray(savedLocal.pages)) {
          const loadedPages = normalizeBoardPages(savedLocal);
          const nextActivePageId = savedLocal.activePageId || loadedPages[0]?.pageId || "";
          const loadedPage = loadedPages.find((page) => page.pageId === nextActivePageId) || loadedPages[0] || buildBoardPage();
          const loadedDraftPlayers = getBoardLevelDraftPlayers(savedLocal, loadedPages);
          const loadedPositionMarkerColor = getBoardPositionMarkerColor(savedLocal);
          const loadedBaseTeamLeague = getBoardBaseTeamLeague(savedLocal, loadedUser?.team || "");
          const loadedBaseTeamCode = getBoardBaseTeamCode(savedLocal, loadedUser?.team || "");
          const loadedNumberDisplayType = getBoardNumberDisplayType(savedLocal);
          const loadedPositionDisplayType = getBoardPositionDisplayType(savedLocal);
          loadedLocalBoardId = savedLocal.boardId || null;
          if (savedLocal.boardId) setSavedBoardId(savedLocal.boardId);
          if (savedLocal.boardName) setBoardName(savedLocal.boardName);
          if (getBoardCreatedAt(savedLocal)) setCurrentBoardCreatedAt(getBoardCreatedAt(savedLocal));
          if (getBoardCreatedBy(savedLocal)) setCurrentBoardCreatedBy(getBoardCreatedBy(savedLocal));
          if (getBoardUpdatedAt(savedLocal)) setCurrentBoardUpdatedAt(getBoardUpdatedAt(savedLocal));
          if (getBoardUpdatedBy(savedLocal)) setCurrentBoardUpdatedBy(getBoardUpdatedBy(savedLocal));
          if (getBoardVersion(savedLocal)) setCurrentBoardVersion(getBoardVersion(savedLocal));
          if (savedLocal.isOwner !== undefined) setCurrentBoardIsOwner(savedLocal.isOwner !== false);
          if (savedLocal.canEdit !== undefined) setCurrentBoardCanEdit(savedLocal.canEdit !== false);
          if (savedLocal.shareCanEdit !== undefined) setShareCanEdit(isTruthyFlag(savedLocal.shareCanEdit));
          if (savedLocal.shareCount !== undefined) setCurrentBoardShareCount(Number.parseInt(savedLocal.shareCount || 0, 10) || 0);
          setBoardPages(loadedPages);
          setActivePageId(loadedPage.pageId);
          setBoardPlayers(loadedPage.players);
          setBoardLabels(loadedPage.labels);
          setBoardDetails(loadedPage.details);
          setBoardOverlays(loadedPage.overlays);
          setActivePresetDraft(loadedPage.presetDraft || null);
          setDraftPlayers(loadedDraftPlayers);
          setBoardPositionMarkerColor(loadedPositionMarkerColor);
          setBoardBaseTeamLeague(loadedBaseTeamLeague);
          setBoardBaseTeamCode(loadedBaseTeamCode);
          setBoardNumberDisplayType(loadedNumberDisplayType);
          setBoardPositionDisplayType(loadedPositionDisplayType);
        } else {
          setBoardBaseTeamLeague(getBoardBaseTeamLeague({}, loadedUser?.team || ""));
          setBoardBaseTeamCode(getBoardBaseTeamCode({}, loadedUser?.team || ""));
          setBoardNumberDisplayType("pro");
          setBoardPositionDisplayType("pro");
          setShowWelcomeBoardPrompt(true);
        }
      } catch {
        setBoardBaseTeamLeague(getBoardBaseTeamLeague({}, loadedUser?.team || ""));
        setBoardBaseTeamCode(getBoardBaseTeamCode({}, loadedUser?.team || ""));
        setBoardNumberDisplayType("pro");
        setBoardPositionDisplayType("pro");
        setShowWelcomeBoardPrompt(true);
      }

      if (!loadedUser?.team || !loadedUser?.scout || !loadedLocalBoardId) return;

      try {
        const response = await fetch(DRAFT_BOARD_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            scriptName: "getBoards",
            team: loadedUser.team,
            scoutId: loadedUser.scout,
            security: loadedUser.userContactLevel || loadedUser.security || "",
          }),
        });
        const data = await response.json();
        if (!isMounted || !data?.success) return;

        const databaseBoards = Array.isArray(data.boards) ? data.boards : [];
        setSavedBoards(databaseBoards);
        const databaseBoard = databaseBoards.find((board) => String(board?.boardId || "") === String(loadedLocalBoardId));
        if (!databaseBoard || !isDatabaseBoardNewer(databaseBoard, savedLocal)) return;

        const loadedPages = normalizeBoardPages(databaseBoard?.boardData || {});
        const nextActivePageId = databaseBoard?.boardData?.activePageId || loadedPages[0]?.pageId || "";
        const loadedPage = loadedPages.find((page) => page.pageId === nextActivePageId) || loadedPages[0] || buildBoardPage();
        const loadedDraftPlayers = getBoardLevelDraftPlayers(databaseBoard?.boardData || {}, loadedPages);
        const nextCreatedAt = getBoardCreatedAt(databaseBoard);
        const nextCreatedBy = getBoardCreatedBy(databaseBoard);
        const nextUpdatedAt = getBoardUpdatedAt(databaseBoard);
        const nextUpdatedBy = getBoardUpdatedBy(databaseBoard);
        const nextBoardVersion = getBoardVersion(databaseBoard);
        const nextShareCount = Number.parseInt(databaseBoard?.shareCount || 0, 10) || 0;
        const nextShareCanEdit = isTruthyFlag(databaseBoard?.shareCanEdit);
        const nextPositionMarkerColor = getBoardPositionMarkerColor(databaseBoard?.boardData || {});
        const nextBaseTeamLeague = getBoardBaseTeamLeague(databaseBoard?.boardData || {}, loadedUser?.team || "");
        const nextBaseTeamCode = getBoardBaseTeamCode(databaseBoard?.boardData || {}, loadedUser?.team || "");
        const nextNumberDisplayType = getBoardNumberDisplayType(databaseBoard?.boardData || {});
        const nextPositionDisplayType = getBoardPositionDisplayType(databaseBoard?.boardData || {});

        setSavedBoardId(databaseBoard.boardId || null);
        setBoardName(databaseBoard.boardName || "Saved Board");
        setCurrentBoardCreatedAt(nextCreatedAt);
        setCurrentBoardCreatedBy(nextCreatedBy);
        setCurrentBoardUpdatedAt(nextUpdatedAt);
        setCurrentBoardUpdatedBy(nextUpdatedBy);
        setCurrentBoardVersion(nextBoardVersion);
        setCurrentBoardIsOwner(databaseBoard?.isOwner !== false);
        setCurrentBoardCanEdit(databaseBoard?.canEdit !== false);
        setShareCanEdit(nextShareCanEdit);
        setCurrentBoardShareCount(nextShareCount);
        setBoardPages(loadedPages);
        setActivePageId(loadedPage.pageId);
        setBoardPlayers(loadedPage.players);
        setBoardLabels(loadedPage.labels);
        setBoardDetails(loadedPage.details);
        setBoardOverlays(loadedPage.overlays);
        setActivePresetDraft(loadedPage.presetDraft || null);
        setDraftPlayers(loadedDraftPlayers);
        setBoardPositionMarkerColor(nextPositionMarkerColor);
        setBoardBaseTeamLeague(nextBaseTeamLeague);
        setBoardBaseTeamCode(nextBaseTeamCode);
        setBoardNumberDisplayType(nextNumberDisplayType);
        setBoardPositionDisplayType(nextPositionDisplayType);
        saveBoardLocal(loadedPage.players, {
          boardId: databaseBoard.boardId || null,
          boardName: databaseBoard.boardName || "Saved Board",
          isOwner: databaseBoard?.isOwner !== false,
          canEdit: databaseBoard?.canEdit !== false,
          shareCanEdit: nextShareCanEdit,
          shareCount: nextShareCount,
          createdAt: nextCreatedAt,
          createdBy: nextCreatedBy,
          updatedAt: nextUpdatedAt,
          updatedBy: nextUpdatedBy,
          boardVersion: nextBoardVersion,
          positionMarkerColor: nextPositionMarkerColor,
          baseTeamLeague: nextBaseTeamLeague,
          baseTeamCode: nextBaseTeamCode,
          numberDisplayType: nextNumberDisplayType,
          positionDisplayType: nextPositionDisplayType,
        }, loadedDraftPlayers, loadedPage.labels, loadedPage.details, loadedPage.overlays, { pages: loadedPages, activePageId: loadedPage.pageId });
        setSaveMessage("Board refreshed from database");
      } catch {}

    };

    loadBoard();

    return () => {
      isMounted = false;
    };
  }, []);

  const getDropPoint = (event, dragOffset = null, type = "player") => {
    const rect = whiteboardRef.current?.getBoundingClientRect();
    if (!rect) return { x: 20, y: 20 };
    const printOriginOffsetX = type === "detail" ? 0 : getPrintOriginOffsetX(rect);
    const printOriginOffsetY = type === "detail" ? 0 : getPrintOriginOffsetY(rect);
    const offsetX = Number.isFinite(Number(dragOffset?.x))
      ? Number(dragOffset.x)
      : ((boardCardWidth + BOARD_REMOVE_RAIL_WIDTH) / 2);
    const offsetY = Number.isFinite(Number(dragOffset?.y)) ? Number(dragOffset.y) : 24;
    const { x: minX, y: minY } = getBoardItemMinimums(type);
    const itemInset = type === "player" ? BOARD_REMOVE_RAIL_WIDTH : 0;
    return clampBoardPointToPrintableArea({
      x: Math.max(minX, snapToGrid(event.clientX - rect.left - printOriginOffsetX - offsetX) + itemInset),
      y: Math.max(minY, snapToGrid(event.clientY - rect.top - printOriginOffsetY - offsetY)),
    }, type);
  };

  const getSnappedHeadingRowY = (y, ignoredLabelItemId = "") => {
    const numericY = Number(y);
    if (!Number.isFinite(numericY)) return y;

    const nearestLabel = boardLabels
      .filter((label) => label?.labelItemId !== ignoredLabelItemId)
      .map((label) => {
        const labelY = Number(label?.y);
        return Number.isFinite(labelY)
          ? { y: labelY, distance: Math.abs(labelY - numericY) }
          : null;
      })
      .filter(Boolean)
      .sort((left, right) => left.distance - right.distance)[0];

    return nearestLabel && nearestLabel.distance <= BOARD_LABEL_ROW_SNAP_DISTANCE
      ? nearestLabel.y
      : numericY;
  };

  const getSnappedHeadingColumnX = (x, ignoredLabelItemId = "") => {
    const numericX = Number(x);
    if (!Number.isFinite(numericX)) return x;

    const nearestLabel = boardLabels
      .filter((label) => label?.labelItemId !== ignoredLabelItemId)
      .map((label) => {
        const labelX = Number(label?.x);
        return Number.isFinite(labelX)
          ? { x: labelX, distance: Math.abs(labelX - numericX) }
          : null;
      })
      .filter(Boolean)
      .sort((left, right) => left.distance - right.distance)[0];

    return nearestLabel && nearestLabel.distance <= BOARD_LABEL_COLUMN_SNAP_DISTANCE
      ? nearestLabel.x
      : numericX;
  };

  const getHeadingDropPoint = (event, dragOffset, labelItemId = "") => {
    const point = getDropPoint(event, dragOffset, "label");
    return clampBoardPointToPrintableArea({
      x: getSnappedHeadingColumnX(point.x, labelItemId),
      y: getSnappedHeadingRowY(point.y, labelItemId),
    }, "label");
  };

  const getHeadingPlayerX = (label) => Number(label?.x || 0);

  const headingsShareColumn = (leftX, rightX) =>
    leftX < rightX + BOARD_LABEL_PRINT_WIDTH && rightX < leftX + BOARD_LABEL_PRINT_WIDTH;

  const getHeadingBottomY = (headingLabel) => {
    const headingX = getHeadingPlayerX(headingLabel);
    const headingY = Number(headingLabel?.y || 0);
    const nextHeadingY = boardLabels
      .filter((label) => {
        if (label?.labelItemId === headingLabel?.labelItemId) return false;
        const labelY = Number(label?.y || 0);
        return labelY > headingY && headingsShareColumn(headingX, getHeadingPlayerX(label));
      })
      .map((label) => Number(label?.y || 0))
      .sort((left, right) => left - right)[0];

    return Number.isFinite(nextHeadingY) ? nextHeadingY : Infinity;
  };

  const getHeadingForPoint = (point) => {
    if (!isPlayerPointInPrintArea(point)) return null;

    const playerCenterX = Number(point?.x || 0) + (boardCardWidth / 2);
    const playerTopY = Number(point?.y || 0);
    const playerBottomY = playerTopY + boardCardHeight;

    return boardLabels
      .map((label, index) => {
        const headingX = getHeadingPlayerX(label);
        const headingY = Number(label?.y || 0);
        const headingBottomY = getHeadingBottomY(label);
        const inColumn = playerCenterX >= headingX && playerCenterX <= headingX + BOARD_LABEL_PRINT_WIDTH;
        const touchesHeadingGroup = playerBottomY >= headingY && playerTopY < headingBottomY;
        const insideHeadingGroup = playerTopY < headingBottomY;
        if (!inColumn || !touchesHeadingGroup || !insideHeadingGroup) return null;
        return {
          label,
          index,
          x: headingX,
          y: headingY,
          bottomY: headingBottomY,
          distanceY: Math.abs(playerTopY - headingY),
        };
      })
      .filter(Boolean)
      .sort((left, right) => left.distanceY - right.distanceY || left.index - right.index)[0] || null;
  };

  const getPlayerBelongsToHeading = (player, heading, activePlayerId = "") => {
    if (!heading || player?.boardItemId === activePlayerId) return false;
    if (!isPlayerPointInPrintArea(player)) return false;
    const playerX = Number(player?.x || 0);
    const playerY = Number(player?.y || 0);
    return Math.abs(playerX - heading.x) <= BOARD_GRID_SIZE &&
      playerY >= heading.y + BOARD_LABEL_PRINT_HEIGHT &&
      playerY < heading.bottomY;
  };

  const placeActivePlayerInStackLane = (players, activePlayerId, x, minY, maxY, options = {}) => {
    const direction = options.direction === "up" ? "up" : "down";
    const stackStepY = options.stackStepY || (boardCardHeight + BOARD_HEADING_STACK_GAP);
    const sameLanePlayers = (Array.isArray(options.sameLanePlayers) ? options.sameLanePlayers : [])
      .filter((player) => player?.boardItemId !== activePlayerId);
    const slotCandidates = [];

    if (direction === "up") {
      for (let slotY = maxY; slotY >= minY; slotY -= stackStepY) {
        if (!isStackTopInBounds(slotY, minY, maxY)) continue;
        slotCandidates.push(slotY);
      }
    } else {
      for (let slotY = minY; slotY <= maxY; slotY += stackStepY) {
        if (!isStackTopInBounds(slotY, minY, maxY)) continue;
        slotCandidates.push(slotY);
      }
    }

    if (slotCandidates.length === 0) return players;

    const activePlayer = players.find((player) => player?.boardItemId === activePlayerId);
    if (!activePlayer) return players;

    const getNearestSlotIndex = (top) => {
      const numericTop = Number(top);
      if (!Number.isFinite(numericTop)) return 0;
      return slotCandidates
        .map((slotY, index) => ({
          index,
          distance: Math.abs(slotY - numericTop),
        }))
        .sort((left, right) => left.distance - right.distance || left.index - right.index)[0]?.index || 0;
    };
    const orderedLanePlayers = sameLanePlayers
      .map((player, index) => ({
        player,
        index,
        slotIndex: getNearestSlotIndex(player?.y),
      }))
      .sort((left, right) => left.slotIndex - right.slotIndex || left.index - right.index)
      .map(({ player }) => player);
    const activeSlotIndex = getNearestSlotIndex(options.activeY ?? activePlayer.y);
    const insertionIndex = Math.min(activeSlotIndex, orderedLanePlayers.length);
    const orderedPlayers = [...orderedLanePlayers];
    orderedPlayers.splice(insertionIndex, 0, activePlayer);

    if (slotCandidates.length < orderedPlayers.length) return players;

    const positionById = orderedPlayers.reduce((positions, player, index) => {
      const point = getStackPointInPrintArea({
        x,
        y: slotCandidates[index],
      });
      if (point && player?.boardItemId) {
        positions[player.boardItemId] = point;
      }
      return positions;
    }, {});

    if (!positionById[activePlayerId]) return players;

    return players.map((player) => (
      positionById[player?.boardItemId]
        ? {
          ...player,
          ...positionById[player.boardItemId],
        }
        : player
    ));
  };

  const applyHeadingStackLayout = (players, activePlayerId, point) => {
    const heading = getHeadingForPoint(point);
    if (!heading) return players;

    const stackStartY = heading.y + BOARD_LABEL_PRINT_HEIGHT + BOARD_HEADING_STACK_GAP;
    const stackStepY = boardCardHeight + BOARD_HEADING_STACK_GAP;
    const stackMaxY = Math.min(
      Number.isFinite(heading.bottomY)
        ? heading.bottomY - BOARD_HEADING_STACK_GAP - boardCardHeight
        : BOARD_PRINT_PAGE_HEIGHT - BOARD_GRID_SIZE - boardCardHeight,
      BOARD_PRINT_PAGE_HEIGHT - boardCardHeight
    );
    const sameLanePlayers = players.filter((player) =>
      player?.boardItemId !== activePlayerId && getPlayerBelongsToHeading(player, heading, activePlayerId)
    );

    return placeActivePlayerInStackLane(players, activePlayerId, heading.x, stackStartY, stackMaxY, {
      sameLanePlayers,
      stackStepY,
    });
  };

  const applySplitSnapLayout = (players, activePlayerId, point) => {
    if (!isSplitSortPage) return players;
    if (!isPlayerPointInPrintArea(point)) return players;

    const droppedX = Number(point?.x || 0);
    const activeY = Number(point?.y || 0);
    if (!Number.isFinite(droppedX) || !Number.isFinite(activeY)) return players;

    const zone = activeY < BOARD_SPLIT_SORT_Y ? "top" : "bottom";
    const stackStepY = boardCardHeight + BOARD_HEADING_STACK_GAP;
    const splitHeaderSnapDistance = BOARD_LABEL_PRINT_WIDTH / 2;
    const headersShareSplitLane = (left, right) => Math.abs(Number(left?.x || 0) - Number(right?.x || 0)) <= BOARD_GRID_SIZE;
    const zoneHeaders = boardLabels
      .filter((label) => {
        const labelY = Number(label?.y || 0);
        if (!Number.isFinite(labelY)) return false;
        return zone === "top" ? labelY < BOARD_SPLIT_SORT_Y : labelY >= BOARD_SPLIT_SORT_Y;
      })
      .map((label) => {
        const labelX = getHeadingPlayerX(label);
        const labelCenterX = labelX + (BOARD_LABEL_PRINT_WIDTH / 2);
        const labelY = Number(label?.y || 0);
        return {
          id: label?.labelItemId || `${labelX}-${labelY}-${label?.text || ""}`,
          x: labelX,
          y: labelY,
          label,
          centerX: labelCenterX,
        };
      })
      .filter((label) => Number.isFinite(label.x) && Number.isFinite(label.y));
    const getSplitHeaderBounds = (header) => {
      if (!header) return null;
      if (zone === "top") {
        const previousHeadingY = boardLabels
          .filter((label) => {
            if (label?.labelItemId === header.label?.labelItemId) return false;
            const labelY = Number(label?.y || 0);
            return labelY < header.y && headersShareSplitLane({ x: getHeadingPlayerX(label) }, header);
          })
          .map((label) => Number(label?.y || 0))
          .sort((left, right) => right - left)[0];
        const minTop = Number.isFinite(previousHeadingY)
          ? previousHeadingY + BOARD_LABEL_PRINT_HEIGHT + BOARD_HEADING_STACK_GAP
          : BOARD_GRID_SIZE;
        const maxTop = Math.max(BOARD_GRID_SIZE, header.y - BOARD_HEADING_STACK_GAP - boardCardHeight);
        return {
          minTop,
          maxTop: Math.max(minTop, maxTop),
        };
      }

      const nextHeadingY = boardLabels
        .filter((label) => {
          if (label?.labelItemId === header.label?.labelItemId) return false;
          const labelY = Number(label?.y || 0);
          return labelY > header.y && headersShareSplitLane({ x: getHeadingPlayerX(label) }, header);
        })
        .map((label) => Number(label?.y || 0))
        .sort((left, right) => left - right)[0];
      const minTop = header.y + BOARD_LABEL_PRINT_HEIGHT + BOARD_HEADING_STACK_GAP;
      const maxTop = Number.isFinite(nextHeadingY)
        ? nextHeadingY - BOARD_HEADING_STACK_GAP - boardCardHeight
        : BOARD_PRINT_PAGE_HEIGHT - BOARD_GRID_SIZE - boardCardHeight;
      return {
        minTop,
        maxTop: Math.max(minTop, maxTop),
      };
    };
    const isPointInSplitHeaderBlock = (header, top) => {
      const bounds = getSplitHeaderBounds(header);
      if (!bounds) return false;
      return top >= bounds.minTop && top <= bounds.maxTop;
    };
    const getNearestSplitHeader = (left, top, options = {}) => {
      const centerX = Number(left || 0) + (boardCardWidth / 2);
      const itemY = Number(top || 0);
      if (!Number.isFinite(centerX) || !Number.isFinite(itemY)) return null;
      const headers = options.requireBlock
        ? zoneHeaders.filter((header) => isPointInSplitHeaderBlock(header, itemY))
        : zoneHeaders;
      return headers
        .map((header) => {
          const xDistance = Math.abs(header.centerX - centerX);
          const yDistance = Math.abs(header.y - itemY);
          return {
            ...header,
            xDistance,
            distance: Math.sqrt((xDistance * xDistance) + (yDistance * yDistance)),
          };
        })
        .sort((leftHeader, rightHeader) => leftHeader.distance - rightHeader.distance)[0] || null;
    };
    const getOverlappedSplitHeader = (left, top) => {
      const centerX = Number(left || 0) + (boardCardWidth / 2);
      const itemTop = Number(top || 0);
      const itemBottom = itemTop + boardCardHeight;
      if (!Number.isFinite(centerX) || !Number.isFinite(itemTop)) return null;

      return zoneHeaders
        .map((header) => {
          const labelTop = header.y;
          const labelBottom = header.y + BOARD_LABEL_PRINT_HEIGHT;
          const xDistance = Math.abs(header.centerX - centerX);
          const overlapsLabel = itemBottom >= labelTop && itemTop <= labelBottom;
          if (!overlapsLabel || xDistance > splitHeaderSnapDistance) return null;
          return {
            ...header,
            xDistance,
            distance: Math.abs(itemTop - header.y),
          };
        })
        .filter(Boolean)
        .sort((leftHeader, rightHeader) => leftHeader.distance - rightHeader.distance || leftHeader.xDistance - rightHeader.xDistance)[0] || null;
    };
    const nearestHeader =
      getOverlappedSplitHeader(droppedX, activeY) ||
      getNearestSplitHeader(droppedX, activeY, { requireBlock: true }) ||
      getNearestSplitHeader(droppedX, activeY);
    const hasNearestHeader = nearestHeader && nearestHeader.xDistance <= splitHeaderSnapDistance;
    const activeX = hasNearestHeader
      ? nearestHeader.x
      : droppedX;
    const headerY = hasNearestHeader
      ? nearestHeader.y
      : BOARD_SPLIT_SORT_Y;
    const activeHeaderBounds = hasNearestHeader
      ? getSplitHeaderBounds(nearestHeader)
      : null;
    const bottomStackStartY = activeHeaderBounds?.minTop ?? (headerY + BOARD_LABEL_PRINT_HEIGHT + BOARD_HEADING_STACK_GAP);

    if (zone === "top") {
      const topStackMinY = BOARD_GRID_SIZE;
      const topStackMaxY = Math.min(
        headerY - BOARD_HEADING_STACK_GAP - boardCardHeight,
        BOARD_PRINT_PAGE_HEIGHT - BOARD_GRID_SIZE - boardCardHeight
      );
      const sameLanePlayers = players.filter((player) => {
        if (player?.boardItemId === activePlayerId || !isPlayerPointInPrintArea(player)) return false;
        const playerX = Number(player?.x || 0);
        const playerY = Number(player?.y || 0);
        if (hasNearestHeader) {
          const playerHeader = getNearestSplitHeader(playerX, playerY, { requireBlock: true });
          return playerHeader?.id === nearestHeader.id;
        }
        return Math.abs(playerX - activeX) <= BOARD_GRID_SIZE && playerY < BOARD_SPLIT_SORT_Y;
      });
      return placeActivePlayerInStackLane(players, activePlayerId, activeX, topStackMinY, topStackMaxY, {
        direction: "up",
        sameLanePlayers,
        stackStepY,
      });
    }

    const bottomStackMaxY = activeHeaderBounds?.maxTop ?? (BOARD_PRINT_PAGE_HEIGHT - BOARD_GRID_SIZE - boardCardHeight);
    const sameLanePlayers = players.filter((player) => {
        if (player?.boardItemId === activePlayerId) return false;
        if (!isPlayerPointInPrintArea(player)) return false;
        const playerX = Number(player?.x || 0);
        const playerY = Number(player?.y || 0);
        const playerZone = playerY < BOARD_SPLIT_SORT_Y ? "top" : "bottom";
        if (playerZone !== zone) return false;
        if (hasNearestHeader) {
          const playerHeader = getNearestSplitHeader(playerX, playerY, { requireBlock: true });
          return playerHeader?.id === nearestHeader.id;
        }
        return Math.abs(playerX - activeX) <= BOARD_GRID_SIZE;
    });

    return placeActivePlayerInStackLane(players, activePlayerId, activeX, bottomStackStartY, bottomStackMaxY, {
      sameLanePlayers,
      stackStepY,
    });
  };

  const applyFlippedSnapLayout = (players, activePlayerId, point) => {
    if (!isFlippedPage) return players;
    if (!isPlayerPointInPrintArea(point)) return players;

    const droppedX = Number(point?.x || 0);
    const activeY = Number(point?.y || 0);
    if (!Number.isFinite(droppedX) || !Number.isFinite(activeY)) return players;

    const stackStepY = boardCardHeight + BOARD_HEADING_STACK_GAP;
    const headerSnapDistance = BOARD_LABEL_PRINT_WIDTH / 2;
    const headersShareLane = (left, right) => Math.abs(Number(left?.x || 0) - Number(right?.x || 0)) <= BOARD_GRID_SIZE;
    const headers = boardLabels
      .map((label) => {
        const labelX = getHeadingPlayerX(label);
        const labelCenterX = labelX + (BOARD_LABEL_PRINT_WIDTH / 2);
        const labelY = Number(label?.y || 0);
        return {
          id: label?.labelItemId || `${labelX}-${labelY}-${label?.text || ""}`,
          x: labelX,
          y: labelY,
          label,
          centerX: labelCenterX,
        };
      })
      .filter((label) => Number.isFinite(label.x) && Number.isFinite(label.y));

    const getHeaderBounds = (header) => {
      if (!header) return null;
      const previousHeadingY = boardLabels
        .filter((label) => {
          if (label?.labelItemId === header.label?.labelItemId) return false;
          const labelY = Number(label?.y || 0);
          return labelY < header.y && headersShareLane({ x: getHeadingPlayerX(label) }, header);
        })
        .map((label) => Number(label?.y || 0))
        .sort((left, right) => right - left)[0];
      const minTop = Number.isFinite(previousHeadingY)
        ? previousHeadingY + BOARD_LABEL_PRINT_HEIGHT + BOARD_HEADING_STACK_GAP
        : BOARD_GRID_SIZE;
      const maxTop = Math.max(BOARD_GRID_SIZE, header.y - BOARD_HEADING_STACK_GAP - boardCardHeight);
      return {
        minTop,
        maxTop: Math.max(minTop, maxTop),
      };
    };

    const isPointInHeaderBlock = (header, top) => {
      const bounds = getHeaderBounds(header);
      if (!bounds) return false;
      return top >= bounds.minTop && top <= bounds.maxTop;
    };
    const getNearestHeader = (left, top, options = {}) => {
      const centerX = Number(left || 0) + (boardCardWidth / 2);
      const itemY = Number(top || 0);
      if (!Number.isFinite(centerX) || !Number.isFinite(itemY)) return null;
      const candidates = options.requireBlock
        ? headers.filter((header) => isPointInHeaderBlock(header, itemY))
        : headers;
      return candidates
        .map((header) => {
          const xDistance = Math.abs(header.centerX - centerX);
          const yDistance = Math.abs(header.y - itemY);
          return {
            ...header,
            xDistance,
            distance: Math.sqrt((xDistance * xDistance) + (yDistance * yDistance)),
          };
        })
        .sort((leftHeader, rightHeader) => leftHeader.distance - rightHeader.distance)[0] || null;
    };
    const getOverlappedHeader = (left, top) => {
      const centerX = Number(left || 0) + (boardCardWidth / 2);
      const itemTop = Number(top || 0);
      const itemBottom = itemTop + boardCardHeight;
      if (!Number.isFinite(centerX) || !Number.isFinite(itemTop)) return null;

      return headers
        .map((header) => {
          const labelTop = header.y;
          const labelBottom = header.y + BOARD_LABEL_PRINT_HEIGHT;
          const xDistance = Math.abs(header.centerX - centerX);
          const overlapsLabel = itemBottom >= labelTop && itemTop <= labelBottom;
          if (!overlapsLabel || xDistance > headerSnapDistance) return null;
          return {
            ...header,
            xDistance,
            distance: Math.abs(itemTop - header.y),
          };
        })
        .filter(Boolean)
        .sort((leftHeader, rightHeader) => leftHeader.distance - rightHeader.distance || leftHeader.xDistance - rightHeader.xDistance)[0] || null;
    };

    const nearestHeader =
      getOverlappedHeader(droppedX, activeY) ||
      getNearestHeader(droppedX, activeY, { requireBlock: true }) ||
      getNearestHeader(droppedX, activeY);
    if (!nearestHeader || nearestHeader.xDistance > headerSnapDistance) return players;

    const headerBounds = getHeaderBounds(nearestHeader);
    const topStackBottomY = headerBounds?.maxTop ?? (nearestHeader.y - BOARD_HEADING_STACK_GAP - boardCardHeight);
    const topStackMinY = BOARD_GRID_SIZE;
    const topStackMaxY = Math.min(
      nearestHeader.y - BOARD_HEADING_STACK_GAP - boardCardHeight,
      BOARD_PRINT_PAGE_HEIGHT - BOARD_GRID_SIZE - boardCardHeight
    );
    const sameLanePlayers = players.filter((player) => {
        if (player?.boardItemId === activePlayerId) return false;
        if (!isPlayerPointInPrintArea(player)) return false;
        const playerX = Number(player?.x || 0);
        const playerY = Number(player?.y || 0);
        const playerHeader = getNearestHeader(playerX, playerY, { requireBlock: true });
        return playerHeader?.id === nearestHeader.id;
    });

    return placeActivePlayerInStackLane(players, activePlayerId, nearestHeader.x, topStackMinY, Math.min(topStackBottomY, topStackMaxY), {
      direction: "up",
      sameLanePlayers,
      stackStepY,
    });
  };

  const applyBoardStackLayout = (players, activePlayerId, point) => {
    if (isSplitSortPage) return applySplitSnapLayout(players, activePlayerId, point);
    if (isFlippedPage) return applyFlippedSnapLayout(players, activePlayerId, point);
    const headingStackedPlayers = applyHeadingStackLayout(players, activePlayerId, point);
    if (headingStackedPlayers !== players) return headingStackedPlayers;
    return players;
  };

  const getBoardDragOffset = (event, item, type) => {
    const rect = whiteboardRef.current?.getBoundingClientRect();
    const printOriginOffsetX = type === "detail" ? 0 : getPrintOriginOffsetX(rect);
    const printOriginOffsetY = type === "detail" ? 0 : getPrintOriginOffsetY(rect);
    const baseLeft = printOriginOffsetX + Number(item?.x || 0) - (type === "player" ? BOARD_REMOVE_RAIL_WIDTH : 0);
    const baseTop = printOriginOffsetY + Number(item?.y || 0);
    if (!rect) return null;
    return {
      x: Math.max(0, event.clientX - rect.left - baseLeft),
      y: Math.max(0, event.clientY - rect.top - baseTop),
    };
  };

  const handlePlayerDragStart = (event, player) => {
    if (!canEditCurrentBoard) {
      event.preventDefault();
      setSaveMessage("Board is view only");
      return;
    }
    event.dataTransfer.setData("application/fbx-draft-player", JSON.stringify(player));
    event.dataTransfer.setData("application/fbx-draft-offset", JSON.stringify({
      x: BOARD_REMOVE_RAIL_WIDTH + (boardCardWidth / 2),
      y: 24,
    }));
    event.dataTransfer.effectAllowed = "copy";
  };

  const handleBoardCardDragStart = (event, item) => {
    if (!canEditCurrentBoard) {
      event.preventDefault();
      setSaveMessage("Board is view only");
      return;
    }
    if (event.target?.closest?.(".draft-board-card-remove, .draft-board-card-duplicate")) {
      event.preventDefault();
      return;
    }
    if (groupSelectOpen && selectedBoardItemCount > 1 && isBoardItemSelected("player", item.boardItemId)) {
      event.preventDefault();
      return;
    }
    pageTransferDragRef.current = {
      boardItemId: item.boardItemId,
      sourcePageId: activePageId,
      player: item,
      hoverPageId: "",
    };
    event.dataTransfer.setData("application/fbx-draft-board-item", item.boardItemId);
    event.dataTransfer.setData("application/fbx-draft-offset", JSON.stringify(getBoardDragOffset(event, item, "player")));
    event.dataTransfer.effectAllowed = "move";
  };

  const handleLabelDragStart = (event, item) => {
    if (!canEditCurrentBoard) {
      event.preventDefault();
      setSaveMessage("Board is view only");
      return;
    }
    if (event.target?.closest?.(".draft-board-label-remove")) {
      event.preventDefault();
      return;
    }
    if (groupSelectOpen && selectedBoardItemCount > 1 && isBoardItemSelected("label", item.labelItemId)) {
      event.preventDefault();
      return;
    }
    event.dataTransfer.setData("application/fbx-draft-label-item", item.labelItemId);
    event.dataTransfer.setData("application/fbx-draft-offset", JSON.stringify(getBoardDragOffset(event, item, "label")));
    event.dataTransfer.effectAllowed = "move";
  };

  const handleDetailDragStart = (event, item) => {
    if (!canEditCurrentBoard) {
      event.preventDefault();
      setSaveMessage("Board is view only");
      return;
    }
    if (event.target?.closest?.(".draft-board-detail-remove") || event.target?.closest?.(".draft-board-detail-textarea")) {
      event.preventDefault();
      return;
    }
    if (groupSelectOpen && selectedBoardItemCount > 1 && isBoardItemSelected("detail", item.detailItemId)) {
      event.preventDefault();
      return;
    }
    event.dataTransfer.setData("application/fbx-draft-detail-item", item.detailItemId);
    event.dataTransfer.setData("application/fbx-draft-offset", JSON.stringify(getBoardDragOffset(event, item, "detail")));
    event.dataTransfer.effectAllowed = "move";
  };

  const handleOverlayDragStart = (event, item) => {
    if (!canEditCurrentBoard) {
      event.preventDefault();
      setSaveMessage("Board is view only");
      return;
    }
    if (event.target?.closest?.(".draft-board-overlay-remove")) {
      event.preventDefault();
      return;
    }
    if (groupSelectOpen && selectedBoardItemCount > 1 && isBoardItemSelected("overlay", item.overlayItemId)) {
      event.preventDefault();
      return;
    }
    event.dataTransfer.setData("application/fbx-draft-overlay-item", item.overlayItemId);
    event.dataTransfer.setData("application/fbx-draft-offset", JSON.stringify(getBoardDragOffset(event, item, "overlay")));
    event.dataTransfer.effectAllowed = "move";
  };

  const handleDraftRowDragStart = (event, index) => {
    if (!canEditCurrentBoard) {
      event.preventDefault();
      setSaveMessage("Board is view only");
      return;
    }
    event.dataTransfer.setData("application/fbx-draft-order-index", String(index));
    event.dataTransfer.effectAllowed = "move";
  };

  const removeBoardPlayer = (boardItemId) => {
    if (!canEditCurrentBoard) {
      setSaveMessage("Board is view only");
      return;
    }
    const removedPlayer = boardPlayers.find((item) => item.boardItemId === boardItemId);
    const removedPlayerId = String(removedPlayer?.FBXID || "").trim();
    const nextPlayers = boardPlayers.filter((item) => item.boardItemId !== boardItemId);
    const nextDraftPlayers = draftPlayers.filter((draftPlayer) =>
      nextPlayers.some((boardPlayer) => String(boardPlayer?.FBXID || "") === String(draftPlayer?.FBXID || ""))
    );
    if (removedPlayerId) {
      if (String(notePlayer?.FBXID || "").trim() === removedPlayerId) clearPlayerNoteSelection();
      setHighlightedFBXID((current) => current === removedPlayerId ? "" : current);
    }
    setSelectedBoardItems((current) => {
      const next = { ...current };
      delete next[getBoardSelectionKey("player", boardItemId)];
      return next;
    });
    saveBoardTransaction(nextPlayers, nextDraftPlayers);
  };

  const getDuplicateBoardPlayerPoint = (sourcePlayer) => {
    const sourceX = Number(sourcePlayer?.x || 0);
    const sourceY = Number(sourcePlayer?.y || 0);
    const clampPoint = (point) => clampBoardPointToPrintableArea({
      x: snapToGrid(point.x),
      y: snapToGrid(point.y),
    }, "player");
    const getRect = (point) => ({
      left: point.x,
      top: point.y,
      right: point.x + boardCardWidth,
      bottom: point.y + boardCardHeight,
    });
    const overlaps = (leftRect, rightRect) =>
      leftRect.left < rightRect.right &&
      leftRect.right > rightRect.left &&
      leftRect.top < rightRect.bottom &&
      leftRect.bottom > rightRect.top;
    const usedRects = boardPlayers
      .filter((player) => player?.boardItemId !== sourcePlayer?.boardItemId)
      .concat(sourcePlayer ? [sourcePlayer] : [])
      .map((player) => getRect({ x: Number(player?.x || 0), y: Number(player?.y || 0) }));
    const isOpenPoint = (point) => !usedRects.some((rect) => overlaps(getRect(point), rect));
    const horizontalStep = boardCardWidth + BOARD_GRID_SIZE;
    const verticalStep = boardCardHeight + BOARD_HEADING_STACK_GAP;
    const seedPoints = [
      { x: sourceX + horizontalStep, y: sourceY },
      { x: sourceX - horizontalStep, y: sourceY },
      { x: sourceX, y: sourceY + verticalStep },
      { x: sourceX, y: sourceY - verticalStep },
    ].map(clampPoint);

    const seen = new Set();
    const candidates = [];
    const addCandidate = (point) => {
      const key = `${point.x}:${point.y}`;
      if (seen.has(key)) return;
      seen.add(key);
      candidates.push(point);
    };
    seedPoints.forEach(addCandidate);

    for (let radius = BOARD_GRID_SIZE; radius <= 220; radius += BOARD_GRID_SIZE) {
      for (let dx = -radius; dx <= radius; dx += BOARD_GRID_SIZE) {
        addCandidate(clampPoint({ x: sourceX + dx, y: sourceY - radius }));
        addCandidate(clampPoint({ x: sourceX + dx, y: sourceY + radius }));
      }
      for (let dy = -radius + BOARD_GRID_SIZE; dy <= radius - BOARD_GRID_SIZE; dy += BOARD_GRID_SIZE) {
        addCandidate(clampPoint({ x: sourceX - radius, y: sourceY + dy }));
        addCandidate(clampPoint({ x: sourceX + radius, y: sourceY + dy }));
      }
    }

    return candidates
      .filter(isOpenPoint)
      .sort((left, right) => {
        const leftDistance = Math.hypot(left.x - sourceX, left.y - sourceY);
        const rightDistance = Math.hypot(right.x - sourceX, right.y - sourceY);
        return leftDistance - rightDistance || left.y - right.y || left.x - right.x;
      })[0] || clampPoint({ x: sourceX + horizontalStep, y: sourceY });
  };

  const duplicateBoardPlayer = (boardItemId) => {
    if (!canEditCurrentBoard) {
      setSaveMessage("Board is view only");
      return;
    }

    const sourcePlayer = boardPlayers.find((item) => item.boardItemId === boardItemId);
    if (!sourcePlayer) return;

    const duplicatePoint = getDuplicateBoardPlayerPoint(sourcePlayer);
    const duplicatePlayer = {
      ...sourcePlayer,
      boardItemId: `${sourcePlayer?.FBXID || Date.now()}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      ...duplicatePoint,
    };
    saveBoardTransaction([...boardPlayers, duplicatePlayer]);
    setHighlightedFBXID(String(duplicatePlayer.FBXID || "").trim());
    setSaveMessage("Player duplicated");
  };

  const clearCurrentPageItems = () => {
    if (!canEditCurrentBoard) {
      setSaveMessage("Board is view only");
      return;
    }

    const confirmed = window.confirm(`Clear all items from "${activePageName}"?`);
    if (!confirmed) return;

    setHighlightedFBXID("");
    setBoardImpNipFilter("");
    clearBoardSelection();
    clearPlayerNoteSelection();
    saveBoardItemsTransaction([], [], [], [], []);
    setSaveMessage("Page cleared");
  };

  const clearCurrentPagePlayers = () => {
    if (!canEditCurrentBoard) {
      setSaveMessage("Board is view only");
      return;
    }

    const confirmed = window.confirm(`Clear players from "${activePageName}" but keep positions and notes?`);
    if (!confirmed) return;

    setHighlightedFBXID("");
    setBoardImpNipFilter("");
    clearBoardSelection();
    clearPlayerNoteSelection();
    saveBoardItemsTransaction([], boardLabels, boardDetails, boardOverlays, []);
    setSaveMessage("Players cleared");
  };

  const addFilteredPlayersToBoard = () => {
    if (!canEditCurrentBoard) {
      setSaveMessage("Board is view only");
      return;
    }

    const playersToAdd = filteredPlayers.filter((player) => {
      const playerId = String(player?.FBXID || "").trim();
      return playerId && !activePageCounts[playerId];
    });

    if (playersToAdd.length === 0) {
      setSaveMessage("No unused filtered players to add to this page");
      return;
    }

    const columnWidth = boardCardWidth + BOARD_REMOVE_RAIL_WIDTH;
    const fallbackColumns = Math.max(1, Math.floor((BOARD_PRINT_PAGE_WIDTH - (BOARD_PRINT_SAFE_INSET * 2) - BOARD_REMOVE_RAIL_WIDTH) / columnWidth));
    const fallbackBottomY = BOARD_PRINT_PAGE_HEIGHT - BOARD_PRINT_SAFE_INSET - boardCardHeight;
    const stackStepY = boardCardHeight + BOARD_HEADING_STACK_GAP;
    const getBucketCapacity = (label, stackDirection) => {
      const headingY = Number(label?.y || 0);

      if (stackDirection === "above") {
        const lastTopY = headingY - BOARD_HEADING_STACK_GAP - boardCardHeight;
        return lastTopY >= BOARD_GRID_SIZE
          ? Math.floor((lastTopY - BOARD_GRID_SIZE) / stackStepY) + 1
          : 0;
      }

      const headingBottomY = getHeadingBottomY(label);
      const lastTopY = Number.isFinite(headingBottomY)
        ? headingBottomY - BOARD_HEADING_STACK_GAP - boardCardHeight
        : BOARD_PRINT_PAGE_HEIGHT - BOARD_GRID_SIZE - boardCardHeight;
      const stackStartY = headingY + BOARD_LABEL_PRINT_HEIGHT + BOARD_HEADING_STACK_GAP;
      return lastTopY >= stackStartY
        ? Math.floor((lastTopY - stackStartY) / stackStepY) + 1
        : 0;
    };
    const getBucketBounds = (label, stackDirection) => {
      const headingY = Number(label?.y || 0);
      if (stackDirection === "above") {
        return {
          minY: BOARD_GRID_SIZE,
          maxY: Math.max(BOARD_GRID_SIZE, headingY - BOARD_HEADING_STACK_GAP - boardCardHeight),
        };
      }

      const headingBottomY = getHeadingBottomY(label);
      const minY = headingY + BOARD_LABEL_PRINT_HEIGHT + BOARD_HEADING_STACK_GAP;
      const maxY = Number.isFinite(headingBottomY)
        ? headingBottomY - BOARD_HEADING_STACK_GAP - boardCardHeight
        : BOARD_PRINT_PAGE_HEIGHT - BOARD_GRID_SIZE - boardCardHeight;
      return {
        minY,
        maxY: Math.max(minY, maxY),
      };
    };
    const headingBuckets = boardLabels.reduce((buckets, label, index) => {
      const heading = normalizeHeadingText(label?.text);
      if (!heading) return buckets;
      if (!buckets[heading]) buckets[heading] = [];
      const headingPlayerX = getHeadingPlayerX(label);
      const headingY = Number(label?.y || 0);
      const isSplitTopHeading = isSplitSortPage && headingY < BOARD_SPLIT_SORT_Y;
      const isFlippedHeading = isFlippedPage;
      const stackDirection = (isSplitTopHeading || isFlippedHeading) ? "above" : "below";
      const capacity = getBucketCapacity(label, stackDirection);
      const bounds = getBucketBounds(label, stackDirection);
      buckets[heading].push({
        index,
        x: headingPlayerX,
        y: headingY,
        stackDirection,
        capacity,
        bounds,
        count: boardPlayers.filter((item) => {
          const playerX = Number(item?.x || 0);
          const playerY = Number(item?.y || 0);
          if (Math.abs(playerX - headingPlayerX) > BOARD_GRID_SIZE) return false;
          if (playerY < bounds.minY || playerY > bounds.maxY) return false;

          if (isSplitSortPage || isFlippedHeading) {
            return stackDirection === "above"
              ? playerY < headingY && (isFlippedHeading || playerY < BOARD_SPLIT_SORT_Y)
              : playerY >= headingY + BOARD_LABEL_PRINT_HEIGHT && playerY >= BOARD_SPLIT_SORT_Y;
          }

          return playerY >= headingY + BOARD_LABEL_PRINT_HEIGHT;
        }).length,
      });
      return buckets;
    }, {});
    const hasHeadingBuckets = Object.keys(headingBuckets).length > 0;
    const fallbackPlayers = [];
    const placedPlayers = [];

    playersToAdd.forEach((player) => {
      const candidates = getPlayerBoardHeadingCandidates(player);
      const olineHeadings = isBoardOffensiveLinePlayer(player)
        ? getBoardOffensiveLineHeadingCandidates(player).filter((candidate) => headingBuckets[candidate]?.length)
        : [];
      const olineHeadingsWithSpace = olineHeadings.filter((candidate) =>
        headingBuckets[candidate].some((bucket) => !Number.isFinite(bucket.capacity) || bucket.count < bucket.capacity)
      );
      const candidatesWithSpace = candidates.filter((candidate) =>
        headingBuckets[candidate]?.some((bucket) => !Number.isFinite(bucket.capacity) || bucket.count < bucket.capacity)
      );
      const heading = isBoardOffensiveLinePlayer(player)
        ? olineHeadingsWithSpace[Math.floor(Math.random() * olineHeadingsWithSpace.length)]
        : candidatesWithSpace.find((candidate) => headingBuckets[candidate]?.length);

      if (!heading) {
        fallbackPlayers.push(player);
        return;
      }

      const targetBuckets = isBoardOffensiveLinePlayer(player)
        ? headingBuckets[heading]
          .filter((bucket) => !Number.isFinite(bucket.capacity) || bucket.count < bucket.capacity)
          .map((bucket) => ({ bucket, candidateIndex: 0 }))
        : candidatesWithSpace.flatMap((candidate, candidateIndex) => (
          (headingBuckets[candidate] || [])
            .filter((bucket) => !Number.isFinite(bucket.capacity) || bucket.count < bucket.capacity)
            .map((bucket) => ({ bucket, candidateIndex }))
        ));

      if (targetBuckets.length === 0) {
        fallbackPlayers.push(player);
        return;
      }
      targetBuckets.sort((left, right) =>
        left.bucket.count - right.bucket.count ||
        left.bucket.y - right.bucket.y ||
        left.bucket.x - right.bucket.x ||
        left.candidateIndex - right.candidateIndex ||
        left.bucket.index - right.bucket.index
      );
      const target = targetBuckets[0].bucket;
      const nextY = target.stackDirection === "above"
        ? Math.max(BOARD_PRINT_SAFE_INSET, target.y - BOARD_HEADING_STACK_GAP - boardCardHeight - (target.count * stackStepY))
        : target.y + BOARD_LABEL_PRINT_HEIGHT + BOARD_HEADING_STACK_GAP + (target.count * stackStepY);
      if (nextY < target.bounds.minY || nextY > target.bounds.maxY) {
        fallbackPlayers.push(player);
        return;
      }
      const point = clampBoardPointToPrintableArea({ x: target.x, y: nextY }, "player");
      const nextPlayer = buildBoardPlayer(player, point.x, point.y);
      target.count += 1;
      placedPlayers.push(nextPlayer);
    });
    const nextPlayers = [
      ...boardPlayers,
      ...placedPlayers,
      ...fallbackPlayers.map((player, index) => {
        const column = index % fallbackColumns;
        const rowFromBottom = Math.floor(index / fallbackColumns);
        const point = clampBoardPointToPrintableArea({
          x: BOARD_PRINT_SAFE_INSET + (column * columnWidth),
          y: Math.max(BOARD_PRINT_SAFE_INSET, fallbackBottomY - (rowFromBottom * boardCardHeight)),
        }, "player");
        return buildBoardPlayer(player, point.x, point.y);
      }),
    ];

    saveBoardTransaction(nextPlayers);
    setSaveMessage(hasHeadingBuckets && placedPlayers.length > 0
      ? `Added ${playersToAdd.length} players to page (${placedPlayers.length} matched positions)`
      : `Added ${playersToAdd.length} players to page`);
  };

  const addFilteredOnBoardPlayersToDraft = () => {
    if (!canEditCurrentBoard) {
      setSaveMessage("Board is view only");
      return;
    }

    const existingDraftIds = new Set(draftPlayers.map((player) => String(player?.FBXID || "").trim()).filter(Boolean));
    const playersToDraft = filteredOnBoardPlayers.filter((player) => {
      const playerId = String(player?.FBXID || "").trim();
      return playerId && !existingDraftIds.has(playerId);
    });

    if (playersToDraft.length === 0) {
      setSaveMessage("No new on-board players to add to Priority");
      return;
    }

    saveDraftTransaction([
      ...draftPlayers,
      ...playersToDraft.map(buildDraftPlayer),
    ]);
    setSaveMessage(`Added ${playersToDraft.length} players to Priority`);
  };

  const addBoardLabel = () => {
    if (!canEditCurrentBoard) {
      setSaveMessage("Board is view only");
      return;
    }

    const { x: nextX, y: nextY } = clampBoardPointToPrintableArea({
      x: BOARD_PRINT_SAFE_INSET,
      y: BOARD_PRINT_SAFE_INSET + ((boardLabels.length + boardDetails.length) % 8) * BOARD_GRID_SIZE,
    }, "label");
    const presetLabelData = activePresetDraft?.presetSessionId
      ? {
        presetSessionId: activePresetDraft.presetSessionId,
        presetSource: activePresetDraft.source,
        presetId: activePresetDraft.presetId,
        presetType: activePresetDraft.type,
      }
      : {};
    saveLabelTransaction([...boardLabels, buildBoardLabel(nextX, nextY, "Position", "", presetLabelData)], activePresetDraft);
  };

  const addBoardDetail = () => {
    if (!canEditCurrentBoard) {
      setSaveMessage("Board is view only");
      return;
    }

    const { x: nextX, y: nextY } = clampBoardPointToPrintableArea({
      x: BOARD_PRINT_SAFE_INSET,
      y: BOARD_PRINT_SAFE_INSET + ((boardLabels.length + boardDetails.length) % 8) * BOARD_GRID_SIZE,
    }, "detail");
    saveDetailTransaction([...boardDetails, buildBoardDetail(nextX, nextY, {
      scoutId: currentScoutId,
      scoutName: currentScoutName,
    })]);
  };

  const placePresetLabelsOnTargetPage = (newLabels, nextPresetDraft, successMessage, mode = "erase") => {
    const currentPages = mergeCurrentPage(boardPlayers, draftPlayers, boardLabels, boardDetails, boardOverlays);
    const presetPageType = getPresetPageTypeFromData(nextPresetDraft, nextPresetDraft?.name);
    let targetPageId = activePageId || currentPages[0]?.pageId || "";
    let targetPageName = activePageName;
    let nextPages = currentPages;

    if (mode === "new") {
      const existingNames = new Set(currentPages.map((page) => cleanText(page?.pageName).toLowerCase()).filter(Boolean));
      let pageName = "";

      while (!pageName) {
        const enteredName = window.prompt("New page name", nextPresetDraft?.name || "Page Preset Page");
        if (enteredName === null) return false;

        const trimmedName = cleanText(enteredName);
        if (!trimmedName) {
          window.alert("Page name required.");
          continue;
        }

        if (existingNames.has(trimmedName.toLowerCase())) {
          window.alert("Page name already exists.");
          continue;
        }

        pageName = trimmedName;
      }

      const nextPage = buildBoardPage(pageName, { pageType: presetPageType });
      targetPageId = nextPage.pageId;
      targetPageName = nextPage.pageName;
      nextPages = [...currentPages, nextPage];
    }

    const nextPagesWithPreset = nextPages.map((page) => (
      page.pageId === targetPageId
        ? {
            ...stripPageDraftPlayers(page),
            players: [],
            labels: newLabels,
            details: [],
            overlays: [],
            pageType: presetPageType,
            presetDraft: nextPresetDraft,
          }
        : page
    ));

    captureUndoSnapshot();
    setBoardPages(nextPagesWithPreset);
    setActivePageId(targetPageId);
    setBoardPlayers([]);
    setBoardLabels(newLabels);
    setBoardDetails([]);
    setBoardOverlays([]);
    setActivePresetDraft(nextPresetDraft);
    setHighlightedFBXID("");
    setBoardImpNipFilter("");
    clearBoardSelection();
    clearPlayerNoteSelection();
    saveBoardLocal([], {}, draftPlayers, newLabels, [], [], {
      pages: nextPagesWithPreset,
      activePageId: targetPageId,
    });

    if (savedBoardId && currentBoardCanEdit) {
      saveBoardToDatabase([], {
        draftPlayers,
        labels: newLabels,
        details: [],
        overlays: [],
        pages: nextPagesWithPreset,
        activePageId: targetPageId,
      });
    } else if (savedBoardId && !currentBoardCanEdit) {
      setSaveMessage("Board is view only");
      return false;
    }

    setShowNewPageForm(false);
    setNewPageName("");
    setNewPageType(BOARD_PAGE_TYPES.regular);
    setNewPageNameError("");
    setTeamPresetMessage("");
    setSaveMessage(`${successMessage}${targetPageName ? ` on ${targetPageName}` : ""}`);
    setOverlaysOpen(false);
    return true;
  };

  const applyPresetLabelsToTargetPage = (newLabels, nextPresetDraft, successMessage) => {
    const currentPageHasData = boardPlayers.length > 0 || boardLabels.length > 0 || boardDetails.length > 0 || boardOverlays.length > 0;
    if (currentPageHasData) {
      setPendingPresetChoice({
        newLabels,
        nextPresetDraft,
        successMessage,
        activePageName,
      });
      return false;
    }

    return placePresetLabelsOnTargetPage(newLabels, nextPresetDraft, successMessage, "erase");
  };

  const completePendingPresetChoice = (mode) => {
    if (!pendingPresetChoice) return;
    const placed = placePresetLabelsOnTargetPage(
      pendingPresetChoice.newLabels,
      pendingPresetChoice.nextPresetDraft,
      pendingPresetChoice.successMessage,
      mode
    );
    if (placed || mode === "new") setPendingPresetChoice(null);
  };

  const addBoardOverlay = (type) => {
    if (!canEditCurrentBoard) {
      setSaveMessage("Board is view only");
      return;
    }

    if (BOARD_HEADING_LABEL_SETS[type]) {
      const overlayConfig = BOARD_OVERLAY_TYPES[type] || {};
      const sourcePreset = overlayConfig.sourcePresetName
        ? teamPresets.find((preset) => cleanText(preset?.presetName).toLowerCase() === overlayConfig.sourcePresetName.toLowerCase())
        : null;
      const sourcePresetLabels = sourcePreset ? getPresetLabelsFromData(sourcePreset.presetData) : [];
      const labelSet = sourcePresetLabels.length > 0 ? sourcePresetLabels : BOARD_HEADING_LABEL_SETS[type];
      const overlayLabel = overlayConfig.label || "Position page preset";
      const presetPageType = overlayConfig.pageType || getPresetPageTypeFromData(sourcePreset?.presetData, sourcePreset?.presetName);
      const presetSessionId = `preset-system-${type}-${Date.now()}`;
      const newLabels = labelSet.map((label, index) => {
        const point = clampBoardPointToPrintableArea({ x: label.x, y: label.y }, "label");
        return buildBoardLabel(point.x, point.y, label.text, `${type}-${index}`, {
          presetSessionId,
          presetSource: "system",
          presetType: type,
        });
      });
      const nextPresetDraft = {
        source: "system",
        type,
        presetId: 0,
        name: overlayLabel,
        presetSessionId,
        baseline: stringifyPresetLabels(newLabels),
        pageType: presetPageType,
      };
      applyPresetLabelsToTargetPage(newLabels, nextPresetDraft, `Added ${overlayLabel} positions`);
      return;
    }

    const { x: nextX, y: nextY } = clampBoardPointToPrintableArea({
      x: BOARD_PRINT_SAFE_INSET + BOARD_REMOVE_RAIL_WIDTH + (BOARD_GRID_SIZE * 8),
      y: BOARD_PRINT_SAFE_INSET,
    }, "overlay", { type });
    saveOverlayTransaction([...boardOverlays, buildBoardOverlay(type, nextX, nextY)]);
    setOverlaysOpen(false);
  };

  const addTeamPreset = (preset) => {
    if (!canEditCurrentBoard) {
      setSaveMessage("Board is view only");
      return;
    }

    const presetId = Number(preset?.presetId || 0);
    const presetName = cleanText(preset?.presetName) || "Team page preset";
    const labelSet = getPresetLabelsFromData(preset?.presetData);
    const presetPageType = getPresetPageTypeFromData(preset?.presetData, presetName);
    if (labelSet.length === 0) {
      setTeamPresetMessage("Page preset has no saved positions");
      return;
    }

    const presetSessionId = `preset-team-${presetId}-${Date.now()}`;
    const newLabels = labelSet.map((label, index) => {
      const point = clampBoardPointToPrintableArea({ x: label.x, y: label.y }, "label");
      return buildBoardLabel(point.x, point.y, label.text, `team-${presetId}-${index}`, {
        presetSessionId,
        presetSource: "team",
        presetId,
      });
    });
    const nextPresetDraft = {
      source: "team",
      type: "",
      presetId,
      name: presetName,
      presetSessionId,
      baseline: stringifyPresetLabels(newLabels),
      pageType: presetPageType,
    };
    applyPresetLabelsToTargetPage(newLabels, nextPresetDraft, `Added ${presetName} positions`);
  };

  const handleSavePresetAs = async () => {
    if (!canSavePresetAs) return;

    const labelsToSave = activePresetDraft && activePresetLabels.length > 0 ? activePresetLabels : boardLabels;
    const defaultName = activePresetDraft?.name || activePageName || "Team Page Preset";
    const enteredName = window.prompt("Save page preset as", `${defaultName} Copy`);
    const presetName = cleanText(enteredName);
    if (!presetName) return;

    await saveTeamPreset({
      presetId: 0,
      presetName,
      labels: labelsToSave,
      pageType: activePageType,
      applyToPageLabels: true,
    });
  };

  const handleUpdatePreset = async (preset) => {
    const presetId = Number(preset?.presetId || 0);
    if (!activePresetModified || Number(activePresetDraft?.presetId || 0) !== presetId) return;

    await saveTeamPreset({
      presetId,
      presetName: preset?.presetName || activePresetDraft?.name || "Team Page Preset",
      labels: activePresetLabels,
      pageType: activePageType,
      applyToPageLabels: true,
    });
  };

  const handleRenamePreset = async (preset) => {
    const presetId = Number(preset?.presetId || 0);
    if (!presetId || !canEditCurrentBoard) return;

    const currentName = preset?.presetName || "Team Page Preset";
    const enteredName = window.prompt("Page preset name", currentName);
    const presetName = cleanText(enteredName);
    if (!presetName || presetName === currentName) return;

    const labelsToSave = getPresetLabelsFromData(preset?.presetData);
    if (labelsToSave.length === 0) {
      setTeamPresetMessage("Page preset has no saved positions");
      return;
    }

    const isActivePreset = activePresetDraft?.source === "team" && Number(activePresetDraft?.presetId || 0) === presetId;
    await saveTeamPreset({
      presetId,
      presetName,
      labels: isActivePreset ? activePresetLabels : labelsToSave,
      pageType: isActivePreset ? activePageType : getPresetPageTypeFromData(preset?.presetData, presetName),
      activateSavedPreset: isActivePreset,
    });
  };

  const removeBoardLabel = (labelItemId) => {
    if (!canEditCurrentBoard) {
      setSaveMessage("Board is view only");
      return;
    }
    setSelectedBoardItems((current) => {
      const next = { ...current };
      delete next[getBoardSelectionKey("label", labelItemId)];
      return next;
    });
    saveLabelTransaction(boardLabels.filter((item) => item.labelItemId !== labelItemId));
  };

  const updateBoardLabelText = (labelItemId, text) => {
    const nextLabels = boardLabels.map((item) =>
      item.labelItemId === labelItemId ? { ...item, text } : item
    );
    saveLabelTransaction(nextLabels, activePresetDraft, { debounceDatabase: true });
  };

  const removeBoardDetail = (detailItemId) => {
    if (!canEditCurrentBoard) {
      setSaveMessage("Board is view only");
      return;
    }
    setSelectedBoardItems((current) => {
      const next = { ...current };
      delete next[getBoardSelectionKey("detail", detailItemId)];
      return next;
    });
    saveDetailTransaction(boardDetails.filter((item) => item.detailItemId !== detailItemId));
  };

  const removeBoardOverlay = (overlayItemId) => {
    if (!canEditCurrentBoard) {
      setSaveMessage("Board is view only");
      return;
    }
    setSelectedBoardItems((current) => {
      const next = { ...current };
      delete next[getBoardSelectionKey("overlay", overlayItemId)];
      return next;
    });
    saveOverlayTransaction(boardOverlays.filter((item) => item.overlayItemId !== overlayItemId));
  };

  const updateBoardDetailText = (detailItemId, text) => {
    const nextDetails = boardDetails.map((item) =>
      item.detailItemId === detailItemId ? { ...item, text } : item
    );
    saveDetailTransaction(nextDetails, { debounceDatabase: true });
  };

  const addBoardItemToDraft = (boardItemId, insertIndex = draftPlayers.length) => {
    if (!canEditCurrentBoard) {
      setSaveMessage("Board is view only");
      return;
    }
    const boardPlayer = boardPlayers.find((item) => item.boardItemId === boardItemId);
    if (!boardPlayer) return;

    const playerId = String(boardPlayer.FBXID || "").trim();
    const exists = draftPlayers.some((item) => String(item.FBXID || "").trim() === playerId);
    if (playerId && exists) {
      setSaveMessage("Player already in Priority");
      return;
    }

    const boundedIndex = Math.max(0, Math.min(insertIndex, draftPlayers.length));
    const nextDraftPlayers = [...draftPlayers];
    nextDraftPlayers.splice(boundedIndex, 0, buildDraftPlayer(boardPlayer));
    saveDraftTransaction(nextDraftPlayers);
  };

  const addPlayerToDraft = (player, insertIndex = draftPlayers.length) => {
    if (!canEditCurrentBoard) {
      setSaveMessage("Board is view only");
      return;
    }

    const playerId = String(player?.FBXID || "").trim();
    const exists = draftPlayers.some((item) => String(item.FBXID || "").trim() === playerId);
    if (playerId && exists) {
      setSaveMessage("Player already in Priority");
      return;
    }

    const boundedIndex = Math.max(0, Math.min(insertIndex, draftPlayers.length));
    const nextDraftPlayers = [...draftPlayers];
    nextDraftPlayers.splice(boundedIndex, 0, buildDraftPlayer(player));
    saveDraftTransaction(nextDraftPlayers);
  };

  const moveDraftPlayer = (fromIndex, toIndex) => {
    if (!canEditCurrentBoard) {
      setSaveMessage("Board is view only");
      return;
    }
    if (fromIndex === toIndex || fromIndex < 0 || fromIndex >= draftPlayers.length) return;

    const boundedIndex = Math.max(0, Math.min(toIndex, draftPlayers.length - 1));
    const nextDraftPlayers = [...draftPlayers];
    const [moved] = nextDraftPlayers.splice(fromIndex, 1);
    nextDraftPlayers.splice(boundedIndex, 0, moved);
    saveDraftTransaction(nextDraftPlayers);
  };

  const removeDraftPlayer = (draftItemId, fallbackIndex) => {
    if (!canEditCurrentBoard) {
      setSaveMessage("Board is view only");
      return;
    }

    const nextDraftPlayers = draftPlayers.filter((player, index) => {
      if (draftItemId) return player?.draftItemId !== draftItemId;
      return index !== fallbackIndex;
    });
    saveDraftTransaction(nextDraftPlayers);
    setSaveMessage("Removed from Priority");
  };

  const handleDraftDrop = (event, insertIndex = draftPlayers.length) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canEditCurrentBoard) {
      setSaveMessage("Board is view only");
      return;
    }
    const draftIndex = event.dataTransfer.getData("application/fbx-draft-order-index");
    const boardItemId = event.dataTransfer.getData("application/fbx-draft-board-item");
    const rawPlayer = event.dataTransfer.getData("application/fbx-draft-player");

    if (draftIndex !== "") {
      moveDraftPlayer(Number(draftIndex), insertIndex);
      return;
    }

    if (boardItemId) {
      addBoardItemToDraft(boardItemId, insertIndex);
      return;
    }

    if (rawPlayer) {
      try {
        addPlayerToDraft(JSON.parse(rawPlayer), insertIndex);
      } catch {}
    }
  };

  const handleWhiteboardDrop = (event) => {
    event.preventDefault();
    if (!canEditCurrentBoard) {
      setSaveMessage("Board is view only");
      return;
    }
    let dragOffset = null;
    try {
      dragOffset = JSON.parse(event.dataTransfer.getData("application/fbx-draft-offset") || "null");
    } catch {}
    const boardItemId = event.dataTransfer.getData("application/fbx-draft-board-item");
    const labelItemId = event.dataTransfer.getData("application/fbx-draft-label-item");
    const detailItemId = event.dataTransfer.getData("application/fbx-draft-detail-item");
    const overlayItemId = event.dataTransfer.getData("application/fbx-draft-overlay-item");

    if (boardItemId) {
      const point = getDropPoint(event, dragOffset, "player");
      const transferDrag = pageTransferDragRef.current;
      if (transferDrag?.boardItemId === boardItemId && transferDrag.sourcePageId && transferDrag.sourcePageId !== activePageId) {
        moveBoardPlayerToPage(boardItemId, activePageId, point, transferDrag.sourcePageId);
        clearPageTransferDrag();
        return;
      }
      const movedPlayers = boardPlayers.map((item) =>
        item.boardItemId === boardItemId ? { ...item, ...point } : item
      );
      const nextPlayers = applyBoardStackLayout(movedPlayers, boardItemId, point);
      saveBoardTransaction(nextPlayers);
      clearPageTransferDrag();
      return;
    }

    if (labelItemId) {
      const point = getHeadingDropPoint(event, dragOffset, labelItemId);
      const nextLabels = boardLabels.map((item) =>
        item.labelItemId === labelItemId ? { ...item, ...point } : item
      );
      saveLabelTransaction(nextLabels);
      return;
    }

    if (detailItemId) {
      const point = getDropPoint(event, dragOffset, "detail");
      const nextDetails = boardDetails.map((item) =>
        item.detailItemId === detailItemId ? { ...item, ...point } : item
      );
      saveDetailTransaction(nextDetails);
      return;
    }

    if (overlayItemId) {
      const nextOverlays = boardOverlays.map((item) =>
        item.overlayItemId === overlayItemId
          ? { ...item, ...clampBoardPointToPrintableArea(getDropPoint(event, dragOffset, "overlay"), "overlay", item) }
          : item
      );
      saveOverlayTransaction(nextOverlays);
      return;
    }

    const rawPlayer = event.dataTransfer.getData("application/fbx-draft-player");
    if (!rawPlayer) return;

    try {
      const player = JSON.parse(rawPlayer);
      const point = getDropPoint(event, dragOffset, "player");
      const nextPlayer = buildBoardPlayer(player, point.x, point.y);
      const nextPlayers = applyBoardStackLayout([...boardPlayers, nextPlayer], nextPlayer.boardItemId, point);
      saveBoardTransaction(nextPlayers);
    } catch {}
  };

  const printBoard = () => {
    closeWhiteBoardPopups();
    window.setTimeout(() => window.print(), 0);
  };

  const isBoardChooserState = showWelcomeBoardPrompt && !savedBoardId && boardName === BOARD_CHOOSER_NAME;

  return (
    <div ref={draftBoardScreenRef} className={`draft-board-screen ${isPrintSizeMode ? "draft-board-screen-print-size" : "draft-board-screen-full-size"} ${isGridSizeMode ? "draft-board-screen-grid-size" : ""} ${isCenteredGridSizeMode ? "draft-board-screen-grid-centered-size" : ""}`}>
        {showWelcomeBoardPrompt && (
          <div className="draft-board-welcome-overlay">
            <div className="draft-board-welcome-modal">
              <div className="draft-board-welcome-title">{isBoardChooserState ? "Choose a Board" : "Welcome to White Board"}</div>
              <div className="draft-board-welcome-copy">
                {isBoardChooserState
                  ? "Open an existing board, or create a new board to continue."
                  : "White Board is a new FBX feature that lets you use players from the system to design any whiteboard, including depth charts."}
              </div>
              <div className="draft-board-welcome-copy">
                {isBoardChooserState
                  ? "Deleted boards are not replaced with a local board."
                  : "Open a saved board, or enter a board name to create your first board."}
              </div>
              <div className="draft-board-welcome-saved">
                <div className="draft-board-welcome-saved-title">Saved Boards</div>
                {!user?.team || !user?.scout ? (
                  <div className="draft-board-board-empty">Sign in to load saved boards.</div>
                ) : boardsLoading ? (
                  <div className="draft-board-board-empty">Loading boards...</div>
                ) : boardsError ? (
                  <div className="draft-board-board-empty">{boardsError}</div>
                ) : savedBoards.length === 0 ? (
                  <div className="draft-board-board-empty">No saved boards found.</div>
                ) : (
                  <div className="draft-board-board-list draft-board-welcome-board-list">
                    <div className="draft-board-board-section-title">My Boards</div>
                    {myBoards.length === 0 ? (
                      <div className="draft-board-board-empty draft-board-board-empty-small">No boards saved by you.</div>
                    ) : (
                      myBoards.map(renderBoardRow)
                    )}
                    <div className="draft-board-board-divider" />
                    <div className="draft-board-board-section-title">Shared Boards</div>
                    {sharedBoards.length === 0 ? (
                      <div className="draft-board-board-empty draft-board-board-empty-small">No shared boards.</div>
                    ) : (
                      sharedBoards.map(renderBoardRow)
                    )}
                  </div>
                )}
              </div>
              <div className="draft-board-welcome-saved-title">Create New Board</div>
              <input
                type="text"
                className="draft-board-welcome-input"
                placeholder="Board name"
                value={welcomeBoardName}
                maxLength={120}
                onChange={(event) => {
                  setWelcomeBoardName(event.target.value);
                  if (welcomeBoardNameError) setWelcomeBoardNameError("");
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && welcomeBoardName.trim().length >= 10) handleWelcomeBoardCreate();
                  if (event.key === "Escape") updateActiveSideButton("");
                }}
                autoFocus
              />
              <div className="draft-board-welcome-meta">{welcomeBoardName.trim().length}/10 minimum</div>
              {welcomeBoardNameError && (
                <div className="draft-board-welcome-error">{welcomeBoardNameError}</div>
              )}
              <div className="draft-board-welcome-actions">
                <button
                  type="button"
                  className="draft-board-welcome-button draft-board-welcome-cancel"
                  onClick={() => updateActiveSideButton("")}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="draft-board-welcome-button draft-board-welcome-continue"
                  disabled={welcomeBoardName.trim().length < 10}
                  onClick={handleWelcomeBoardCreate}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="draft-board-nav">
          <div className="draft-board-nav-top">
        <div className="draft-board-left-actions">
          <div className="draft-board-list-buttons">
            <BigBoxButtons
              lists={listArray}
              shareLists={sharedListArray}
              sharedQueries={savedQueryArray}
              onLoaded={() => {
                closeWhiteBoardPopups("players");
                setPlayersOpen(true);
              }}
            />
          </div>
	          <div className="draft-board-quick-search" ref={quickSearchRef}>
	            <button
              type="button"
	              className={`draft-board-quick-search-trigger ${quickSearchOpen ? "draft-board-quick-search-trigger-active" : ""}`}
              onClick={() => {
                const nextOpen = !quickSearchOpen;
                closeWhiteBoardPopups(nextOpen ? "quickSearch" : "");
                setQuickSearchOpen(nextOpen);
                setQuickSearchMessage("");
              }}
              disabled={quickSearchLoading}
            >
              {quickSearchLoading ? "..." : "Search"}
            </button>
            {quickSearchOpen && (
              <div
                className="draft-board-quick-search-panel"
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    runDraftBoardQuickSearch();
                  }
                  if (event.key === "Escape") {
                    setQuickSearchOpen(false);
                  }
                }}
              >
                <div className="draft-board-quick-search-top-row">
                  <div className={`draft-board-filter-input-wrap ${hasFilterValue(quickSearchValues.LAST) ? "draft-board-filter-input-wrap-has-clear" : ""}`}>
                    <input
                      type="text"
                      className={`draft-board-quick-search-input ${hasFilterValue(quickSearchValues.LAST) ? "draft-board-filter-active" : ""}`}
                      placeholder="Last Name"
                      value={quickSearchValues.LAST}
                      onChange={(event) => updateQuickSearchValue("LAST", event.target.value)}
                      autoFocus
                    />
                    {hasFilterValue(quickSearchValues.LAST) && (
                      <button type="button" className="draft-board-filter-clear" onClick={() => updateQuickSearchValue("LAST", "")}>
                        X
                      </button>
                    )}
                  </div>
                  <div className={`draft-board-filter-input-wrap ${hasFilterValue(quickSearchValues.FIRST) ? "draft-board-filter-input-wrap-has-clear" : ""}`}>
                    <input
                      type="text"
                      className={`draft-board-quick-search-input ${hasFilterValue(quickSearchValues.FIRST) ? "draft-board-filter-active" : ""}`}
                      placeholder="First Name"
                      value={quickSearchValues.FIRST}
                      onChange={(event) => updateQuickSearchValue("FIRST", event.target.value)}
                    />
                    {hasFilterValue(quickSearchValues.FIRST) && (
                      <button type="button" className="draft-board-filter-clear" onClick={() => updateQuickSearchValue("FIRST", "")}>
                        X
                      </button>
                    )}
                  </div>
                  <div className={`draft-board-filter-input-wrap draft-board-filter-input-wrap-select ${hasFilterValue(quickSearchValues.GENPOS) ? "draft-board-filter-input-wrap-has-clear" : ""}`}>
                    <select
                      className={`draft-board-quick-search-input ${hasFilterValue(quickSearchValues.GENPOS) ? "draft-board-filter-active" : ""}`}
                      value={quickSearchValues.GENPOS}
                      onChange={(event) => updateQuickSearchValue("GENPOS", event.target.value)}
                      disabled={quickSearchValues.GENUNIT !== "-"}
                    >
                      <option value="-">General Position</option>
                      {genPos.map((position) => (
                        <option key={position.GeneralPos} value={position.GeneralPos}>
                          {position.GeneralPos}
                        </option>
                      ))}
                    </select>
                    {hasFilterValue(quickSearchValues.GENPOS) && (
                      <button type="button" className="draft-board-filter-clear" onClick={() => updateQuickSearchValue("GENPOS", "-")}>
                        X
                      </button>
                    )}
                  </div>
                  <div className={`draft-board-filter-input-wrap draft-board-filter-input-wrap-select ${hasFilterValue(quickSearchValues.GENUNIT) ? "draft-board-filter-input-wrap-has-clear" : ""}`}>
                    <select
                      className={`draft-board-quick-search-input ${hasFilterValue(quickSearchValues.GENUNIT) ? "draft-board-filter-active" : ""}`}
                      value={quickSearchValues.GENUNIT}
                      onChange={(event) => updateQuickSearchValue("GENUNIT", event.target.value)}
                      disabled={quickSearchValues.GENPOS !== "-"}
                    >
                      <option value="-">All Units</option>
                      {POSITION_UNIT_OPTIONS.map((unit) => (
                        <option key={`quick-${unit.code}`} value={unit.code}>
                          {unit.label}
                        </option>
                      ))}
                    </select>
                    {hasFilterValue(quickSearchValues.GENUNIT) && (
                      <button type="button" className="draft-board-filter-clear" onClick={() => updateQuickSearchValue("GENUNIT", "-")}>
                        X
                      </button>
	            )}
	          </div>
	        </div>
                {[
                  {
                    label: "CFL",
                    teamField: "CFLTEAM",
                    statusField: "CFLSTATUS",
                    startDateField: "CFLStatusDateStart",
                    endDateField: "CFLStatusDateEnd",
                    teams: cflObject,
                    statuses: CFLStatus,
                  },
                  {
                    label: "NFL",
                    teamField: "NFLTEAM",
                    statusField: "NFLSTATUS",
                    startDateField: "NFLStatusDateStart",
                    endDateField: "NFLStatusDateEnd",
                    teams: nflObject,
                    statuses: NFLStatus,
                  },
                  {
                    label: "UFL24",
                    teamField: "UFL24TEAM",
                    statusField: "UFL24STATUS",
                    startDateField: "UFL24StatusDateStart",
                    endDateField: "UFL24StatusDateEnd",
                    teams: ufl24Object,
                    statuses: NFLStatus,
                  },
	                ].map((league) => (
	                  <div className={`draft-board-quick-search-league draft-board-quick-search-league-${league.label.toLowerCase()}`} key={league.label}>
	                    <div className="draft-board-quick-search-league-title">{league.label}</div>
	                    <div className={`draft-board-filter-input-wrap draft-board-filter-input-wrap-select ${hasFilterValue(quickSearchValues[league.teamField]) ? "draft-board-filter-input-wrap-has-clear" : ""}`}>
	                      <select
	                        className={`draft-board-quick-search-input ${hasFilterValue(quickSearchValues[league.teamField]) ? "draft-board-filter-active" : ""}`}
	                        value={quickSearchValues[league.teamField]}
	                        onChange={(event) => updateQuickSearchValue(league.teamField, event.target.value)}
	                      >
	                        <option value="-">Team</option>
	                        {league.teams.map((team) => (
	                          <option key={`${league.label}-${team.Team}`} value={team.Team}>
	                            {team.Team_Name || team.Team}
	                          </option>
	                        ))}
	                      </select>
	                      {hasFilterValue(quickSearchValues[league.teamField]) && (
	                        <button type="button" className="draft-board-filter-clear" onClick={() => updateQuickSearchValue(league.teamField, "-")}>
	                          X
	                        </button>
	                      )}
	                    </div>
	                    <div className={`draft-board-filter-input-wrap draft-board-filter-input-wrap-select ${hasFilterValue(quickSearchValues[league.statusField]) ? "draft-board-filter-input-wrap-has-clear" : ""}`}>
	                      <select
	                        className={`draft-board-quick-search-input ${hasFilterValue(quickSearchValues[league.statusField]) ? "draft-board-filter-active" : ""}`}
	                        value={quickSearchValues[league.statusField]}
	                        onChange={(event) => updateQuickSearchValue(league.statusField, event.target.value)}
	                      >
	                        <option value="-">Status</option>
	                        {getVisibleStatusOptions(league.statuses).map((status) => (
	                          <option key={`${league.label}-${status.status_id}`} value={status.status_id}>
	                            {status.status || status.status_id}
	                          </option>
	                        ))}
	                      </select>
	                      {hasFilterValue(quickSearchValues[league.statusField]) && (
	                        <button type="button" className="draft-board-filter-clear" onClick={() => updateQuickSearchValue(league.statusField, "-")}>
	                          X
	                        </button>
		            )}
		          </div>
                    <FBXCalendar
                      fieldName={league.startDateField}
                      dateIn={quickSearchValues[league.startDateField]}
                      minYear="1950"
                      maxYear="2035"
                      placeholder="From Date"
                      onDateChange={(value) => updateQuickSearchValue(league.startDateField, value || "")}
                      onToggleCalendar={toggleQuickSearchCalendar}
                      isOpen={openQuickSearchCalendar === league.startDateField}
                      style={{ width: "100%" }}
                    />
                    <FBXCalendar
                      fieldName={league.endDateField}
                      dateIn={quickSearchValues[league.endDateField]}
                      minYear="1950"
                      maxYear="2035"
                      placeholder="To Date"
                      onDateChange={(value) => updateQuickSearchValue(league.endDateField, value || "")}
                      onToggleCalendar={toggleQuickSearchCalendar}
                      isOpen={openQuickSearchCalendar === league.endDateField}
                      style={{ width: "100%" }}
                    />
		        </div>
			                ))}
                <div className="draft-board-quick-search-footer">
                  <span className="draft-board-quick-search-message">{quickSearchMessage}</span>
                  <button
                    type="button"
                    className="draft-board-quick-search-action draft-board-quick-search-clear"
                    onClick={clearQuickSearch}
                    disabled={quickSearchLoading}
                  >
                    Clear All
                  </button>
                  <button
                    type="button"
                    className="draft-board-quick-search-action"
                    onClick={() => setQuickSearchOpen(false)}
                    disabled={quickSearchLoading}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="draft-board-quick-search-action draft-board-quick-search-submit"
                    onClick={runDraftBoardQuickSearch}
                    disabled={quickSearchLoading}
                  >
                    {quickSearchLoading ? "Searching..." : "Search"}
                  </button>
	          </div>
		        </div>
	            )}
	          </div>
	          <div className="draft-board-top-action-divider" />
	          <button
	            type="button"
	            className={`draft-board-add-players-trigger ${playersOpen ? "draft-board-add-players-trigger-active" : ""}`}
	            onClick={() => {
                const nextOpen = !playersOpen;
                closeWhiteBoardPopups(nextOpen ? "players" : "");
                setPlayersOpen(nextOpen);
              }}
	          >
	            Searched Players
	          </button>
	        </div>
        <div className="draft-board-title-block">
          {isEditingBoardName ? (
            <div className="draft-board-name-edit">
              <input
                type="text"
                className="draft-board-name-edit-input"
                value={editBoardName}
                placeholder="Board name"
                maxLength={120}
                onChange={(event) => {
                  setEditBoardName(event.target.value);
                  if (editBoardNameError) setEditBoardNameError("");
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") saveBoardNameEdit();
                  if (event.key === "Escape") cancelBoardNameEdit();
                }}
                autoFocus
              />
              <button
                type="button"
                className="draft-board-name-icon-button draft-board-name-save-button"
                onClick={saveBoardNameEdit}
                title="Save board name"
              >
                Save
              </button>
              <button
                type="button"
                className="draft-board-name-icon-button draft-board-name-cancel-button"
                onClick={cancelBoardNameEdit}
                title="Cancel"
              >
                X
              </button>
              {savedBoardId && currentBoardIsOwner && (
                <button
                  type="button"
                  className="draft-board-name-icon-button draft-board-name-delete-button"
                  onClick={() => deleteSavedBoard()}
                  title="Delete board"
                >
                  Delete
                </button>
              )}
              {editBoardNameError && <div className="draft-board-name-edit-error">{editBoardNameError}</div>}
            </div>
          ) : (
            <div className="draft-board-current-name">
              <span className={`draft-board-current-name-text ${currentBoardIsShared ? "draft-board-current-name-shared" : ""}`}>{boardDisplayName}</span>
              {!savedBoardId && <span className="draft-board-unsaved-label">(not saved)</span>}
              {currentBoardIsShared && (
                <span className={`draft-board-share-label ${currentShareBadgeCanEdit ? "draft-board-share-label-edit" : ""}`}>
                  {currentShareBadgeCanEdit ? "shared editable" : "shared view only"}
                </span>
              )}
              {savedBoardId && currentBoardIsOwner && (
                <button
                  type="button"
                  className="draft-board-name-pencil"
                  onClick={startBoardNameEdit}
                  title="Edit board name"
                >
                  Edit Board
                </button>
              )}
            </div>
          )}
        </div>
        <div className="draft-board-actions">
          {savedBoardId && currentBoardIsOwner && (
            <div className="draft-board-share-dropdown">
              <button
                type="button"
                className={`draft-board-button draft-board-share-button ${showShareBoardForm ? "draft-board-button-active" : ""} ${currentBoardShareCount > 0 ? "draft-board-share-button-shared" : ""}`}
                onClick={openShareBoardMenu}
                title={currentBoardShareCount > 0 ? "Manage sharing" : "Share board"}
              >
                <img src={shareIcon} alt="" aria-hidden="true" />
                <span>Share</span>
              </button>
              {showShareBoardForm && (
                <div className="draft-board-share-menu">
                  <div className="draft-board-share-form">
                    {isAdminUser && <div className="draft-board-save-form-title">Share With Teams</div>}
                    <label className="draft-board-share-editable">
                      <input
                        type="checkbox"
                        checked={shareCanEdit}
                        disabled={shareLoading}
                        onChange={(event) => updateShareCanEdit(event.target.checked)}
                      />
                      Editable by shared scouts
                    </label>
                    {canUseShareToFbx && (
                      <label className="draft-board-share-fbx-toggle">
                        <input
                          type="checkbox"
                          checked={isSharedToFbx}
                          disabled={shareLoading || fbxShareScoutIds.length === 0}
                          onChange={(event) => updateSharedToFbx(event.target.checked)}
                        />
                        Share to FBX
                      </label>
                    )}
                    {(shareScoutIds.length > 0 || currentBoardShareCount > 0) && (
                      <button
                        type="button"
                        className="draft-board-share-clear-button"
                        disabled={shareLoading}
                        onClick={removeAllBoardShares}
                      >
                        Remove All Sharing
                      </button>
                    )}
                    {isAdminUser ? (
                      <div className="draft-board-share-team-list">
                        {shareLoading || adminShareLoading ? (
                          <div className="draft-board-board-empty">Loading CFL scouts...</div>
                        ) : (
                          cflShareTeams.map((team) => {
                            const teamScouts = adminShareScoutsByTeam[team.code] || [];
                            const selectedCount = teamScouts.filter((scout) => shareScoutIds.includes(scout.id)).length;
                            const isExpanded = expandedShareTeam === team.code;

                            return (
                              <div className="draft-board-share-team" key={team.code}>
                                <button
                                  type="button"
                                  className={`draft-board-share-team-trigger ${isExpanded ? "draft-board-share-team-trigger-active" : ""}`}
                                  onClick={() => setExpandedShareTeam((current) => current === team.code ? "" : team.code)}
                                >
                                  <span>{team.name}</span>
                                  <span>{selectedCount}/{teamScouts.length}</span>
                                </button>
                                {isExpanded && (
                                  <div className="draft-board-share-list draft-board-share-list-team">
                                    {teamScouts.length === 0 ? (
                                      <div className="draft-board-board-empty draft-board-board-empty-small">No active scouts.</div>
                                    ) : (
                                      teamScouts.map((scout) => (
                                        <label className="draft-board-share-scout-row" key={scout.id}>
                                          <input
                                            type="checkbox"
                                            checked={shareScoutIds.includes(scout.id)}
                                            disabled={shareLoading || adminShareLoading}
                                            onChange={(event) => updateSharedScout(scout.id, event.target.checked)}
                                          />
                                          <span>{scout.name}</span>
                                        </label>
                                      ))
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    ) : (
                      <div className="draft-board-share-list">
                        {shareLoading ? (
                          <div className="draft-board-board-empty">Loading shares...</div>
                        ) : shareScoutOptions.length === 0 ? (
                          <div className="draft-board-board-empty">No scouts available.</div>
                        ) : (
                          shareScoutOptions.map((scout) => (
                            <label className="draft-board-share-scout-row" key={scout.id}>
                              <input
                                type="checkbox"
                                checked={shareScoutIds.includes(scout.id)}
                                disabled={shareLoading}
                                onChange={(event) => updateSharedScout(scout.id, event.target.checked)}
                              />
                              <span>{scout.name}</span>
                            </label>
                          ))
                        )}
                      </div>
                    )}
                    {adminShareMessage && <div className="draft-board-share-message">{adminShareMessage}</div>}
                    {shareMessage && <div className="draft-board-share-message">{shareMessage}</div>}
                  </div>
                </div>
              )}
            </div>
          )}
          {savedBoardId && currentBoardIsOwner && <div className="draft-board-action-divider" aria-hidden="true" />}
          <div className="draft-board-action-menu-dropdown">
            <button
              type="button"
              className={`draft-board-button draft-board-hamburger-button ${actionMenuOpen ? "draft-board-button-active" : ""}`}
              onClick={openActionMenu}
              title="Board actions"
              aria-label="Board actions"
              aria-expanded={actionMenuOpen}
            >
              <span className="draft-board-hamburger-lines" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </button>
            {actionMenuOpen && (
              <div className="draft-board-action-menu">
                <button
                  type="button"
                  className="draft-board-dropdown-item"
                  onClick={() => printBoard()}
                >
                  Print
                </button>
                <button
                  type="button"
                  className="draft-board-dropdown-item"
                  onClick={openBoardPreferences}
                >
                  Board Specific Preferences
                </button>
              </div>
            )}
          </div>
          <div className="draft-board-dropdown">
            <button
              type="button"
              className={`draft-board-button draft-board-dropdown-trigger ${boardsOpen ? "draft-board-button-active" : ""}`}
              onClick={openBoardsMenu}
            >
              Boards
            </button>
            {boardsOpen && (
              <div className="draft-board-dropdown-menu draft-board-boards-menu">
                <div className="draft-board-save-form-title">
                  Boards <span className="draft-board-shared-legend">Underlined Board Names are Shared Boards</span>
                </div>
                <button
                  type="button"
                  className="draft-board-dropdown-item draft-board-new-board-trigger"
                  onClick={() => {
                    const nextOpen = !showNewBoardForm;
                    closeWhiteBoardPopups(nextOpen ? "newBoard" : "boards");
                    setBoardsOpen(true);
                    setShowNewBoardForm(nextOpen);
                    setSaveBoardName("");
                    setSaveBoardNameError("");
                  }}
                >
                  Create New Board
                </button>
                {showNewBoardForm && (
                  <div className="draft-board-save-new-form">
                    <input
                      type="text"
                      className="draft-board-save-name-input"
                      placeholder="Board name"
                      value={saveBoardName}
                      maxLength={120}
                      onChange={(event) => {
                        setSaveBoardName(event.target.value);
                        if (saveBoardNameError) setSaveBoardNameError("");
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && saveBoardName.trim().length >= 10) handleNamedBoardSave();
                        if (event.key === "Escape") {
                          setShowNewBoardForm(false);
                          setSaveBoardName("");
                          setSaveBoardNameError("");
                        }
                      }}
                      autoFocus
                    />
                    <div className="draft-board-save-name-meta">
                      {saveBoardName.trim().length}/10 minimum
                    </div>
                    {saveBoardNameError && (
                      <div className="draft-board-save-name-error">{saveBoardNameError}</div>
                    )}
                    <div className="draft-board-save-name-actions">
                      <button
                        type="button"
                        className="draft-board-dropdown-item draft-board-save-name-button"
                        disabled={saveBoardName.trim().length < 10}
                        onClick={handleNamedBoardSave}
                      >
                        Create
                      </button>
                      <button
                        type="button"
                        className="draft-board-dropdown-item draft-board-save-name-cancel-button"
                        onClick={() => {
                          setShowNewBoardForm(false);
                          setBoardsOpen(false);
                          setSaveBoardName("");
                          setSaveBoardNameError("");
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                <div className="draft-board-board-list">
                  {boardsLoading ? (
                    <div className="draft-board-board-empty">Loading boards...</div>
                  ) : boardsError ? (
                    <div className="draft-board-board-empty">{boardsError}</div>
                  ) : savedBoards.length === 0 ? (
                    <div className="draft-board-board-empty">No saved boards.</div>
                  ) : (
                    <>
                      <div className="draft-board-board-section-title">My Boards</div>
                      {myBoards.length === 0 ? (
                        <div className="draft-board-board-empty draft-board-board-empty-small">No boards saved by you.</div>
                      ) : (
                        myBoards.map(renderBoardRow)
                      )}
                      <div className="draft-board-board-divider" />
                      <div className="draft-board-board-section-title">Shared Boards</div>
                      {sharedBoards.length === 0 ? (
                        <div className="draft-board-board-empty draft-board-board-empty-small">No shared boards.</div>
                      ) : (
                        sharedBoards.map(renderBoardRow)
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          <button
            type="button"
            className="draft-board-button"
            onClick={() => updateActiveSideButton("")}
          >
            Close
          </button>
        </div>
        </div>
        {boardPreferencesOpen && (
          <div className="draft-board-preferences-backdrop">
            <div className="draft-board-preferences-modal" role="dialog" aria-modal="true" aria-labelledby="draft-board-preferences-title">
              <div className="draft-board-preferences-header">
                <div>
                  <div className="draft-board-preferences-title" id="draft-board-preferences-title">Board Specific Preferences</div>
                  <div className="draft-board-preferences-subtitle">Settings saved to this board only.</div>
                </div>
                <button
                  type="button"
                  className="draft-board-preferences-close"
                  onClick={() => setBoardPreferencesOpen(false)}
                  aria-label="Close board preferences"
                >
                  X
                </button>
              </div>
              <div className="draft-board-position-preference-row">
                <div>
                  <div className="draft-board-position-preference-title">Position Card Color</div>
                  <div className="draft-board-position-preference-note">
                    Position boxes on this board use this color.
                  </div>
                </div>
                <label className="draft-board-position-preference-control">
                  <span
                    className="draft-board-position-preference-preview"
                    style={{
                      backgroundColor: boardPreferenceColorDraft,
                      color: getReadableTextColor(boardPreferenceColorDraft),
                    }}
                  >
                    POS
                  </span>
                  <input
                    type="color"
                    value={boardPreferenceColorDraft}
                    onChange={(event) => {
                      setBoardPreferenceColorDraft(normalizeWhiteBoardPositionMarkerColor(event.target.value));
                      setBoardPreferencesMessage("");
                    }}
                    disabled={!canEditCurrentBoard}
                    aria-label="Board position card color"
                  />
                </label>
              </div>
              <div className="draft-board-position-preference-row draft-board-base-team-preference-row">
                <div>
                  <div className="draft-board-position-preference-title">Board Base Team</div>
                  <div className="draft-board-position-preference-note">
                    Used to check if players are still on this board&apos;s CFL or NFL team. Choose No Base Team to skip that warning.
                  </div>
                </div>
                <div className="draft-board-base-team-preference-controls">
                  <label>
                    <span>League</span>
                    <select
                      value={boardPreferenceBaseTeamLeagueDraft}
                      onChange={(event) => {
                        const nextLeague = event.target.value;
                        const nextOptions = getBaseTeamOptions(nextLeague, "").filter((team) => team.code);
                        setBoardPreferenceBaseTeamLeagueDraft(nextLeague);
                        setBoardPreferenceBaseTeamCodeDraft(boardPreferenceBaseTeamCodeDraft === "" ? "" : (nextOptions[0]?.code || ""));
                        setBoardPreferencesMessage("");
                      }}
                      disabled={!canEditCurrentBoard}
                    >
                      {WHITE_BOARD_BASE_TEAM_LEAGUES.map((league) => (
                        <option value={league} key={league}>{league}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>Team</span>
                    <select
                      value={boardPreferenceBaseTeamCodeDraft}
                      onChange={(event) => {
                        setBoardPreferenceBaseTeamCodeDraft(event.target.value);
                        setBoardPreferencesMessage("");
                      }}
                      disabled={!canEditCurrentBoard}
                    >
                      {getBaseTeamOptions(boardPreferenceBaseTeamLeagueDraft, boardPreferenceBaseTeamCodeDraft).map((team) => (
                        <option value={team.code} key={team.code}>
                          {team.code ? `${team.code} - ${team.name}` : team.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>
              <div className="draft-board-position-preference-row draft-board-base-team-preference-row">
                <div>
                  <div className="draft-board-position-preference-title">Player Position</div>
                  <div className="draft-board-position-preference-note">
                    College Position uses the college position. Pro Position uses CFLGP or NFLGP based on the board league.
                  </div>
                </div>
                <div className="draft-board-number-toggle">
                  {WHITE_BOARD_POSITION_DISPLAY_TYPES.map((item) => (
                    <button
                      type="button"
                      key={item.code}
                      className={boardPreferencePositionDisplayTypeDraft === item.code ? "active" : ""}
                      onClick={() => {
                        setBoardPreferencePositionDisplayTypeDraft(item.code);
                        setBoardPreferencesMessage("");
                      }}
                      disabled={!canEditCurrentBoard}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="draft-board-position-preference-row draft-board-base-team-preference-row">
                <div>
                  <div className="draft-board-position-preference-title">Player Number</div>
                  <div className="draft-board-position-preference-note">
                    Choose which number appears on player cards when the board refreshes.
                  </div>
                </div>
                <div className="draft-board-number-toggle">
                  {WHITE_BOARD_NUMBER_DISPLAY_TYPES.map((item) => (
                    <button
                      type="button"
                      key={item.code}
                      className={boardPreferenceNumberDisplayTypeDraft === item.code ? "active" : ""}
                      onClick={() => {
                        setBoardPreferenceNumberDisplayTypeDraft(item.code);
                        setBoardPreferencesMessage("");
                      }}
                      disabled={!canEditCurrentBoard}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
              {boardPreferencesMessage && (
                <div className="draft-board-preferences-message">{boardPreferencesMessage}</div>
              )}
              <div className="draft-board-preferences-actions">
                <button
                  type="button"
                  className="draft-board-preferences-secondary"
                  onClick={() => {
                    setBoardPreferenceColorDraft(DEFAULT_WHITE_BOARD_POSITION_MARKER_COLOR);
                    setBoardPreferenceBaseTeamLeagueDraft("CFL");
                    setBoardPreferenceBaseTeamCodeDraft("");
                    setBoardPreferenceNumberDisplayTypeDraft("pro");
                    setBoardPreferencePositionDisplayTypeDraft("pro");
                    setBoardPreferencesMessage("");
                  }}
                  disabled={!canEditCurrentBoard}
                >
                  Default
                </button>
                <button
                  type="button"
                  className="draft-board-preferences-secondary"
                  onClick={() => setBoardPreferencesOpen(false)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="draft-board-preferences-primary"
                  onClick={saveBoardPreferences}
                  disabled={!canEditCurrentBoard}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="draft-board-page-row">
          <div className="draft-board-page-bar">
            <div className="draft-board-page-tabs">
              {boardPages.map((page) => {
                const hasHighlightedPlayer = pageHasHighlightedPlayer(page);
                return (
                  <button
                    type="button"
                    key={page.pageId}
                    className={`draft-board-page-tab ${page.pageId === activePageId ? "draft-board-page-tab-active" : ""} ${page.pageId === pageTransferHoverId ? "draft-board-page-tab-transfer-hover" : ""} ${hasHighlightedPlayer ? "draft-board-page-tab-has-highlighted-player" : ""}`}
                    onClick={() => switchBoardPage(page.pageId)}
                    onDragEnter={(event) => {
                      if (!canEditCurrentBoard || page.pageId === activePageId) return;
                      const dragTypes = Array.from(event.dataTransfer?.types || []);
                      if (dragTypes.includes("application/fbx-draft-board-item")) {
                        event.preventDefault();
                        schedulePageTransferOpen(page);
                      }
                    }}
                    onDragOver={(event) => {
                      if (!canEditCurrentBoard || page.pageId === activePageId) return;
                      const dragTypes = Array.from(event.dataTransfer?.types || []);
                      if (dragTypes.includes("application/fbx-draft-board-item")) {
                        event.preventDefault();
                        event.dataTransfer.dropEffect = "move";
                        schedulePageTransferOpen(page);
                      }
                    }}
                    onDragLeave={() => {
                      if (pageTransferHoverId === page.pageId) clearPageTransferHover();
                    }}
                    onDrop={(event) => {
                      const boardItemId = event.dataTransfer.getData("application/fbx-draft-board-item");
                      if (!boardItemId) return;
                      event.preventDefault();
                      event.stopPropagation();
                      const sourcePageId = pageTransferDragRef.current?.sourcePageId || activePageId;
                      moveBoardPlayerToPage(boardItemId, page.pageId, null, sourcePageId);
                      clearPageTransferDrag();
                    }}
                    title={hasHighlightedPlayer ? `${page.pageName} - selected player is on this page` : page.pageName}
                  >
	                    {page.pageName}
	                    {normalizeBoardPageType(page.pageType) !== BOARD_PAGE_TYPES.regular && (
	                      <span className="draft-board-page-tab-type">{getBoardPageTypeLabel(page.pageType, "badgeLabel")}</span>
	                    )}
                  </button>
                );
              })}
              {canEditCurrentBoard && (
                <button
                  type="button"
                  className="draft-board-page-add"
                  onClick={() => {
                    const nextOpen = !showNewPageForm;
                    closeWhiteBoardPopups(nextOpen ? "newPage" : "");
                    setShowNewPageForm(nextOpen);
                    setNewPageName("");
                    setNewPageType(BOARD_PAGE_TYPES.regular);
                    setNewPageNameError("");
                  }}
                  title="Add page"
                >
                  Add Page
                </button>
              )}
              {canEditCurrentBoard && boardPages.length > 0 && (
                <button
                  type="button"
                  className={`draft-board-page-edit ${pageEditorOpen ? "draft-board-page-edit-active" : ""}`}
                  onClick={openPageEditor}
                  title="Edit page names and order"
                >
                  Edit Pages
                </button>
              )}
            </div>
          </div>
          {showNewPageForm && (
            <div className="draft-board-page-modal-backdrop" role="presentation">
              <div className="draft-board-page-form" role="dialog" aria-modal="true" aria-labelledby="draft-board-add-page-title">
                <div id="draft-board-add-page-title" className="draft-board-page-form-title">
                  Add Page to {boardName}
                </div>
                <div className="draft-board-page-form-note">
                  This will add the page at the end of the list, but you can re-order the pages using the Edit Pages button.
                </div>
	                <div className="draft-board-page-type-title">Page Type</div>
	                <div className="draft-board-page-type-control" aria-label="Page type">
	                  {BOARD_PAGE_TYPE_OPTIONS.map((option) => (
	                    <button
	                      key={option.type}
	                      type="button"
	                      className={`draft-board-page-type-button ${newPageType === option.type ? "draft-board-page-type-button-active" : ""}`}
	                      onClick={() => setNewPageType(option.type)}
	                    >
	                      {option.label}
	                    </button>
	                  ))}
	                </div>
                <div className="draft-board-page-name-title">Page Name</div>
                <input
                  type="text"
                  className="draft-board-page-input"
                  value={newPageName}
                  placeholder="Page name"
                  maxLength={60}
                  onChange={(event) => {
                    setNewPageName(event.target.value);
                    if (newPageNameError) setNewPageNameError("");
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") addBoardPage();
                    if (event.key === "Escape") {
                      setShowNewPageForm(false);
                      setNewPageName("");
                      setNewPageType(BOARD_PAGE_TYPES.regular);
                      setNewPageNameError("");
                    }
                  }}
                  autoFocus
                />
                {newPageNameError && <div className="draft-board-page-error">{newPageNameError}</div>}
                <div className="draft-board-page-form-actions">
                  <button
                    type="button"
                    className="draft-board-page-cancel"
                    onClick={() => {
                      setShowNewPageForm(false);
                      setNewPageName("");
                      setNewPageType(BOARD_PAGE_TYPES.regular);
                      setNewPageNameError("");
                    }}
                  >
                    Cancel
                  </button>
                  <button type="button" className="draft-board-page-save" onClick={addBoardPage}>Add Page</button>
                </div>
              </div>
            </div>
          )}
	          {pageEditorOpen && (
	            <div className="draft-board-page-editor">
	              <div className="draft-board-page-editor-help">Drag pages to reorder them. Check the box to include that page in Board counts. Hide warnings removes red roster warnings from that page.</div>
	              <div className="draft-board-page-editor-list">
	                {pageEditorDraft.map((page) => (
	                  <div
	                    className="draft-board-page-editor-row"
	                    key={page.pageId}
	                    draggable
	                    onDragStart={(event) => {
	                      pageEditorDragPageIdRef.current = page.pageId;
	                      event.dataTransfer.setData("text/plain", page.pageId);
	                      event.dataTransfer.effectAllowed = "move";
	                    }}
	                    onDragOver={(event) => {
	                      event.preventDefault();
	                      event.dataTransfer.dropEffect = "move";
	                      movePageEditorRow(pageEditorDragPageIdRef.current, page.pageId);
	                    }}
	                    onDrop={(event) => {
	                      event.preventDefault();
	                      pageEditorDragPageIdRef.current = "";
	                    }}
	                    onDragEnd={() => {
	                      pageEditorDragPageIdRef.current = "";
	                    }}
	                  >
	                    <div className="draft-board-page-drag-handle" title="Drag to reorder">Drag</div>
	                    <label
	                      className="draft-board-page-count-checkbox"
	                      title="Include this page in Board count"
	                      onMouseDown={(event) => event.stopPropagation()}
	                      onDragStart={(event) => event.preventDefault()}
	                    >
	                      <input
	                        type="checkbox"
	                        checked={page.includeInBoardCount !== false}
	                        onChange={(event) => updatePageEditorIncludeInBoardCount(page.pageId, event.target.checked)}
	                      />
	                    </label>
	                    <input
	                      type="text"
	                      className="draft-board-page-editor-input"
	                      value={page.pageName}
	                      maxLength={60}
	                      onChange={(event) => updatePageEditorName(page.pageId, event.target.value)}
	                    />
	                    <select
	                      className="draft-board-page-type-select"
	                      title="Page type"
	                      value={normalizeBoardPageType(page.pageType)}
	                      onChange={(event) => updatePageEditorType(page.pageId, event.target.value)}
	                    >
	                      {BOARD_PAGE_TYPE_OPTIONS.map((option) => (
	                        <option key={option.type} value={option.type}>{option.label}</option>
	                      ))}
	                    </select>
	                    <select
	                      className="draft-board-page-warning-select"
	                      title="Roster warnings"
	                      value={page.hideWarnings === true ? "hide" : "show"}
	                      onChange={(event) => updatePageEditorHideWarnings(page.pageId, event.target.value === "hide")}
	                    >
	                      <option value="show">Show Warnings</option>
	                      <option value="hide">Hide Warnings</option>
	                    </select>
	                    <button
	                      type="button"
	                      className="draft-board-page-delete-button"
	                      disabled={pageEditorDraft.length <= 1}
	                      onClick={() => deletePageEditorRow(page.pageId)}
	                      title="Delete page"
	                    >
	                      Delete
	                    </button>
	                  </div>
	                ))}
	              </div>
	              <div className="draft-board-page-editor-actions">
                <button type="button" className="draft-board-page-editor-save" onClick={savePageEditor}>
                  Save Pages
                </button>
                <button
                  type="button"
                  className="draft-board-page-editor-cancel"
                  onClick={() => {
                    setPageEditorOpen(false);
                    setPageEditorError("");
                  }}
                >
                  Cancel
                </button>
              </div>
              {pageEditorError && <div className="draft-board-page-editor-error">{pageEditorError}</div>}
            </div>
          )}
        </div>
      </div>

      <div className="draft-board-print-title">
        <div className="draft-board-print-heading">FBXchange.com</div>
        <div className="draft-board-print-name">{boardName} [{activePageName}]</div>
      </div>

	      <div className="draft-board-body">
	        <div className="draft-board-side-nav">
	          <button
	            type="button"
            className={`draft-board-side-nav-button ${onBoardOpen ? "draft-board-side-nav-button-active" : ""}`}
            onClick={() => {
              const nextOpen = !onBoardOpen;
              closeWhiteBoardPopups(nextOpen ? "onBoard" : "");
              setOnBoardOpen(nextOpen);
            }}
          >
            Placed Players
          </button>
          <button
            type="button"
            className={`draft-board-side-nav-button ${draftOpen ? "draft-board-side-nav-button-active" : ""}`}
            onClick={() => {
              const nextOpen = !draftOpen;
              closeWhiteBoardPopups(nextOpen ? "draft" : "");
              setDraftOpen(nextOpen);
            }}
          >
            Priority List
          </button>
          <button
            type="button"
            className={`draft-board-side-nav-button ${dataOpen ? "draft-board-side-nav-button-active" : ""}`}
            onClick={() => {
              const nextOpen = !dataOpen;
              closeWhiteBoardPopups(nextOpen ? "data" : "");
              setDataOpen(nextOpen);
            }}
          >
	            Player Data
	          </button>
          <button
            type="button"
            className="draft-board-side-nav-button draft-board-side-nav-button-muted"
            onClick={() => {
              closePresetsMenu();
              resetWhiteBoardPopups();
            }}
            title="Reset White Board popup positions"
          >
            Reset Popups
          </button>
          <div className="draft-board-side-nav-divider draft-board-count-divider" />
          <div className="draft-board-filter-pills" aria-label="Filter board players by IMPNIP">
            <div className="draft-board-filter-pills-label-row">
              <div className="draft-board-filter-pills-label"># on {countScope === "board" ? "Board" : "Page"}</div>
            </div>
            <div className="draft-board-count-scope-row">
              <div
                className={`draft-board-count-scope-toggle draft-board-count-scope-toggle-${countScope}`}
                role="switch"
                aria-checked={countScope === "board"}
                aria-label={`Count by ${countScope === "board" ? "board" : "page"}`}
                tabIndex={0}
                title={`Counting ${countScope === "board" ? "unique players on the full board" : "unique players on the current page"}`}
                onClick={() => {
                  closePresetsMenu();
                  setCountScope((scope) => (scope === "board" ? "page" : "board"));
                }}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  closePresetsMenu();
                  setCountScope((scope) => (scope === "board" ? "page" : "board"));
                }}
              >
                <span className="draft-board-count-scope-slider" />
              </div>
            </div>
            {[
              { value: "N", count: boardImpNipCounts.N, title: "Nationals", className: "draft-board-filter-pill-n" },
              { value: "G", count: boardImpNipCounts.G, title: "Globals", className: "draft-board-filter-pill-g" },
              { value: "V", count: boardImpNipCounts.V, title: "Nationalized Americans", className: "draft-board-filter-pill-v" },
              { value: "U", count: boardImpNipCounts.U, title: "Americans", className: "draft-board-filter-pill-u" },
            ].map((pill) => (
              <button
                type="button"
                key={pill.value}
                className={`draft-board-filter-pill ${pill.className} ${boardImpNipFilter === pill.value ? "draft-board-filter-pill-active" : ""}`}
                onClick={() => {
                  closePresetsMenu();
                  setBoardImpNipFilter((current) => current === pill.value ? "" : pill.value);
                }}
                title={pill.title}
                disabled={pill.count <= 0}
              >
                {pill.count}
              </button>
            ))}
            <div className="draft-board-filter-board-total">
              {countScope === "board" ? uniqueBoardPlayerTotal : uniquePagePlayerTotal} Players
            </div>
          </div>
          <div className="draft-board-side-nav-divider draft-board-tool-divider" />
          <div className="draft-board-overlay-dropdown">
            <button
              type="button"
              className={`draft-board-board-tool-button ${overlaysOpen ? "draft-board-board-tool-button-active" : ""}`}
              onClick={togglePresetsMenu}
              title="Board page presets"
            >
              Page Presets
            </button>
            {overlaysOpen && (
              <div
                className="draft-board-overlay-menu"
                style={{
                  left: `${presetsPopupPosition.left}px`,
                  top: `${presetsPopupPosition.top}px`,
                }}
              >
                <div
                  className="draft-board-overlay-header"
                  onMouseDown={handlePresetsPopupMouseDown}
                >
                  <span>Page Presets</span>
                  <button
                    type="button"
                    className="draft-board-overlay-close"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={closePresetsMenu}
                    aria-label="Close page presets"
                  >
                    X
                  </button>
                </div>
                <div className="draft-board-overlay-body">
                  <div className="draft-board-overlay-section">
                    <div className="draft-board-overlay-section-title">System Page Presets</div>
                    {BOARD_PRESET_TYPES.map((type) => {
                      const overlay = BOARD_OVERLAY_TYPES[type];
                      return (
                        <button
                          type="button"
                          className="draft-board-overlay-option"
                          key={type}
                          onClick={() => addBoardOverlay(type)}
                        >
                          {overlay.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="draft-board-overlay-section draft-board-overlay-section-saved">
                    <button
                      type="button"
                      className="draft-board-overlay-save"
                      disabled={!canSavePresetAs || !canEditCurrentBoard}
                      onClick={handleSavePresetAs}
                    >
                      Create New Page Preset
                    </button>
                    {teamPresetMessage && (
                      <div className="draft-board-overlay-message">{teamPresetMessage}</div>
                    )}
                    <div className="draft-board-overlay-section-title">Saved Page Presets</div>
                    {teamPresetsLoading && (
                      <div className="draft-board-overlay-empty">Loading...</div>
                    )}
                    {!teamPresetsLoading && teamPresets.length === 0 && (
                      <div className="draft-board-overlay-empty">No saved page presets</div>
                    )}
                    {!teamPresetsLoading && teamPresets.map((preset) => {
                      const presetId = Number(preset?.presetId || 0);
                      const isActivePreset = activePresetDraft?.source === "team" && Number(activePresetDraft?.presetId || 0) === presetId;
                    return (
                      <div className="draft-board-overlay-preset-row" key={presetId || preset?.presetName}>
                        <button
                          type="button"
                          className={`draft-board-overlay-option ${isActivePreset ? "draft-board-overlay-option-active" : ""}`}
                          onClick={() => addTeamPreset(preset)}
                        >
                          {preset?.presetName || "Team Page Preset"}
                        </button>
                        <button
                          type="button"
                          className={`draft-board-overlay-small-button ${isActivePreset && activePresetModified ? "draft-board-overlay-small-button-update-active" : ""}`}
                          disabled={!isActivePreset || !activePresetModified || !canEditCurrentBoard}
                          onClick={() => handleUpdatePreset(preset)}
                          title="Update this team page preset"
                        >
                          Update
                        </button>
                        <button
                          type="button"
                          className="draft-board-overlay-small-button"
                          disabled={!canEditCurrentBoard}
                          onClick={() => handleRenamePreset(preset)}
                          title="Rename this team page preset"
                        >
                          Rename
                        </button>
                        <button
                          type="button"
                          className="draft-board-overlay-small-button draft-board-overlay-small-button-danger"
                          disabled={!canEditCurrentBoard}
                          onClick={() => deleteTeamPreset(preset)}
                          title="Delete this team page preset"
                        >
                          Delete
                        </button>
                      </div>
                    );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
          {pendingPresetChoice && (
            <div className="draft-board-preset-choice-backdrop" role="dialog" aria-modal="true">
              <div className="draft-board-preset-choice">
                <div className="draft-board-preset-choice-title">Place Page Preset</div>
                <div className="draft-board-preset-choice-note">
                  {pendingPresetChoice.activePageName || activePageName} already has data.
                </div>
                <div className="draft-board-preset-choice-actions">
                  <button
                    type="button"
                    className="draft-board-preset-choice-button draft-board-preset-choice-button-danger"
                    onClick={() => completePendingPresetChoice("erase")}
                  >
                    Erase Current Page and Place Page Preset
                  </button>
                  <button
                    type="button"
                    className="draft-board-preset-choice-button"
                    onClick={() => completePendingPresetChoice("new")}
                  >
                    Create New Page
                  </button>
                  <button
                    type="button"
                    className="draft-board-preset-choice-button draft-board-preset-choice-button-cancel"
                    onClick={() => setPendingPresetChoice(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          <button
            type="button"
            className={`draft-board-board-tool-button ${groupSelectOpen ? "draft-board-board-tool-button-active" : ""}`}
            disabled={!canEditCurrentBoard}
            onClick={() => {
              closePresetsMenu();
              setGroupSelectOpen((open) => {
                if (open) clearBoardSelection();
                return !open;
              });
            }}
            title="Select multiple board items to move together"
          >
            Group{selectedBoardItemCount > 0 ? ` (${selectedBoardItemCount})` : ""}
          </button>
          <button
            type="button"
            className="draft-board-board-tool-button"
            disabled={!canEditCurrentBoard || (boardPlayers.length + boardLabels.length + boardDetails.length + boardOverlays.length) === 0}
            onClick={() => {
              closePresetsMenu();
              selectAllBoardItems();
            }}
            title="Select every item on this board page"
          >
            Select All
          </button>
          {groupSelectOpen && selectedBoardItemCount > 0 && (
            <button
              type="button"
              className="draft-board-board-tool-button"
              onClick={() => {
                closePresetsMenu();
                clearBoardSelection();
                setGroupSelectOpen(false);
              }}
              title="Clear selected items"
            >
              Clear Select
            </button>
          )}
          {canAlignSelectedItems && (
            <div className="draft-board-align-menu">
              <button
                type="button"
                className={`draft-board-board-tool-button ${alignmentMenuOpen ? "draft-board-board-tool-button-active" : ""}`}
                onClick={() => {
                  const nextOpen = !alignmentMenuOpen;
                  closeWhiteBoardPopups(nextOpen ? "align" : "");
                  setAlignmentMenuOpen(nextOpen);
                }}
                title="Align or space selected board items"
                aria-expanded={alignmentMenuOpen}
              >
                Align
              </button>
              {alignmentMenuOpen && (
                <div className="draft-board-align-menu-flyout">
                  <div className="draft-board-align-menu-title">Horizontal</div>
                  <div className="draft-board-align-menu-row">
                    <button type="button" className="draft-board-align-option" onClick={() => alignSelectedBoardItems("left")}>Left</button>
                    <button type="button" className="draft-board-align-option" onClick={() => alignSelectedBoardItems("center")}>Center</button>
                    <button type="button" className="draft-board-align-option" onClick={() => alignSelectedBoardItems("right")}>Right</button>
                  </div>
                  <div className="draft-board-align-menu-title">Vertical</div>
                  <div className="draft-board-align-menu-row">
                    <button type="button" className="draft-board-align-option" onClick={() => alignSelectedBoardItems("top")}>Top</button>
                    <button type="button" className="draft-board-align-option" onClick={() => alignSelectedBoardItems("middle")}>Middle</button>
                    <button type="button" className="draft-board-align-option" onClick={() => alignSelectedBoardItems("bottom")}>Bottom</button>
                  </div>
                  <div className="draft-board-align-menu-title">Space</div>
                  <div className="draft-board-align-menu-row draft-board-align-menu-row-wide">
                    <button
                      type="button"
                      className="draft-board-align-option"
                      disabled={!canDistributeSelectedItems}
                      onClick={() => distributeSelectedBoardItems("horizontal")}
                    >
                      Horizontal
                    </button>
                    <button
                      type="button"
                      className="draft-board-align-option"
                      disabled={!canDistributeSelectedItems}
                      onClick={() => distributeSelectedBoardItems("vertical")}
                    >
                      Vertical
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="draft-board-clear-menu">
            <button
              type="button"
              className="draft-board-clear-page-button"
              disabled={!canEditCurrentBoard || (boardPlayers.length + draftPlayers.length + boardLabels.length + boardDetails.length + boardOverlays.length) === 0}
              onClick={() => {
                const nextOpen = !clearOptionsOpen;
                closeWhiteBoardPopups(nextOpen ? "clear" : "");
                setClearOptionsOpen(nextOpen);
              }}
              title="Clear players or the complete page"
              aria-expanded={clearOptionsOpen}
            >
              Clear
            </button>
            {clearOptionsOpen && (
              <div className="draft-board-clear-menu-flyout">
                <button
                  type="button"
                  className="draft-board-clear-menu-option draft-board-clear-menu-option-players"
                  disabled={!canEditCurrentBoard || (boardPlayers.length + draftPlayers.length) === 0}
                  onClick={() => {
                    closePresetsMenu();
                    setClearOptionsOpen(false);
                    clearCurrentPagePlayers();
                  }}
                  title="Clear players from this page and keep positions"
                >
                  Players
                </button>
                <button
                  type="button"
                  className="draft-board-clear-menu-option"
                  disabled={!canEditCurrentBoard || (boardPlayers.length + draftPlayers.length + boardLabels.length + boardDetails.length + boardOverlays.length) === 0}
                  onClick={() => {
                    closePresetsMenu();
                    setClearOptionsOpen(false);
                    clearCurrentPageItems();
                  }}
                  title="Clear all items from this page"
                >
                  Complete Board
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            className="draft-board-undo-button"
            onClick={() => {
              closePresetsMenu();
              undoLastBoardChange();
            }}
            disabled={!canEditCurrentBoard || !undoSnapshot}
            title="Undo the last board change"
          >
            Undo
          </button>
          <div className="draft-board-side-nav-divider" />
          <button
            type="button"
            className="draft-board-side-nav-button draft-board-side-nav-tutorial-button"
            onClick={() => {
              closeWhiteBoardPopups("tutorial");
              setTutorialOpen(true);
            }}
          >
            Tutorial
          </button>
          <div className="draft-board-view-menu">
            <button
              type="button"
              className="draft-board-side-nav-button draft-board-view-menu-trigger"
              onClick={() => {
                const nextOpen = !viewOptionsOpen;
                closeWhiteBoardPopups(nextOpen ? "view" : "");
                setViewOptionsOpen(nextOpen);
              }}
              aria-expanded={viewOptionsOpen}
            >
              {boardSizeModeLabel}
            </button>
            {viewOptionsOpen && (
              <div className="draft-board-view-menu-flyout">
                {[
                  { mode: "print", label: "Print Size" },
                  { mode: "grid", label: "Guide Right" },
                  { mode: "grid-centered", label: "Guide Centered" },
                ].map((option) => (
                  <button
                    type="button"
                    key={option.mode}
                    className={`draft-board-view-menu-option ${boardSizeMode === option.mode ? "draft-board-view-menu-option-active" : ""}`}
                    onClick={() => {
                      closePresetsMenu();
                      setBoardSizeMode(option.mode);
                      setViewOptionsOpen(false);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
	        <div
	          ref={whiteboardRef}
	          className={`draft-board-whiteboard ${isSplitSortPage ? "draft-board-whiteboard-split-sort" : ""} ${isFlippedPage ? "draft-board-whiteboard-flipped" : ""} ${!canEditCurrentBoard ? "draft-board-whiteboard-readonly" : ""}`}
          onDragOver={(event) => event.preventDefault()}
          onDrop={handleWhiteboardDrop}
        >
          {isGridSizeMode && <div className="draft-board-print-boundary" aria-hidden="true" />}
          {isSplitSortPage && (
            <div className="draft-board-split-sort-guide" aria-hidden="true">
              <div className="draft-board-split-sort-line" />
              <div className="draft-board-split-sort-marker draft-board-split-sort-marker-boundary">Boundary</div>
              <div className="draft-board-split-sort-marker draft-board-split-sort-marker-field">Field</div>
            </div>
          )}
          {renderBoardInfoStamp()}
          <div className={`draft-board-add-dropdown draft-board-add-dropdown-surface ${hasBoardInfoStamp ? "draft-board-add-dropdown-surface-below-info" : ""}`}>
            <button
              type="button"
              className={`draft-board-button draft-board-add-trigger ${addMenuOpen ? "draft-board-button-active" : ""}`}
              onClick={openAddMenu}
              title="Add to board"
            >
              Add
            </button>
            {addMenuOpen && (
              <div className="draft-board-add-menu">
                <button
                  type="button"
                  className="draft-board-dropdown-item"
                  onClick={() => {
                    closePresetsMenu();
                    addBoardLabel();
                    setAddMenuOpen(false);
                  }}
                >
                  Add Position
                </button>
                <button
                  type="button"
                  className="draft-board-dropdown-item"
                  onClick={() => {
                    closePresetsMenu();
                    addBoardDetail();
                    setAddMenuOpen(false);
                  }}
                >
                  Add Details
                </button>
              </div>
            )}
          </div>
          {boardOverlays.map((item) => {
            const overlayConfig = getBoardOverlayConfig(item.type);
            const isGameDayOverlay = String(overlayConfig.kind || "").startsWith("gameDay");

            return (
              <div
                key={item.overlayItemId}
                className={`draft-board-position-overlay ${isGameDayOverlay ? "draft-board-game-day-overlay" : "draft-board-oline-overlay"} ${isBoardItemSelected("overlay", item.overlayItemId) ? "draft-board-item-selected" : ""}`}
                draggable={canEditCurrentBoard && !(groupSelectOpen && selectedBoardItemCount > 1 && isBoardItemSelected("overlay", item.overlayItemId))}
                onMouseDown={(event) => handleGroupMoveMouseDown(event, "overlay", item.overlayItemId)}
                onClick={(event) => toggleBoardItemSelection(event, "overlay", item.overlayItemId)}
                onDragStart={(event) => handleOverlayDragStart(event, item)}
                style={{
                  ...getBoardItemPositionStyle(item, "overlay", "overlayItemId"),
                  gridTemplateColumns: overlayConfig.cells ? `repeat(${overlayConfig.cells.length}, 1fr)` : undefined,
                  width: getOverlayWidth(overlayConfig),
                  height: getOverlayHeight(overlayConfig),
                }}
              >
                {canEditCurrentBoard && (
                  <button
                    type="button"
                    className="draft-board-overlay-remove"
                    title="Delete page preset"
                    onClick={(event) => {
                      event.stopPropagation();
                      removeBoardOverlay(item.overlayItemId);
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                  >
                    X
                  </button>
                )}
                {isGameDayOverlay ? (
                  <GameDayLayoutOverlay type={overlayConfig.kind} />
                ) : (
                  overlayConfig.cells.map((label, index) => (
                    <div className="draft-board-position-overlay-cell" key={`${label}-${index}`}>
                      {label}
                    </div>
                  ))
                )}
              </div>
            );
          })}
          {visibleBoardPlayers.map((item) => {
            const isMutedByImpNipFilter = Boolean(boardImpNipFilter && getBoardPlayerImpNipGroup(item) !== boardImpNipFilter);
            const mutedImpNip = getBoardPlayerImpNip(item) || "U";
            const mutedImpNipLabel = mutedImpNip === "U" ? "USA" : mutedImpNip;

            return (
              <div
                key={item.boardItemId}
                className={`draft-board-card ${getBoardPlayerImpNipClass(item)} ${isMutedByImpNipFilter ? "draft-board-card-impnip-muted" : ""} ${!isMutedByImpNipFilter && !activePageHideWarnings && isPlayerRosterAlert(item) ? "draft-board-card-roster-alert" : ""} ${String(item.FBXID || "") === highlightedFBXID ? "draft-board-card-highlighted" : ""} ${isBoardItemSelected("player", item.boardItemId) ? "draft-board-item-selected" : ""}`}
                draggable={!isMutedByImpNipFilter && canEditCurrentBoard && !(groupSelectOpen && selectedBoardItemCount > 1 && isBoardItemSelected("player", item.boardItemId))}
                onMouseDown={(event) => {
                  if (isMutedByImpNipFilter) return;
                  handleGroupMoveMouseDown(event, "player", item.boardItemId);
                }}
                onClick={(event) => {
                  if (isMutedByImpNipFilter) return;
                  if (toggleBoardItemSelection(event, "player", item.boardItemId)) return;
                  const playerId = String(item.FBXID || "").trim();
                  if (playerId) setHighlightedFBXID((current) => current === playerId ? "" : playerId);
                }}
                onDragStart={(event) => {
                  if (isMutedByImpNipFilter) {
                    event.preventDefault();
                    return;
                  }
                  handleBoardCardDragStart(event, item);
                }}
                onDragEnd={clearPageTransferDrag}
                style={getBoardItemPositionStyle(item, "player", "boardItemId")}
              >
                {isMutedByImpNipFilter ? (
                  <span className="draft-board-card-impnip-muted-label">{mutedImpNipLabel}</span>
                ) : (() => {
                const playerName = [item.last, item.first].filter(Boolean).join(", ") || item.FBXID;
                const cardPlayerName = getPrintCardPlayerName(item, playerName);
                const playerGrade = getPlayerGrade(item.FBXID);

                return (
                  <>
              {canEditCurrentBoard && (
                <>
                  <button
                    type="button"
                    className="draft-board-card-duplicate"
                    title="Duplicate player card"
                    onClick={(event) => {
                      event.stopPropagation();
                      duplicateBoardPlayer(item.boardItemId);
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                  >
                    +
                  </button>
                  <button
                    type="button"
                    className="draft-board-card-remove"
                    title="Remove from board"
                    onClick={(event) => {
                      event.stopPropagation();
                      removeBoardPlayer(item.boardItemId);
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                  >
                    X
                  </button>
                </>
              )}
              <button
                type="button"
                className="draft-board-card-data-button"
                title="Open player data"
                onClick={(event) => openBoardPlayerDataBox(event, item)}
                onMouseDown={(event) => event.stopPropagation()}
              >
                ?
              </button>
              <div className="draft-board-card-info">
                <div className="draft-board-card-name" title={playerName}>
                  <span className={`draft-board-card-name-text ${hasPlayerNote(item) ? "draft-board-card-name-has-note" : ""}`} title={playerName}>
                    {cardPlayerName}
                  </span>
                </div>
                <div className="draft-board-card-meta">
                  <span className={`draft-board-card-meta-text ${hasPlayerBoardMetaOverride(item) ? "draft-board-card-meta-has-override" : ""}`}>
                    {getPlayerBoardMetaLine(item)}
                  </span>
                </div>
              </div>
              {playerGrade && (
                <span className={`draft-board-card-grade ${getPlayerGradeClass(playerGrade)}`} style={getPlayerGradeStyle(playerGrade)}>
                  {playerGrade}
                </span>
              )}
                  </>
                );
              })()}
              </div>
            );
          })}
          {boardLabels.map((item) => (
            <div
              key={item.labelItemId}
              className={`draft-board-label ${(isFlippedPage || (isSplitSortPage && Number(item?.y || 0) < BOARD_SPLIT_SORT_Y)) ? "draft-board-label-split-above-line" : ""} ${isBoardItemSelected("label", item.labelItemId) ? "draft-board-item-selected" : ""}`}
              draggable={canEditCurrentBoard && !(groupSelectOpen && selectedBoardItemCount > 1 && isBoardItemSelected("label", item.labelItemId))}
              onMouseDown={(event) => handleGroupMoveMouseDown(event, "label", item.labelItemId)}
              onClick={(event) => toggleBoardItemSelection(event, "label", item.labelItemId)}
              onDragStart={(event) => handleLabelDragStart(event, item)}
              style={{
                ...getBoardItemPositionStyle(item, "label", "labelItemId"),
                ...getPositionMarkerStyle(item),
              }}
            >
              {canEditCurrentBoard && (
                <button
                  type="button"
                  className="draft-board-label-remove"
                  title="Remove label"
                  onClick={(event) => {
                    event.stopPropagation();
                    removeBoardLabel(item.labelItemId);
                  }}
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  X
                </button>
              )}
              <input
                type="text"
                className="draft-board-label-input"
                value={item.text || ""}
                readOnly={!canEditCurrentBoard || groupSelectOpen}
                maxLength={80}
                onChange={(event) => updateBoardLabelText(item.labelItemId, event.target.value)}
                onClick={(event) => {
                  if (!groupSelectOpen) event.stopPropagation();
                }}
              />
            </div>
          ))}
          {boardDetails.map((item) => {
            const ownDetail = isOwnDetail(item);
            const detailScoutName = getDetailScoutName(item);

            return (
              <div
                key={item.detailItemId}
                className={`draft-board-detail ${ownDetail ? "" : "draft-board-detail-other"} ${isBoardItemSelected("detail", item.detailItemId) ? "draft-board-item-selected" : ""}`}
                draggable={canEditCurrentBoard && !(groupSelectOpen && selectedBoardItemCount > 1 && isBoardItemSelected("detail", item.detailItemId))}
                onMouseDown={(event) => handleGroupMoveMouseDown(event, "detail", item.detailItemId)}
                onClick={(event) => toggleBoardItemSelection(event, "detail", item.detailItemId)}
                onDragStart={(event) => handleDetailDragStart(event, item)}
                style={getBoardItemPositionStyle(item, "detail", "detailItemId")}
              >
                <div className="draft-board-detail-drag-strip">
                  {canEditCurrentBoard && (
                    <button
                      type="button"
                      className="draft-board-detail-remove"
                      title="Remove detail"
                      onClick={(event) => {
                        event.stopPropagation();
                        removeBoardDetail(item.detailItemId);
                      }}
                      onMouseDown={(event) => event.stopPropagation()}
                    >
                      X
                    </button>
                  )}
                </div>
                <div className="draft-board-detail-content">
                  {!ownDetail && detailScoutName && (
                    <div className="draft-board-detail-author">{detailScoutName}</div>
                  )}
                  <textarea
                    className="draft-board-detail-textarea"
                    value={item.text || ""}
                    readOnly={!canEditCurrentBoard || groupSelectOpen}
                    placeholder="Type board note"
                    maxLength={600}
                    onChange={(event) => updateBoardDetailText(item.detailItemId, event.target.value)}
                    onMouseDown={(event) => {
                      if (!groupSelectOpen) event.stopPropagation();
                    }}
                    onClick={(event) => {
                      if (!groupSelectOpen) event.stopPropagation();
                    }}
                  />
                </div>
              </div>
            );
          })}
          {saveMessage && (
            <div className="draft-board-save-status" key={`${saveMessage}-${saveMessagePulse}`}>
              <span>{saveMessage}</span>
            </div>
          )}
        </div>
        <div
          className="draft-board-print-board"
          style={{
            width: boardPrintMetrics.width * boardPrintMetrics.scale,
            height: boardPrintMetrics.height * boardPrintMetrics.scale,
          }}
        >
          <div
            className="draft-board-print-scale"
            style={{
              width: boardPrintMetrics.width,
              height: boardPrintMetrics.height,
              transform: `scale(${boardPrintMetrics.scale})`,
            }}
          >
            <div
              className="draft-board-print-content"
            >
              {isSplitSortPage && (
                <div className="draft-board-split-sort-guide" aria-hidden="true">
                  <div className="draft-board-split-sort-line" />
                  <div className="draft-board-split-sort-marker draft-board-split-sort-marker-boundary">Boundary</div>
                  <div className="draft-board-split-sort-marker draft-board-split-sort-marker-field">Field</div>
                </div>
              )}
              {boardOverlays.filter((item) => isPrintItemInsidePage(item, "overlay")).map((item) => {
                const overlayConfig = getBoardOverlayConfig(item.type);
                const isGameDayOverlay = String(overlayConfig.kind || "").startsWith("gameDay");

                return (
                  <div
                    key={`print-${item.overlayItemId}`}
                    className={`draft-board-position-overlay ${isGameDayOverlay ? "draft-board-game-day-overlay" : "draft-board-oline-overlay"}`}
                    style={{
                      left: Number(item?.x || 0),
                      top: Number(item?.y || 0),
                      gridTemplateColumns: overlayConfig.cells ? `repeat(${overlayConfig.cells.length}, 1fr)` : undefined,
                      width: getOverlayWidth(overlayConfig),
                      height: getOverlayHeight(overlayConfig),
                    }}
                  >
                    {isGameDayOverlay ? (
                      <GameDayLayoutOverlay type={overlayConfig.kind} />
                    ) : (
                      overlayConfig.cells.map((label, index) => (
                        <div className="draft-board-position-overlay-cell" key={`${label}-${index}`}>
                          {label}
                        </div>
                      ))
                    )}
                  </div>
                );
              })}
              {boardPlayers.filter((item) => isPrintItemInsidePage(item, "player")).map((item) => {
                const playerName = [item.last, item.first].filter(Boolean).join(", ") || item.FBXID;
                const cardPlayerName = getPrintCardPlayerName(item, playerName);
                const playerGrade = getPlayerGrade(item.FBXID);

                return (
                  <div
                    key={`print-${item.boardItemId}`}
                    className={`draft-board-card ${getBoardPlayerImpNipClass(item)} ${!activePageHideWarnings && isPlayerRosterAlert(item) ? "draft-board-card-roster-alert" : ""}`}
                    style={{
                      left: Number(item?.x || 0),
                      top: Number(item?.y || 0),
                    }}
                  >
                    <div className="draft-board-card-info">
                      <div className="draft-board-card-name" title={playerName}>
                        <span className={`draft-board-card-name-text ${hasPlayerNote(item) ? "draft-board-card-name-has-note" : ""}`} title={playerName}>
                          {cardPlayerName}
                        </span>
                      </div>
                      <div className="draft-board-card-meta">
                        <span className={`draft-board-card-meta-text ${hasPlayerBoardMetaOverride(item) ? "draft-board-card-meta-has-override" : ""}`}>
                          {getPlayerBoardMetaLine(item)}
                        </span>
                      </div>
                    </div>
                    {playerGrade && (
                      <span className={`draft-board-card-grade ${getPlayerGradeClass(playerGrade)}`} style={getPlayerGradeStyle(playerGrade)}>
                        {playerGrade}
                      </span>
                    )}
                  </div>
                );
              })}
              {boardLabels.filter((item) => isPrintItemInsidePage(item, "label")).map((item) => (
                <div
                  key={`print-${item.labelItemId}`}
                  className={`draft-board-label draft-board-print-label ${(isFlippedPage || (isSplitSortPage && Number(item?.y || 0) < BOARD_SPLIT_SORT_Y)) ? "draft-board-label-split-above-line" : ""}`}
                  style={{
                    left: Number(item?.x || 0),
                    top: Number(item?.y || 0),
                    ...getPositionMarkerStyle(item),
                  }}
                >
                  {item.text || ""}
                </div>
              ))}
              {boardDetails.filter((item) => isPrintItemInsidePage(item, "detail")).map((item) => {
                const detailScoutName = getDetailScoutName(item);
                const ownDetail = isOwnDetail(item);
                const printPoint = getPrintItemPoint(item, "detail") || { x: 0, y: 0 };

                return (
                  <div
                    key={`print-${item.detailItemId}`}
                    className={`draft-board-detail draft-board-print-detail ${ownDetail ? "" : "draft-board-detail-other"}`}
                    style={{
                      left: printPoint.x,
                      top: printPoint.y,
                    }}
                  >
                    {!ownDetail && detailScoutName && (
                      <div className="draft-board-detail-author">{detailScoutName}</div>
                    )}
                    <div className="draft-board-print-detail-text">
                      {item.text || ""}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        {dataOpen && (
          <DragBox2026
            storageKey="DRAFT_BOARD_DATA_LOCATION"
            defaultPos={getDefaultBoardPopupPosition(78)}
            title={selectedDataHeaderTitle}
            countText=""
            wideTitle
            minWidth={390}
            maxWidth={390}
            zIndex={2700}
            showReset
            showClose
            onClose={() => setDataOpen(false)}
            className="draft-board-data-popup"
          >
            <div className={`draft-board-data-panel ${!selectedDataPlayer ? "draft-board-data-panel-empty" : ""}`}>
              {!selectedDataPlayer ? (
                <div className="draft-board-data-empty">No Player Selected</div>
              ) : (
                <>
                  <div className="draft-board-data-heading">
                    <div className="draft-board-data-player-name">
                      {[selectedDataLastName, selectedDataFirstName].filter(Boolean).join(", ") || selectedDataPlayer.FBXID}
                    </div>
                    <div className="draft-board-data-heading-meta">
                      {selectedDataSchoolLine || "-"}
                    </div>
                    {playerDetailLoading && (
                      <div className="draft-board-data-refreshing">Refreshing player data...</div>
                    )}
                    <div className="draft-board-data-board-position">
                      <span>Board Position</span>
                      <strong>{selectedDataBoardPositionLine || "-"}</strong>
                    </div>
                  </div>
                  <div className="draft-board-data-grid">
                    {[
                      {
                        label: "HT:",
                        parts: getDraftBoardMetricParts(
                          getPlayerField(selectedDataPlayer, "height", "HEIGHT", "Height"),
                          getPlayerField(selectedDataPlayer, "heightV", "Height_V", "HEIGHT_V", "HEIGHTV"),
                          getPlayerField(selectedDataPlayer, "heightDate", "HeightDate", "HEIGHTDATE")
                        ),
                      },
                      {
                        label: "WT:",
                        parts: getDraftBoardMetricParts(
                          getPlayerField(selectedDataPlayer, "weight", "WEIGHT", "Weight"),
                          getPlayerField(selectedDataPlayer, "weightV", "Weight_V", "WEIGHT_V", "WEIGHTV"),
                          getPlayerField(selectedDataPlayer, "weightDate", "WeightDate", "WEIGHTDATE")
                        ),
                      },
                      {
                        label: "SP:",
                        parts: getDraftBoardSpeedParts(selectedDataPlayer),
                        showSurface: true,
                      },
                    ].map((metric) => (
                      <div className="draft-board-data-metric" key={metric.label}>
                        <span className="draft-board-data-metric-label">{metric.label}</span>
                        <span className="draft-board-data-metric-value">{metric.parts.value}</span>
                        <span className="draft-board-data-metric-value">{metric.parts.verified}</span>
                        <span className="draft-board-data-metric-value">{metric.parts.date}</span>
                        <span className="draft-board-data-metric-value">{metric.showSurface ? metric.parts.surface : ""}</span>
                      </div>
                    ))}
                  </div>
                  {["CFL", "NFL", "UFL24", "EVENT"].map((league) => {
                    const leagueData = getLeagueData(selectedDataPlayer, league);
                    const leagueLabel = league === "UFL24" ? "UFL 24" : league === "EVENT" ? "Event" : league;

                    return (
                      <div className={`draft-board-data-league draft-board-data-league-${league.toLowerCase()}`} key={league}>
                        <div className="draft-board-data-league-label">{leagueLabel}</div>
                        <div className="draft-board-data-league-values">
                          <span className="draft-board-data-league-value" title={`Team: ${leagueData.team || "-"}`}>
                            {leagueData.team || "-"}
                          </span>
                          <span className="draft-board-data-league-value" title={`Position: ${leagueData.position || "-"}`}>
                            {leagueData.position || "-"}
                          </span>
                          <span className="draft-board-data-league-value" title={`Status: ${leagueData.status || "-"}`}>
                            {leagueData.status || "-"}
                          </span>
                          <span className="draft-board-data-league-value" title={`Date: ${leagueData.date || "-"}`}>
                            {leagueData.date || "-"}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  <div className="draft-board-data-note-section">
                    <div className="draft-board-data-note-header">
                      <span>Propriety Team Data</span>
                      {!editingSelectedDataNote && (
                        <button
                          type="button"
                          className="draft-board-data-note-edit"
                          onClick={() => openPlayerNote(selectedDataPlayer, "edit")}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                    {editingSelectedDataNote ? (
                      <div className="draft-board-data-note-edit-panel">
                        <div className="draft-board-data-note-field-row">
                          <label className="draft-board-data-note-field">
                            <span>Grade</span>
                            <input
                              type="text"
                              value={playerGrade}
                              maxLength={30}
                              onChange={(event) => setPlayerGrade(event.target.value)}
                            />
                          </label>
                          <label className="draft-board-data-note-field">
                            <span>Number</span>
                            <input
                              type="text"
                              value={playerBoardNumber}
                              maxLength={10}
                              onChange={(event) => setPlayerBoardNumber(event.target.value)}
                            />
                          </label>
                        </div>
                        <label className="draft-board-data-note-field">
                          <span>Pos</span>
                          <select
                            value={playerBoardPosition}
                            onChange={(event) => setPlayerBoardPosition(event.target.value)}
                          >
                            <option value="">Use college position</option>
                            {playerBoardPosition && !boardPositionOptions.some((position) => position.code === playerBoardPosition) && (
                              <option value={playerBoardPosition}>{playerBoardPosition}</option>
                            )}
                            {boardPositionOptions.map((position) => (
                              <option key={`board-data-note-pos-${position.code}`} value={position.code}>
                                {position.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <div className="draft-board-data-note-footer">
                          <span>{playerNoteLoading ? "Loading..." : playerNoteMessage}</span>
                          <button
                            type="button"
                            className="draft-board-data-note-cancel"
                            disabled={playerNoteLoading}
                            onClick={() => {
                              setPlayerNoteMode("view");
                              setPlayerNoteText(selectedPlayerCurrentQuickNote?.noteText || "");
                              setPlayerQuickNoteVersions(Array.isArray(selectedDataNote?.quickNoteVersions) ? selectedDataNote.quickNoteVersions : []);
                              setPlayerQuickNoteVersionIndex(0);
                              setPlayerGrade(selectedDataNote?.grade || "");
                              setPlayerBoardPosition(selectedDataNote?.boardPosition || "");
                              setPlayerBoardNumber(normalizeJerseyNumber(selectedDataNote?.boardNumber));
                              setPlayerNoteMessage("");
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="draft-board-data-note-save"
                            disabled={playerNoteLoading}
                            onClick={savePlayerNote}
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className={`draft-board-data-note-view ${selectedDataHasTeamNote ? "" : "draft-board-data-note-view-empty"}`}>
                        <div className="draft-board-data-note-summary">
                          <span>Grade</span>
                          <strong
                            className={selectedDataTeamGrade ? getPlayerGradeClass(selectedDataTeamGrade) : ""}
                            style={selectedDataTeamGrade ? getPlayerGradeStyle(selectedDataTeamGrade) : undefined}
                          >
                            {selectedDataTeamGrade}
                          </strong>
                        </div>
                        <div className="draft-board-data-note-summary">
                          <span>Pos</span>
                          <strong>{selectedDataTeamPosition}</strong>
                        </div>
                        <div className="draft-board-data-note-summary">
                          <span>Number</span>
                          <strong>{selectedDataTeamNumber}</strong>
                        </div>
                      </div>
                    )}
                    <div className="draft-board-data-qn-panel">
                      <label className="draft-board-data-note-field draft-board-data-qn-field">
                        <span>Quick Note</span>
                        <textarea
                          value={playerNoteText}
                          placeholder="Add a quick note"
                          onChange={(event) => setPlayerNoteText(event.target.value)}
                        />
                      </label>
                      <div className="draft-board-data-qn-meta">
                        {playerQuickNoteVersion ? (
                          <span>
                            {playerQuickNoteIsLatestVersion ? "Current" : "Version"}: edited by {cleanText(playerQuickNoteVersion.updatedByScoutName || playerQuickNoteVersion.createdByScoutName) || `Scout ${playerQuickNoteVersion.updatedByScoutId || playerQuickNoteVersion.createdByScoutId}`} {formatBoardEditedDateTime(playerQuickNoteVersion.updatedAt || playerQuickNoteVersion.createdAt)}
                          </span>
                        ) : (
                          <span>No QN yet</span>
                        )}
                      </div>
                      <div className="draft-board-data-qn-version-bar">
                        <button
                          type="button"
                          onClick={() => goToPlayerQuickNoteVersion(playerQuickNoteVersionIndex + 1)}
                          disabled={playerNoteLoading || playerQuickNoteVersionIndex >= playerQuickNoteVersionCount - 1}
                        >
                          {"<"}
                        </button>
                        <span>{playerQuickNoteVersionCount > 0 ? `${playerQuickNoteVersionIndex + 1} of ${playerQuickNoteVersionCount}` : "0 of 0"}</span>
                        <button
                          type="button"
                          onClick={() => goToPlayerQuickNoteVersion(playerQuickNoteVersionIndex - 1)}
                          disabled={playerNoteLoading || playerQuickNoteVersionIndex <= 0}
                        >
                          {">"}
                        </button>
                        {!playerQuickNoteIsLatestVersion && (
                          <button
                            type="button"
                            className="draft-board-data-qn-newest"
                            onClick={() => goToPlayerQuickNoteVersion(0)}
                            disabled={playerNoteLoading}
                          >
                            Newest
                          </button>
                        )}
                        {playerQuickNoteVersion?.canDelete && (
                          <button
                            type="button"
                            className="draft-board-data-qn-delete"
                            onClick={deletePlayerQuickNoteVersion}
                            disabled={playerNoteLoading}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                      <div className="draft-board-data-note-footer draft-board-data-qn-footer">
                        <span>{playerNoteLoading ? "Loading..." : playerNoteMessage}</span>
                        <button
                          type="button"
                          className="draft-board-data-qn-save"
                          disabled={playerNoteLoading || !canSaveWhiteBoardQuickNote}
                          onClick={saveWhiteBoardQuickNote}
                        >
                          Save QN
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </DragBox2026>
        )}
        {playersOpen && (
          <DragBox2026
            storageKey="DRAFT_BOARD_PLAYERS_LOCATION"
            defaultPos={getDefaultBoardPopupPosition(78)}
            title="Players"
            countText={`[${safePlayers.length}]`}
            minWidth={390}
            maxWidth={390}
            zIndex={2700}
            showReset
            showClose
            onClose={() => setPlayersOpen(false)}
            className="draft-board-players-popup draft-board-players-popup-main"
          >
            <div className="draft-board-player-tools draft-board-player-tools-main">
              <div className={`draft-board-filter-input-wrap ${hasFilterValue(lastFilter) ? "draft-board-filter-input-wrap-has-clear" : ""}`}>
                <input
                  type="text"
                  className={`draft-board-player-filter draft-board-player-filter-last ${hasFilterValue(lastFilter) ? "draft-board-filter-active" : ""}`}
                  placeholder="Last"
                  value={lastFilter}
                  onChange={(event) => setLastFilter(event.target.value)}
                />
                {hasFilterValue(lastFilter) && (
                  <button type="button" className="draft-board-filter-clear" onClick={() => setLastFilter("")}>
                    X
                  </button>
                )}
              </div>
              <div className={`draft-board-filter-input-wrap ${hasFilterValue(firstFilter) ? "draft-board-filter-input-wrap-has-clear" : ""}`}>
                <input
                  type="text"
                  className={`draft-board-player-filter ${hasFilterValue(firstFilter) ? "draft-board-filter-active" : ""}`}
                  placeholder="First"
                  value={firstFilter}
                  onChange={(event) => setFirstFilter(event.target.value)}
                />
                {hasFilterValue(firstFilter) && (
                  <button type="button" className="draft-board-filter-clear" onClick={() => setFirstFilter("")}>
                    X
                  </button>
                )}
              </div>
              <div className={`draft-board-filter-input-wrap draft-board-filter-input-wrap-select ${hasFilterValue(positionFilter) ? "draft-board-filter-input-wrap-has-clear" : ""}`}>
                <select
                  className={`draft-board-player-filter ${hasFilterValue(positionFilter) ? "draft-board-filter-active" : ""}`}
                  value={positionFilter}
                  onChange={(event) => {
                    setPositionFilter(event.target.value);
                    if (event.target.value) setPositionUnitFilter("");
                  }}
                  disabled={!!positionUnitFilter}
                >
                  <option value="">General Position</option>
                  {GENERAL_POSITION_OPTIONS.map((position) => (
                    <option key={`players-${position.code}`} value={position.code} title={position.title}>
                      {position.label}
                    </option>
                  ))}
                </select>
                {hasFilterValue(positionFilter) && (
                  <button type="button" className="draft-board-filter-clear" onClick={() => setPositionFilter("")}>
                    X
                  </button>
                )}
              </div>
              <div className={`draft-board-filter-input-wrap draft-board-filter-input-wrap-select ${hasFilterValue(positionUnitFilter) ? "draft-board-filter-input-wrap-has-clear" : ""}`}>
                <select
                  className={`draft-board-player-filter ${hasFilterValue(positionUnitFilter) ? "draft-board-filter-active" : ""}`}
                  value={positionUnitFilter}
                  onChange={(event) => {
                    setPositionUnitFilter(event.target.value);
                    if (event.target.value) setPositionFilter("");
                  }}
                  disabled={!!positionFilter}
                >
                  <option value="">All Units</option>
                  {POSITION_UNIT_OPTIONS.map((unit) => (
                    <option key={unit.code} value={unit.code}>
                      {unit.label}
                    </option>
                  ))}
                </select>
                {hasFilterValue(positionUnitFilter) && (
                  <button type="button" className="draft-board-filter-clear" onClick={() => setPositionUnitFilter("")}>
                    X
                  </button>
                )}
              </div>
              <button
                type="button"
                className={`draft-board-player-on-board-toggle ${playersOnlyOnBoard ? "draft-board-player-on-board-toggle-active" : ""}`}
                onClick={() => setPlayersOnlyOnBoard((value) => !value)}
              >
                Used
              </button>
              <button
                type="button"
                className="draft-board-player-add-all-button"
                onClick={addFilteredPlayersToBoard}
                disabled={!canEditCurrentBoard || filteredPlayers.every((player) => activePageCounts[String(player?.FBXID || "").trim()])}
              >
                Add All to Page
              </button>
              {showSearchedPlayersProPositionNote && (
                <div className="draft-board-pro-position-note draft-board-pro-position-note-button-slot">
                  This Board is using Pro Positions.
                </div>
              )}
            </div>
            <div className="draft-board-player-list" ref={setPlayerListContainerRef("players")}>
              {filteredPlayers.length === 0 ? (
                <div className="draft-board-player-empty">No players match these filters.</div>
              ) : (
                filteredPlayers.map((player, index) => {
                  const playerId = String(player.FBXID || "").trim();
                  const boardCount = boardCounts[playerId] || 0;
                  const isOnBoard = boardCount > 0;
                  const isHighlighted = playerId !== "" && highlightedFBXID === playerId;
                  const playerPosition = getPlayerBoardPosition(player) || "-";
                  const impnip = getBoardPlayerImpNip(player);

                  return (
                    <React.Fragment key={player.FBXID || `${player.last}-${player.first}-${index}`}>
                      <div
                        ref={setPlayerListRowRef("players", playerId)}
                        className={`draft-board-player-row draft-board-player-row-no-rank ${getBoardPlayerImpNipListClass(player)} ${isOnBoard ? "draft-board-player-row-on-board" : ""} ${isHighlighted ? "draft-board-player-row-highlighted" : ""}`}
                        draggable={canEditCurrentBoard}
                        onClick={() => setHighlightedFBXID((current) => current === playerId ? "" : playerId)}
                        onDragStart={(event) => handlePlayerDragStart(event, player)}
                      >
                        {renderPlayerTransactionToggle(player)}
                        <div className="draft-board-player-main">
                          <div className="draft-board-player-name">
                            {[player.last, player.first].filter(Boolean).join(", ") || "Unknown Player"}
                            {player.school && <span className="draft-board-player-school"> | {player.school}</span>}
                          </div>
                        </div>
                        <div className="draft-board-player-impnip-cell">
                          {impnip && <span className={`draft-board-player-impnip draft-board-player-impnip-${impnip.toLowerCase()}`}>{impnip}</span>}
                        </div>
                        <div className="draft-board-player-count-cell">
                          {isOnBoard && <span className="draft-board-player-count">{boardCount}</span>}
                        </div>
                        <div className="draft-board-player-position">{playerPosition}</div>
                      </div>
                      {renderPlayerTransactionPanel(player)}
                    </React.Fragment>
                  );
                })
              )}
            </div>
          </DragBox2026>
        )}
        {onBoardOpen && (
          <DragBox2026
            storageKey="DRAFT_BOARD_ON_BOARD_LOCATION"
            defaultPos={getDefaultBoardPopupPosition(78)}
            title="Placed Players"
            countText={`[${boardPlayers.length}]`}
            minWidth={390}
            maxWidth={390}
            zIndex={2700}
            showReset
            showClose
            onClose={() => setOnBoardOpen(false)}
            className="draft-board-players-popup"
          >
            <div className="draft-board-player-tools draft-board-on-board-tools">
              <div className={`draft-board-filter-input-wrap ${hasFilterValue(onBoardLastFilter) ? "draft-board-filter-input-wrap-has-clear" : ""}`}>
                <input
                  type="text"
                  className={`draft-board-player-filter ${hasFilterValue(onBoardLastFilter) ? "draft-board-filter-active" : ""}`}
                  placeholder="Last"
                  value={onBoardLastFilter}
                  onChange={(event) => setOnBoardLastFilter(event.target.value)}
                />
                {hasFilterValue(onBoardLastFilter) && (
                  <button type="button" className="draft-board-filter-clear" onClick={() => setOnBoardLastFilter("")}>
                    X
                  </button>
                )}
              </div>
              <div className={`draft-board-filter-input-wrap ${hasFilterValue(onBoardFirstFilter) ? "draft-board-filter-input-wrap-has-clear" : ""}`}>
                <input
                  type="text"
                  className={`draft-board-player-filter ${hasFilterValue(onBoardFirstFilter) ? "draft-board-filter-active" : ""}`}
                  placeholder="First"
                  value={onBoardFirstFilter}
                  onChange={(event) => setOnBoardFirstFilter(event.target.value)}
                />
                {hasFilterValue(onBoardFirstFilter) && (
                  <button type="button" className="draft-board-filter-clear" onClick={() => setOnBoardFirstFilter("")}>
                    X
                  </button>
                )}
              </div>
              <div className={`draft-board-filter-input-wrap draft-board-filter-input-wrap-select ${hasFilterValue(onBoardPositionFilter) ? "draft-board-filter-input-wrap-has-clear" : ""}`}>
                <select
                  className={`draft-board-player-filter ${hasFilterValue(onBoardPositionFilter) ? "draft-board-filter-active" : ""}`}
                  value={onBoardPositionFilter}
                  onChange={(event) => {
                    setOnBoardPositionFilter(event.target.value);
                    if (event.target.value) setOnBoardPositionUnitFilter("");
                  }}
                  disabled={!!onBoardPositionUnitFilter}
                >
                  <option value="">General Position</option>
                  {GENERAL_POSITION_OPTIONS.map((position) => (
                    <option key={`on-board-${position.code}`} value={position.code} title={position.title}>
                      {position.label}
                    </option>
                  ))}
                </select>
                {hasFilterValue(onBoardPositionFilter) && (
                  <button type="button" className="draft-board-filter-clear" onClick={() => setOnBoardPositionFilter("")}>
                    X
                  </button>
                )}
              </div>
              <div className={`draft-board-filter-input-wrap draft-board-filter-input-wrap-select ${hasFilterValue(onBoardPositionUnitFilter) ? "draft-board-filter-input-wrap-has-clear" : ""}`}>
                <select
                  className={`draft-board-player-filter ${hasFilterValue(onBoardPositionUnitFilter) ? "draft-board-filter-active" : ""}`}
                  value={onBoardPositionUnitFilter}
                  onChange={(event) => {
                    setOnBoardPositionUnitFilter(event.target.value);
                    if (event.target.value) setOnBoardPositionFilter("");
                  }}
                  disabled={!!onBoardPositionFilter}
                >
                  <option value="">All Units</option>
                  {POSITION_UNIT_OPTIONS.map((unit) => (
                    <option key={`on-board-${unit.code}`} value={unit.code}>
                      {unit.label}
                    </option>
                  ))}
                </select>
                {hasFilterValue(onBoardPositionUnitFilter) && (
                  <button type="button" className="draft-board-filter-clear" onClick={() => setOnBoardPositionUnitFilter("")}>
                    X
                  </button>
                )}
              </div>
              <button
                type="button"
                className="draft-board-player-add-all-button"
                onClick={addFilteredOnBoardPlayersToDraft}
                disabled={!canEditCurrentBoard || filteredOnBoardPlayers.length === 0 || filteredOnBoardPlayers.every((player) =>
                  draftPlayers.some((draftPlayer) => String(draftPlayer?.FBXID || "").trim() === String(player?.FBXID || "").trim())
                )}
              >
                Add All to Priority
              </button>
            </div>
            <div className="draft-board-player-list" ref={setPlayerListContainerRef("onBoard")}>
              {filteredOnBoardPlayers.length === 0 ? (
                <div className="draft-board-player-empty">
                  {boardPlayers.length === 0 ? "No players are on the board." : "No players match these filters."}
                </div>
              ) : (
                filteredOnBoardPlayers.map((player, index) => {
                  const playerId = String(player.FBXID || "").trim();
                  const isHighlighted = playerId !== "" && highlightedFBXID === playerId;
                  const playerPosition = getPlayerBoardPosition(player) || "-";
                  const impnip = getBoardPlayerImpNip(player);

                  return (
                    <React.Fragment key={player.groupKey || player.boardItemId || `${player.last}-${player.first}-${index}`}>
                      <div
                        ref={setPlayerListRowRef("onBoard", playerId)}
                        className={`draft-board-player-row draft-board-player-row-no-rank ${getBoardPlayerImpNipListClass(player)} ${isHighlighted ? "draft-board-player-row-highlighted" : ""}`}
                        draggable={canEditCurrentBoard}
                        onClick={() => setHighlightedFBXID((current) => current === playerId ? "" : playerId)}
                        onDragStart={(event) => {
                          if (!canEditCurrentBoard) {
                            event.preventDefault();
                            setSaveMessage("Board is view only");
                            return;
                          }
                          event.dataTransfer.setData("application/fbx-draft-player", JSON.stringify(player));
                          event.dataTransfer.effectAllowed = "copy";
                        }}
                      >
                        {renderPlayerTransactionToggle(player)}
                        <div className="draft-board-player-main">
                          <div className="draft-board-player-name">
                            {[player.last, player.first].filter(Boolean).join(", ") || "Unknown Player"}
                            {player.school && <span className="draft-board-player-school"> | {player.school}</span>}
                          </div>
                        </div>
                        <div className="draft-board-player-impnip-cell">
                          {impnip && <span className={`draft-board-player-impnip draft-board-player-impnip-${impnip.toLowerCase()}`}>{impnip}</span>}
                        </div>
                        <div className="draft-board-player-count-cell">
                          <span className="draft-board-player-count">{player.count}</span>
                        </div>
                        <div className="draft-board-player-position">{playerPosition}</div>
                      </div>
                      {renderPlayerTransactionPanel(player)}
                    </React.Fragment>
                  );
                })
              )}
            </div>
          </DragBox2026>
        )}
	        {draftOpen && (
	          <DragBox2026
            storageKey="DRAFT_BOARD_DRAFT_LOCATION"
            defaultPos={getDefaultBoardPopupPosition(78)}
            title="Priority List"
            countText={`[${draftPlayers.length}]`}
            minWidth={390}
            maxWidth={390}
            zIndex={2700}
            showReset
            showClose
            onClose={() => setDraftOpen(false)}
            className="draft-board-players-popup"
          >
            <div
              className="draft-board-draft-drop-zone"
              ref={setPlayerListContainerRef("draft")}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => handleDraftDrop(event)}
            >
              {draftPlayers.length === 0 ? (
                <div className="draft-board-player-empty">Drag players from the whiteboard into Priority.</div>
              ) : (
                draftPlayers.map((player, index) => {
                  const playerId = String(player.FBXID || "").trim();
                  const boardCount = boardCounts[playerId] || 0;
                  const isHighlighted = playerId !== "" && highlightedFBXID === playerId;
                  const playerPosition = getPlayerBoardPosition(player) || "-";
                  const impnip = getBoardPlayerImpNip(player);

                  return (
                    <React.Fragment key={player.draftItemId || player.FBXID || `${player.last}-${player.first}-${index}`}>
                      <div
                        ref={setPlayerListRowRef("draft", playerId)}
                        className={`draft-board-player-row draft-board-draft-row ${isHighlighted ? "draft-board-player-row-highlighted" : ""}`}
                        draggable={canEditCurrentBoard}
                        onClick={() => setHighlightedFBXID((current) => current === playerId ? "" : playerId)}
                        onDragStart={(event) => handleDraftRowDragStart(event, index)}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={(event) => handleDraftDrop(event, index)}
                      >
                        {renderPlayerTransactionToggle(player)}
                        <div className="draft-board-player-rank">{index + 1}</div>
                        <div className="draft-board-player-main">
                          <div className="draft-board-player-name">
                            {[player.last, player.first].filter(Boolean).join(", ") || "Unknown Player"}
                            {player.school && <span className="draft-board-player-school"> | {player.school}</span>}
                          </div>
                        </div>
                        <div className="draft-board-player-impnip-cell">
                          {impnip && <span className={`draft-board-player-impnip draft-board-player-impnip-${impnip.toLowerCase()}`}>{impnip}</span>}
                        </div>
                        <div className="draft-board-player-count-cell">
                          {boardCount > 0 && <span className="draft-board-player-count">{boardCount}</span>}
                        </div>
                        <div className="draft-board-player-position">{playerPosition}</div>
                        <button
                          type="button"
                          className="draft-board-draft-remove-button"
                          disabled={!canEditCurrentBoard}
                          title="Remove from Priority"
                          onClick={(event) => {
                            event.stopPropagation();
                            removeDraftPlayer(player.draftItemId, index);
                          }}
                          onMouseDown={(event) => event.stopPropagation()}
                          onDragStart={(event) => event.preventDefault()}
                        >
                          X
                        </button>
                      </div>
                      {renderPlayerTransactionPanel(player)}
                    </React.Fragment>
                  );
                })
              )}
            </div>
	          </DragBox2026>
	        )}
	        {tutorialOpen && (
	          <WhiteBoardTutorial onClose={() => setTutorialOpen(false)} />
	        )}
	      </div>
	    </div>
	  );
};

export default DraftBoard;
